import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  CalendarDays,
  Bell,
  ChevronDown,
  CheckCircle2,
  CircleEllipsis,
  GripVertical,
  Image,
  KanbanSquare,
  FolderOpen,
  LayoutDashboard,
  Menu,
  MessageCircle,
  MessageSquare,
  Mic,
  Moon,
  Mail,
  Smile,
  Paperclip,
  Pencil,
  Plus,
  Reply,
  Search,
  Settings,
  Share2,
  Sparkles,
  Hash,
  PlayCircle,
  Send,
  LogOut,
  Table2,
  Timer,
  Trash2,
  ThumbsUp,
  Sun,
  Tag,
  AtSign,
  UserPlus2,
  UserRound,
  Users,
  UserCircle2,
  Video,
  X,
  FileText,
  FolderKanban,
  Globe,
  MailOpen,
  NotebookPen,
  ShieldCheck,
} from 'lucide-react';
import LoginPage from './pages/LoginPage';
import {
  apiCreateProject,
  apiCreateProjectTask,
  apiGetMe,
  apiGetProjectBoard,
  apiGetWorkItemDetail,
  apiListMyProjects,
  apiUpdateProject,
  apiGetWorkspaceSettings,
  apiListNotifications,
  apiMoveProjectTask,
  clearAuthSession,
  loadAuthSession,
  mapNotificationDto,
  mapWorkspaceSettingsResponse,
  setAuthSession,
} from './api/client';
import { TaskDetailDrawerApi } from './TaskDetailDrawerApi';
import { TaskDescriptionEditor } from './components/TaskDescriptionEditor';
import UserAvatar from './components/UserAvatar';
import DashboardPageView from './pages/DashboardPage';
import SettingsPageView from './pages/SettingsPage';
import TeamMembersPageView from './pages/TeamMembersPage';
import AiTaskerPageView from './pages/AiTaskerPage';
import AiAssistancePageView from './pages/AiAssistancePage';
import AiVideoPageView from './pages/AiVideoPage';
import NotesPageView from './pages/NotesPage';
import SuperAdminPageView from './pages/SuperAdminPage';
import PrivacyPolicyPageView from './pages/PrivacyPolicyPage';
import ContactMessagesPageView from './pages/ContactMessagesPage';
import MessagesPageView from './pages/MessagesPage';
import NotificationsCenterView from './pages/NotificationsCenter';
import {
  pickSampleTaskTitle,
  SAMPLE_CATEGORY_DRAFT,
  sampleLabelDraft,
  sampleMeetingLink,
  sampleNewProjectModalState,
  sampleProjectPresets,
  sampleSubtaskTitle,
} from './lib/sampleData';
import AcceptInvitePage from './pages/AcceptInvitePage';
import { WorkspaceAddsContext, useWorkspaceAdds } from './workspaceAddsContext';

/** Brand mark: `public/images/logo.png` (source of truth: `coretask/images/logo.png`). */
const BRAND_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/images/logo.png`;

const ThemeContext = createContext(null);
const LocaleContext = createContext(null);

const translations = {
  en: {
    appName: 'CoreTask',
    searchPlaceholder: 'Search projects, tasks, people...',
    toggleDarkMode: 'Toggle dark mode',
    notifications: 'Notifications',
    signedInAs: 'Signed in as',
    logout: 'Logout',
    loginTitle: 'Login',
    loginSubtitle: 'Use your workspace credentials to continue.',
    email: 'Email',
    password: 'Password',
    loginSessionHint:
      'You can sign in as different users in separate tabs or windows — each stays independent. Signing out clears only this tab.',
    signIn: 'Sign In',
    createAccount: 'Create account',
    signingIn: 'Signing in…',
    creatingAccount: 'Creating account…',
    loginValidationBoth: 'Please enter both email and password.',
    checkingInviteLink: 'Checking invitation…',
    invalidOrExpiredInvite: 'This invitation link is invalid or has expired.',
    setYourPasswordTitle: 'Welcome — set your password',
    inviteLinkProblemTitle: 'Invitation link problem',
    finishAndSignIn: 'Finish setup and sign in',
    inviteRoleLabel: 'Your role',
    acceptInviteExistingTitle: 'Join your team workspace',
    acceptInviteExistingBlurb:
      'This invitation uses your existing CoreTask account. Enter the password you already use to sign in—not a new one.',
    invitePasswordLabelNew: 'Choose a password (8+ characters)',
    invitePasswordLabelExisting: 'Your CoreTask password',
    joinWorkspaceSubmitting: 'Signing you in…',
    joinWorkspaceButton: 'Accept invitation & sign in',
    language: 'Language',
    english: 'English',
    arabic: 'Arabic',
    navDashboard: 'Dashboard',
    navProjects: 'Projects',
    navAITasker: 'AI Tasker',
    navAIAssistance: 'AI Assistance',
    navAIVideo: 'AI Video',
    navNotes: 'Notes & Appointments',
    notesTabNotes: 'Notes',
    notesTabAppointments: 'Appointments',
    notesSearchPlaceholder: 'Search notes…',
    notesNewNote: 'New Note',
    notesNewAppointment: 'New Appointment',
    notesCatAll: 'All',
    notesCatWork: 'Work',
    notesCatPersonal: 'Personal',
    notesCatClients: 'Clients',
    notesCatOther: 'Other',
    notesTypeText: 'Text',
    notesTypeVoice: 'Voice',
    notesTitleLabel: 'Title',
    notesTitlePlaceholder: 'Note title…',
    notesContentPlaceholder: 'Write your note here…',
    notesColorLabel: 'Color',
    notesTagsLabel: 'Tags',
    notesTagsPlaceholder: 'tag1, tag2…',
    notesPinned: 'Pinned',
    notesSave: 'Save',
    notesDelete: 'Delete',
    notesEdit: 'Edit',
    notesPin: 'Pin',
    notesUnpin: 'Unpin',
    notesRecordStart: 'Hold to record',
    notesRecordStop: 'Release to stop',
    notesRecording: 'Recording…',
    notesRecordPlay: 'Play',
    notesRecordPause: 'Pause',
    notesEmpty: 'No notes yet. Create your first note.',
    apptTitleLabel: 'Title',
    apptTitlePlaceholder: 'Appointment title…',
    apptDescLabel: 'Description',
    apptDescPlaceholder: 'Optional details…',
    apptDateLabel: 'Date & Time',
    apptReminderLabel: 'Reminder',
    apptReminderNone: 'No reminder',
    apptReminder5: '5 min before',
    apptReminder10: '10 min before',
    apptReminder15: '15 min before',
    apptReminder30: '30 min before',
    apptReminder60: '1 hour before',
    apptReminder1440: '1 day before',
    apptLinkNoteLabel: 'Link a note',
    apptLinkNotePlaceholder: 'Select note…',
    apptStatusUpcoming: 'Upcoming',
    apptStatusCompleted: 'Completed',
    apptStatusCancelled: 'Cancelled',
    apptStatusLabel: 'Status',
    apptEmpty: 'No appointments yet. Add your first one.',
    apptMarkComplete: 'Mark Complete',
    apptMarkCancel: 'Cancel Appointment',
    aiVideoHeroTitle: 'Generate videos from a text prompt',
    aiVideoHeroBody: 'Describe a scene and let Google Veo bring it to life.',
    aiVideoPromptLabel: 'Describe your video',
    aiVideoPromptPlaceholder: 'A serene beach at sunset with gentle waves and golden light...',
    aiVideoAspectRatio: 'Aspect Ratio',
    aiVideoDuration: 'Duration',
    aiVideoGenerate: 'Generate Video',
    aiVideoGenerating: 'Generating…',
    aiVideoWaitHint: 'Video generation usually takes 1–3 minutes.',
    aiVideoDownload: 'Download Video',
    aiVideoTipsTitle: 'Prompt Tips',
    aiVideoTip1: 'Be specific about lighting, mood, and camera angle.',
    aiVideoTip2: 'Mention the style: cinematic, animated, realistic…',
    aiVideoTip3: 'Describe motion: slow pan, close-up, timelapse…',
    aiVideoTip4: 'Keep prompts under 200 characters for best results.',
    aiVideoDisclaimer: 'Powered by Google Veo via Gemini API. Generation may take up to 5 minutes.',
    navTeam: 'Team Members',
    navMessages: 'Messages',
    navNotifications: 'Notifications',
    navSettings: 'Settings',
    navProfile: 'Profile',
    pageDashboardTitle: 'Dashboard',
    pageProjectsTitle: 'Projects',
    pageTeamTitle: 'Team Members',
    pageMessagesTitle: 'Messages',
    pageNotificationsTitle: 'Notifications',
    pageSettingsTitle: 'Settings',
    pageProfileTitle: 'Profile',
    projectsDashboardTitle: 'Projects Dashboard',
    newProject: 'New Project',
    teamMembersTitle: 'Team Members',
    inviteMember: 'Invite Member',
    messagesTitle: 'Messages',
    notificationsTitle: 'Notifications',
    settingsTitle: 'Settings',
    createNewProject: 'Create New Project',
    close: 'Close',
    cancel: 'Cancel',
    fillSampleData: 'Fill sample data',
    loginPresetSuperAdmin: 'Super Admin',
    loginPresetAdmin: 'Admin',
    loginPresetMember: 'Member',
    loginPresetHint: 'Quick fill:',
    audioAttachmentTitle: 'Add audio',
    audioAttachmentSubtitle: 'Upload a file or record a short voice note.',
    uploadAudioFile: 'Upload audio file',
    recordVoiceNote: 'Record voice',
    recordingTapStop: 'Tap stop when finished. Your clip uploads automatically.',
    stopRecording: 'Stop',
    recordingNotSupported: 'Recording is not supported in this browser.',
    micDenied: 'Microphone permission was denied.',
    voiceChat: 'Voice Chat',
    voiceChatTitle: 'Talk to AI',
    voiceChatHint: 'Press the mic and speak naturally',
    voiceChatUnsupported: 'Voice chat is not supported in this browser.',
    tapToSpeak: 'Tap to speak',
    listening: 'Listening…',
    aiSpeaking: 'AI is speaking…',
    youSaid: 'You said',
    stopListeningBtn: 'Stop',
    emptyRecording: 'Nothing was recorded.',
    uploadDocument: 'Document',
    taskDescriptionPlaceholder: 'Add notes, acceptance criteria, links…',
    createProject: 'Create Project',
    markAllAsRead: 'Mark all as read',
    saveProfile: 'Save Profile',
    settingsProfile: 'Profile Settings',
    settingsNotifications: 'Notifications Settings',
    settingsWorkspace: 'Workspace Settings',
    dashboardHeroTitle: 'Dashboard',
    dashboardHeroSubtitle:
      'Welcome back. Here is a quick snapshot of your projects, tasks, and team momentum.',
    dashboardActionNewTask: '+ New Task',
    dashboardActionCreateProject: 'Create Project',
    dashboardActionInviteTeam: 'Invite Team',
    dashboardOpen: 'Open Dashboard',
    dashboardCreateTask: 'Create Task',
    dashboardInviteTeamCard: 'Invite Team',
    saveChanges: 'Save Changes',
    shareMeetingLink: 'Share Meeting Link',
    send: 'Send',
    submit: 'Submit',
    addTask: 'Add Task',
    addSubtask: 'Add Subtask',
    closeButton: 'Close',
    startConversation: 'Start a conversation',
    noMessages: 'No messages',
    noMessagesDesc: 'Start a conversation with your teammate.',
    composerImage: 'Image',
    composerVideo: 'Video',
    composerAudio: 'Audio',
    composerDocument: 'Document',
    messageComposerPlaceholder: 'Type formatted text… attach files from the toolbar below.',
    couldNotLoadBoard: 'Could not load board.',
    uploading: 'Uploading…',
  },
  ar: {
    appName: 'كور تاسك',
    searchPlaceholder: 'ابحث عن المشاريع والمهام والأشخاص...',
    toggleDarkMode: 'تبديل الوضع الليلي',
    notifications: 'الإشعارات',
    signedInAs: 'مسجل الدخول باسم',
    logout: 'تسجيل الخروج',
    loginTitle: 'تسجيل الدخول',
    loginSubtitle: 'استخدم بيانات حسابك للدخول إلى مساحة العمل.',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    loginSessionHint:
      'يمكنك تسجيل الدخول بحسابات مختلفة في علامات تبويب أو نوافذ منفصلة — كل منها مستقل. تسجيل الخروج يمسح هذا التبويب فقط.',
    signIn: 'دخول',
    createAccount: 'إنشاء حساب',
    signingIn: 'جاري تسجيل الدخول…',
    creatingAccount: 'جاري إنشاء الحساب…',
    loginValidationBoth: 'يرجى إدخال البريد وكلمة المرور.',
    checkingInviteLink: 'جاري التحقق من الدعوة…',
    invalidOrExpiredInvite: 'رابط الدعوة غير صالح أو منتهٍ.',
    setYourPasswordTitle: 'مرحبًا — أنشئ كلمة المرور',
    inviteLinkProblemTitle: 'مشكلة في رابط الدعوة',
    finishAndSignIn: 'إنهاء الإعداد وتسجيل الدخول',
    inviteRoleLabel: 'دورك في المساحة',
    acceptInviteExistingTitle: 'انضم إلى مساحة الفريق',
    acceptInviteExistingBlurb:
      'تستخدم هذه الدعوة حساب كور تاسك الحالي. أدخل كلمة المرور التي تستخدمها عادةً لتسجيل الدخول وليس كلمة مرور جديدة.',
    invitePasswordLabelNew: 'اختر كلمة مرور (8 أحرف أو أكثر)',
    invitePasswordLabelExisting: 'كلمة مرور كور تاسك',
    joinWorkspaceSubmitting: 'جاري تسجيل الدخول…',
    joinWorkspaceButton: 'قبول الدعوة وتسجيل الدخول',
    language: 'اللغة',
    english: 'الإنجليزية',
    arabic: 'العربية',
    navDashboard: 'لوحة التحكم',
    navProjects: 'المشاريع',
    navAITasker: 'منشئ المهام AI',
    navAIAssistance: 'مساعد AI',
    navAIVideo: 'فيديو AI',
    navNotes: 'المواعيد والملاحظات',
    notesTabNotes: 'الملاحظات',
    notesTabAppointments: 'المواعيد',
    notesSearchPlaceholder: 'ابحث في الملاحظات…',
    notesNewNote: 'ملاحظة جديدة',
    notesNewAppointment: 'موعد جديد',
    notesCatAll: 'الكل',
    notesCatWork: 'عمل',
    notesCatPersonal: 'شخصي',
    notesCatClients: 'عملاء',
    notesCatOther: 'أخرى',
    notesTypeText: 'نصية',
    notesTypeVoice: 'صوتية',
    notesTitleLabel: 'العنوان',
    notesTitlePlaceholder: 'عنوان الملاحظة…',
    notesContentPlaceholder: 'اكتب ملاحظتك هنا…',
    notesColorLabel: 'اللون',
    notesTagsLabel: 'الوسوم',
    notesTagsPlaceholder: 'وسم1، وسم2…',
    notesPinned: 'مثبتة',
    notesSave: 'حفظ',
    notesDelete: 'حذف',
    notesEdit: 'تعديل',
    notesPin: 'تثبيت',
    notesUnpin: 'إلغاء التثبيت',
    notesRecordStart: 'اضغط للتسجيل',
    notesRecordStop: 'أفلت للإيقاف',
    notesRecording: 'جارٍ التسجيل…',
    notesRecordPlay: 'تشغيل',
    notesRecordPause: 'إيقاف مؤقت',
    notesEmpty: 'لا توجد ملاحظات بعد. أنشئ أولى ملاحظاتك.',
    apptTitleLabel: 'العنوان',
    apptTitlePlaceholder: 'عنوان الموعد…',
    apptDescLabel: 'الوصف',
    apptDescPlaceholder: 'تفاصيل اختيارية…',
    apptDateLabel: 'التاريخ والوقت',
    apptReminderLabel: 'التذكير',
    apptReminderNone: 'بدون تذكير',
    apptReminder5: 'قبل 5 دقائق',
    apptReminder10: 'قبل 10 دقائق',
    apptReminder15: 'قبل 15 دقيقة',
    apptReminder30: 'قبل 30 دقيقة',
    apptReminder60: 'قبل ساعة',
    apptReminder1440: 'قبل يوم',
    apptLinkNoteLabel: 'ربط بملاحظة',
    apptLinkNotePlaceholder: 'اختر ملاحظة…',
    apptStatusUpcoming: 'قادم',
    apptStatusCompleted: 'مكتمل',
    apptStatusCancelled: 'ملغي',
    apptStatusLabel: 'الحالة',
    apptEmpty: 'لا توجد مواعيد بعد. أضف أول موعد.',
    apptMarkComplete: 'تحديد كمكتمل',
    apptMarkCancel: 'إلغاء الموعد',
    aiVideoHeroTitle: 'أنشئ مقاطع فيديو من نص',
    aiVideoHeroBody: 'صف مشهداً ودع Google Veo يحوّله إلى فيديو.',
    aiVideoPromptLabel: 'صف الفيديو الذي تريده',
    aiVideoPromptPlaceholder: 'شاطئ هادئ عند الغروب مع أمواج لطيفة وضوء ذهبي...',
    aiVideoAspectRatio: 'نسبة العرض إلى الارتفاع',
    aiVideoDuration: 'المدة',
    aiVideoGenerate: 'إنشاء فيديو',
    aiVideoGenerating: 'جارٍ الإنشاء…',
    aiVideoWaitHint: 'يستغرق إنشاء الفيديو عادةً من 1 إلى 3 دقائق.',
    aiVideoDownload: 'تحميل الفيديو',
    aiVideoTipsTitle: 'نصائح للوصف',
    aiVideoTip1: 'كن محدداً بشأن الإضاءة والمزاج وزاوية الكاميرا.',
    aiVideoTip2: 'اذكر الأسلوب: سينمائي، متحرك، واقعي…',
    aiVideoTip3: 'صف الحركة: بانوراما بطيئة، لقطة مقربة، فاصل زمني…',
    aiVideoTip4: 'حافظ على الوصف أقل من 200 حرف للحصول على أفضل النتائج.',
    aiVideoDisclaimer: 'مدعوم بـ Google Veo عبر Gemini API. قد يستغرق الإنشاء حتى 5 دقائق.',
    navTeam: 'أعضاء الفريق',
    navMessages: 'الرسائل',
    navNotifications: 'الإشعارات',
    navSettings: 'الإعدادات',
    navProfile: 'الملف الشخصي',
    pageDashboardTitle: 'لوحة التحكم',
    pageProjectsTitle: 'المشاريع',
    pageTeamTitle: 'أعضاء الفريق',
    pageMessagesTitle: 'الرسائل',
    pageNotificationsTitle: 'الإشعارات',
    pageSettingsTitle: 'الإعدادات',
    pageProfileTitle: 'الملف الشخصي',
    projectsDashboardTitle: 'لوحة المشاريع',
    newProject: 'مشروع جديد',
    teamMembersTitle: 'أعضاء الفريق',
    inviteMember: 'دعوة عضو',
    messagesTitle: 'الرسائل',
    notificationsTitle: 'الإشعارات',
    settingsTitle: 'الإعدادات',
    createNewProject: 'إنشاء مشروع جديد',
    close: 'إغلاق',
    cancel: 'إلغاء',
    fillSampleData: 'تعبئة بيانات تجريبية',
    loginPresetSuperAdmin: 'المشرف الأعلى',
    loginPresetAdmin: 'مدير',
    loginPresetMember: 'عضو',
    loginPresetHint: 'ملء سريع:',
    audioAttachmentTitle: 'إضافة صوت',
    audioAttachmentSubtitle: 'ارفع ملفًا أو سجّل مذكرة صوتية قصيرة.',
    uploadAudioFile: 'رفع ملف صوتي',
    recordVoiceNote: 'تسجيل صوتي',
    recordingTapStop: 'اضغط إيقاف عند الانتهاء. يتم رفع المقطع تلقائيًا.',
    stopRecording: 'إيقاف',
    recordingNotSupported: 'التسجيل غير مدعوم في هذا المتصفح.',
    micDenied: 'تم رفض إذن الميكروفون.',
    voiceChat: 'محادثة صوتية',
    voiceChatTitle: 'تحدث مع الذكاء الاصطناعي',
    voiceChatHint: 'اضغط على الميكروفون وتحدث بشكل طبيعي',
    voiceChatUnsupported: 'المحادثة الصوتية غير مدعومة في هذا المتصفح.',
    tapToSpeak: 'اضغط للتحدث',
    listening: 'يستمع…',
    aiSpeaking: 'الذكاء الاصطناعي يتحدث…',
    youSaid: 'قلت',
    stopListeningBtn: 'إيقاف',
    emptyRecording: 'لم يتم تسجيل أي شيء.',
    uploadDocument: 'مستند',
    taskDescriptionPlaceholder: 'أضف ملاحظات، معايير القبول، روابط…',
    createProject: 'إنشاء المشروع',
    markAllAsRead: 'تحديد الكل كمقروء',
    saveProfile: 'حفظ الملف الشخصي',
    settingsProfile: 'إعدادات الملف الشخصي',
    settingsNotifications: 'إعدادات الإشعارات',
    settingsWorkspace: 'إعدادات مساحة العمل',
    dashboardHeroTitle: 'لوحة التحكم',
    dashboardHeroSubtitle: 'مرحبًا بعودتك. هذه نظرة سريعة على المشاريع والمهام وتقدم الفريق.',
    dashboardActionNewTask: '+ مهمة جديدة',
    dashboardActionCreateProject: 'إنشاء مشروع',
    dashboardActionInviteTeam: 'دعوة الفريق',
    dashboardOpen: 'فتح لوحة التحكم',
    dashboardCreateTask: 'إنشاء مهمة',
    dashboardInviteTeamCard: 'دعوة الفريق',
    saveChanges: 'حفظ التغييرات',
    shareMeetingLink: 'مشاركة رابط الاجتماع',
    send: 'إرسال',
    submit: 'إرسال',
    addTask: 'إضافة مهمة',
    addSubtask: 'إضافة مهمة فرعية',
    closeButton: 'إغلاق',
    startConversation: 'ابدأ محادثة',
    noMessages: 'لا توجد رسائل',
    noMessagesDesc: 'ابدأ محادثة مع زميلك.',
    composerImage: 'صورة',
    composerVideo: 'فيديو',
    composerAudio: 'صوت',
    composerDocument: 'مستند',
    messageComposerPlaceholder: 'نص بتنسيق… أرفق ملفات من الشريط أدناه.',
    couldNotLoadBoard: 'تعذّر تحميل اللوحة.',
    uploading: 'جاري الرفع…',
  },
  fr: {
    appName: 'CoreTask',
    searchPlaceholder: 'Rechercher des projets, des tâches, des personnes...',
    toggleDarkMode: 'Activer/désactiver le mode sombre',
    notifications: 'Notifications',
    signedInAs: 'Connecté en tant que',
    logout: 'Déconnexion',
    loginTitle: 'Connexion',
    loginSubtitle: "Utilisez vos identifiants d'espace de travail pour continuer.",
    email: 'E-mail',
    password: 'Mot de passe',
    loginSessionHint:
      'Vous pouvez vous connecter avec différents utilisateurs dans des onglets ou fenêtres séparés — chaque session reste indépendante. La déconnexion efface uniquement cet onglet.',
    signIn: 'Se connecter',
    createAccount: 'Créer un compte',
    signingIn: 'Connexion en cours…',
    creatingAccount: 'Création du compte…',
    loginValidationBoth: "Veuillez saisir l'e-mail et le mot de passe.",
    checkingInviteLink: "Vérification de l'invitation…",
    invalidOrExpiredInvite: 'Cette invitation est invalide ou expirée.',
    setYourPasswordTitle: 'Bienvenue — définissez votre mot de passe',
    inviteLinkProblemTitle: "Problème avec le lien d'invitation",
    finishAndSignIn: 'Terminer et se connecter',
    inviteRoleLabel: 'Votre rôle',
    acceptInviteExistingTitle: "Rejoindre l'espace de travail de votre équipe",
    acceptInviteExistingBlurb:
      'Cette invitation utilise votre compte CoreTask existant. Saisissez le mot de passe que vous utilisez déjà pour vous connecter, pas un nouveau.',
    invitePasswordLabelNew: 'Choisissez un mot de passe (8 caractères ou plus)',
    invitePasswordLabelExisting: 'Votre mot de passe CoreTask',
    joinWorkspaceSubmitting: 'Connexion en cours…',
    joinWorkspaceButton: "Accepter l'invitation et se connecter",
    language: 'Langue',
    english: 'Anglais',
    arabic: 'Arabe',
    navDashboard: 'Tableau de bord',
    navProjects: 'Projets',
    navAITasker: 'AI Tasker',
    navAIAssistance: 'Assistance IA',
    navAIVideo: 'Vidéo IA',
    navNotes: 'Notes et rendez-vous',
    notesTabNotes: 'Notes',
    notesTabAppointments: 'Rendez-vous',
    notesSearchPlaceholder: 'Rechercher des notes…',
    notesNewNote: 'Nouvelle note',
    notesNewAppointment: 'Nouveau rendez-vous',
    notesCatAll: 'Tout',
    notesCatWork: 'Travail',
    notesCatPersonal: 'Personnel',
    notesCatClients: 'Clients',
    notesCatOther: 'Autre',
    notesTypeText: 'Texte',
    notesTypeVoice: 'Vocal',
    notesTitleLabel: 'Titre',
    notesTitlePlaceholder: 'Titre de la note…',
    notesContentPlaceholder: 'Écrivez votre note ici…',
    notesColorLabel: 'Couleur',
    notesTagsLabel: 'Étiquettes',
    notesTagsPlaceholder: 'tag1, tag2…',
    notesPinned: 'Épinglée',
    notesSave: 'Enregistrer',
    notesDelete: 'Supprimer',
    notesEdit: 'Modifier',
    notesPin: 'Épingler',
    notesUnpin: 'Désépingler',
    notesRecordStart: 'Appuyer pour enregistrer',
    notesRecordStop: 'Relâcher pour arrêter',
    notesRecording: 'Enregistrement…',
    notesRecordPlay: 'Lire',
    notesRecordPause: 'Pause',
    notesEmpty: 'Aucune note. Créez votre première note.',
    apptTitleLabel: 'Titre',
    apptTitlePlaceholder: 'Titre du rendez-vous…',
    apptDescLabel: 'Description',
    apptDescPlaceholder: 'Détails optionnels…',
    apptDateLabel: 'Date et heure',
    apptReminderLabel: 'Rappel',
    apptReminderNone: 'Pas de rappel',
    apptReminder5: '5 min avant',
    apptReminder10: '10 min avant',
    apptReminder15: '15 min avant',
    apptReminder30: '30 min avant',
    apptReminder60: '1 heure avant',
    apptReminder1440: '1 jour avant',
    apptLinkNoteLabel: 'Lier une note',
    apptLinkNotePlaceholder: 'Sélectionner une note…',
    apptStatusUpcoming: 'À venir',
    apptStatusCompleted: 'Terminé',
    apptStatusCancelled: 'Annulé',
    apptStatusLabel: 'Statut',
    apptEmpty: 'Aucun rendez-vous. Ajoutez le premier.',
    apptMarkComplete: 'Marquer comme terminé',
    apptMarkCancel: 'Annuler le rendez-vous',
    aiVideoHeroTitle: "Générez des vidéos à partir d'un texte",
    aiVideoHeroBody: 'Décrivez une scène et laissez Google Veo la donner vie.',
    aiVideoPromptLabel: 'Décrivez votre vidéo',
    aiVideoPromptPlaceholder: 'Une plage sereine au coucher du soleil avec de douces vagues…',
    aiVideoAspectRatio: "Format d'image",
    aiVideoDuration: 'Durée',
    aiVideoGenerate: 'Générer la vidéo',
    aiVideoGenerating: 'Génération en cours…',
    aiVideoWaitHint: 'La génération prend généralement 1 à 3 minutes.',
    aiVideoDownload: 'Télécharger la vidéo',
    aiVideoTipsTitle: 'Conseils de description',
    aiVideoTip1: "Soyez précis sur l'éclairage, l'ambiance et l'angle de caméra.",
    aiVideoTip2: 'Mentionnez le style : cinématographique, animé, réaliste…',
    aiVideoTip3: 'Décrivez le mouvement : panoramique lente, gros plan, accéléré…',
    aiVideoTip4: 'Gardez les descriptions sous 200 caractères pour de meilleurs résultats.',
    aiVideoDisclaimer: "Propulsé par Google Veo via l'API Gemini. La génération peut durer jusqu'à 5 minutes.",
    navTeam: "Membres de l'équipe",
    navMessages: 'Messages',
    navNotifications: 'Notifications',
    navSettings: 'Paramètres',
    navProfile: 'Profil',
    pageDashboardTitle: 'Tableau de bord',
    pageProjectsTitle: 'Projets',
    pageTeamTitle: "Membres de l'équipe",
    pageMessagesTitle: 'Messages',
    pageNotificationsTitle: 'Notifications',
    pageSettingsTitle: 'Paramètres',
    pageProfileTitle: 'Profil',
    projectsDashboardTitle: 'Tableau de bord des projets',
    newProject: 'Nouveau projet',
    teamMembersTitle: "Membres de l'équipe",
    inviteMember: 'Inviter un membre',
    messagesTitle: 'Messages',
    notificationsTitle: 'Notifications',
    settingsTitle: 'Paramètres',
    createNewProject: 'Créer un nouveau projet',
    close: 'Fermer',
    cancel: 'Annuler',
    fillSampleData: "Remplir avec des données d'exemple",
    loginPresetSuperAdmin: 'Super Admin',
    loginPresetAdmin: 'Admin',
    loginPresetMember: 'Membre',
    loginPresetHint: 'Remplissage rapide :',
    audioAttachmentTitle: 'Ajouter un audio',
    audioAttachmentSubtitle: 'Téléversez un fichier ou enregistrez une courte note vocale.',
    uploadAudioFile: 'Téléverser un fichier audio',
    recordVoiceNote: 'Enregistrer la voix',
    recordingTapStop: 'Appuyez sur arrêter lorsque vous avez terminé. Votre clip sera téléversé automatiquement.',
    stopRecording: 'Arrêter',
    recordingNotSupported: "L'enregistrement n'est pas pris en charge par ce navigateur.",
    micDenied: "L'autorisation du microphone a été refusée.",
    voiceChat: 'Discussion vocale',
    voiceChatTitle: "Parler à l'IA",
    voiceChatHint: 'Appuyez sur le micro et parlez naturellement',
    voiceChatUnsupported: "La discussion vocale n'est pas prise en charge par ce navigateur.",
    tapToSpeak: 'Appuyer pour parler',
    listening: 'Écoute…',
    aiSpeaking: "L'IA parle…",
    youSaid: 'Vous avez dit',
    stopListeningBtn: 'Arrêter',
    emptyRecording: "Aucun contenu n'a été enregistré.",
    uploadDocument: 'Document',
    taskDescriptionPlaceholder: "Ajoutez des notes, critères d'acceptation, liens…",
    createProject: 'Créer le projet',
    markAllAsRead: 'Tout marquer comme lu',
    saveProfile: 'Enregistrer le profil',
    settingsProfile: 'Paramètres du profil',
    settingsNotifications: 'Paramètres des notifications',
    settingsWorkspace: "Paramètres de l'espace de travail",
    dashboardHeroTitle: 'Tableau de bord',
    dashboardHeroSubtitle:
      "Bon retour. Voici un aperçu rapide de vos projets, tâches et de la dynamique de l'équipe.",
    dashboardActionNewTask: '+ Nouvelle tâche',
    dashboardActionCreateProject: 'Créer un projet',
    dashboardActionInviteTeam: "Inviter l'équipe",
    dashboardOpen: 'Ouvrir le tableau de bord',
    dashboardCreateTask: 'Créer une tâche',
    dashboardInviteTeamCard: "Inviter l'équipe",
    saveChanges: 'Enregistrer les modifications',
    shareMeetingLink: 'Partager le lien de réunion',
    send: 'Envoyer',
    submit: 'Envoyer',
    addTask: 'Ajouter une tâche',
    addSubtask: 'Ajouter une sous-tâche',
    closeButton: 'Fermer',
    startConversation: 'Démarrer une conversation',
    noMessages: 'Aucun message',
    noMessagesDesc: 'Démarrez une conversation avec votre coéquipier.',
    composerImage: 'Image',
    composerVideo: 'Vidéo',
    composerAudio: 'Audio',
    composerDocument: 'Document',
    messageComposerPlaceholder: 'Saisissez du texte formaté… joignez des fichiers depuis la barre ci-dessous.',
    couldNotLoadBoard: 'Impossible de charger le tableau.',
    uploading: 'Téléversement…',
  },
  zh: {
    appName: 'CoreTask',
    searchPlaceholder: '搜索项目、任务、人员...',
    toggleDarkMode: '切换深色模式',
    notifications: '通知',
    signedInAs: '登录身份',
    logout: '退出登录',
    loginTitle: '登录',
    loginSubtitle: '使用你的工作区凭据继续。',
    email: '电子邮箱',
    password: '密码',
    loginSessionHint:
      '你可以在不同标签页或窗口中登录不同用户，每个会话保持独立。退出登录只会清除此标签页。',
    signIn: '登录',
    createAccount: '创建账户',
    signingIn: '正在登录…',
    creatingAccount: '正在创建账户…',
    loginValidationBoth: '请输入电子邮箱和密码。',
    checkingInviteLink: '正在检查邀请…',
    invalidOrExpiredInvite: '此邀请链接无效或已过期。',
    setYourPasswordTitle: '欢迎 — 设置你的密码',
    inviteLinkProblemTitle: '邀请链接有问题',
    finishAndSignIn: '完成设置并登录',
    inviteRoleLabel: '你的角色',
    acceptInviteExistingTitle: '加入你的团队工作区',
    acceptInviteExistingBlurb:
      '此邀请使用你现有的 CoreTask 账户。请输入你平时用于登录的密码，而不是新密码。',
    invitePasswordLabelNew: '选择密码（至少 8 个字符）',
    invitePasswordLabelExisting: '你的 CoreTask 密码',
    joinWorkspaceSubmitting: '正在登录…',
    joinWorkspaceButton: '接受邀请并登录',
    language: '语言',
    english: '英语',
    arabic: '阿拉伯语',
    navDashboard: '仪表板',
    navProjects: '项目',
    navAITasker: 'AI Tasker',
    navAIAssistance: 'AI 助手',
    navAIVideo: 'AI 视频',
    aiVideoHeroTitle: '从文字描述生成视频',
    aiVideoHeroBody: '描述一个场景，让 Google Veo 将其变为现实。',
    aiVideoPromptLabel: '描述您的视频',
    aiVideoPromptPlaceholder: '日落时分宁静的海滩，轻柔的海浪和金色的光芒…',
    aiVideoAspectRatio: '宽高比',
    aiVideoDuration: '时长',
    aiVideoGenerate: '生成视频',
    aiVideoGenerating: '生成中…',
    aiVideoWaitHint: '视频生成通常需要 1-3 分钟。',
    aiVideoDownload: '下载视频',
    aiVideoTipsTitle: '提示词技巧',
    aiVideoTip1: '具体描述光线、氛围和拍摄角度。',
    aiVideoTip2: '提及风格：电影感、动画、写实…',
    aiVideoTip3: '描述运动：慢镜平移、特写、延时摄影…',
    aiVideoTip4: '保持描述在 200 字以内以获得最佳效果。',
    aiVideoDisclaimer: '由 Google Veo 通过 Gemini API 驱动，生成最长可达 5 分钟。',
    navNotes: '记事与预约',
    notesTabNotes: '笔记',
    notesTabAppointments: '预约',
    notesSearchPlaceholder: '搜索笔记…',
    notesNewNote: '新建笔记',
    notesNewAppointment: '新建预约',
    notesCatAll: '全部',
    notesCatWork: '工作',
    notesCatPersonal: '个人',
    notesCatClients: '客户',
    notesCatOther: '其他',
    notesTypeText: '文字',
    notesTypeVoice: '语音',
    notesTitleLabel: '标题',
    notesTitlePlaceholder: '笔记标题…',
    notesContentLabel: '内容',
    notesContentPlaceholder: '在此输入笔记内容…',
    notesColorLabel: '颜色',
    notesTagsLabel: '标签',
    notesTagsPlaceholder: '标签1, 标签2…',
    notesPinned: '置顶',
    notesSave: '保存',
    notesDelete: '删除',
    notesEdit: '编辑',
    notesPin: '置顶',
    notesUnpin: '取消置顶',
    notesRecordStart: '开始录音',
    notesRecordStop: '停止录音',
    notesRecording: '录音中…',
    notesRecordPlay: '播放',
    notesRecordPause: '暂停',
    notesEmpty: '暂无笔记。点击"新建笔记"开始。',
    apptTitleLabel: '标题',
    apptTitlePlaceholder: '预约标题…',
    apptDescLabel: '描述',
    apptDescPlaceholder: '详细信息…',
    apptDateLabel: '日期与时间',
    apptReminderLabel: '提醒',
    apptReminderNone: '无提醒',
    apptReminder5: '提前 5 分钟',
    apptReminder10: '提前 10 分钟',
    apptReminder15: '提前 15 分钟',
    apptReminder30: '提前 30 分钟',
    apptReminder60: '提前 1 小时',
    apptReminder1440: '提前 1 天',
    apptLinkNoteLabel: '关联笔记',
    apptLinkNotePlaceholder: '选择笔记（可选）',
    apptStatusLabel: '状态',
    apptStatusUpcoming: '即将到来',
    apptStatusCompleted: '已完成',
    apptStatusCancelled: '已取消',
    apptMarkComplete: '标记完成',
    apptMarkCancel: '取消预约',
    apptEmpty: '暂无预约。点击"新建预约"开始。',
    navTeam: '团队成员',
    navMessages: '消息',
    navNotifications: '通知',
    navSettings: '设置',
    navProfile: '个人资料',
    pageDashboardTitle: '仪表板',
    pageProjectsTitle: '项目',
    pageTeamTitle: '团队成员',
    pageMessagesTitle: '消息',
    pageNotificationsTitle: '通知',
    pageSettingsTitle: '设置',
    pageProfileTitle: '个人资料',
    projectsDashboardTitle: '项目仪表板',
    newProject: '新建项目',
    teamMembersTitle: '团队成员',
    inviteMember: '邀请成员',
    messagesTitle: '消息',
    notificationsTitle: '通知',
    settingsTitle: '设置',
    createNewProject: '创建新项目',
    close: '关闭',
    cancel: '取消',
    fillSampleData: '填充示例数据',
    loginPresetSuperAdmin: '超级管理员',
    loginPresetAdmin: '管理员',
    loginPresetMember: '成员',
    loginPresetHint: '快速填写：',
    audioAttachmentTitle: '添加音频',
    audioAttachmentSubtitle: '上传文件或录制简短语音备注。',
    uploadAudioFile: '上传音频文件',
    recordVoiceNote: '录制语音',
    recordingTapStop: '完成后点击停止。你的音频片段会自动上传。',
    stopRecording: '停止',
    recordingNotSupported: '此浏览器不支持录音。',
    micDenied: '麦克风权限被拒绝。',
    voiceChat: '语音对话',
    voiceChatTitle: '与AI对话',
    voiceChatHint: '按下麦克风自然说话',
    voiceChatUnsupported: '此浏览器不支持语音对话。',
    tapToSpeak: '点击说话',
    listening: '聆听中…',
    aiSpeaking: 'AI正在说话…',
    youSaid: '你说',
    stopListeningBtn: '停止',
    emptyRecording: '没有录制任何内容。',
    uploadDocument: '文档',
    taskDescriptionPlaceholder: '添加备注、验收标准、链接…',
    createProject: '创建项目',
    markAllAsRead: '全部标记为已读',
    saveProfile: '保存个人资料',
    settingsProfile: '个人资料设置',
    settingsNotifications: '通知设置',
    settingsWorkspace: '工作区设置',
    dashboardHeroTitle: '仪表板',
    dashboardHeroSubtitle: '欢迎回来。这里是你的项目、任务和团队进展的快速概览。',
    dashboardActionNewTask: '+ 新任务',
    dashboardActionCreateProject: '创建项目',
    dashboardActionInviteTeam: '邀请团队',
    dashboardOpen: '打开仪表板',
    dashboardCreateTask: '创建任务',
    dashboardInviteTeamCard: '邀请团队',
    saveChanges: '保存更改',
    shareMeetingLink: '分享会议链接',
    send: '发送',
    submit: '提交',
    addTask: '添加任务',
    addSubtask: '添加子任务',
    closeButton: '关闭',
    startConversation: '开始对话',
    noMessages: '没有消息',
    noMessagesDesc: '与你的队友开始对话。',
    composerImage: '图片',
    composerVideo: '视频',
    composerAudio: '音频',
    composerDocument: '文档',
    messageComposerPlaceholder: '输入格式化文本… 从下方工具栏附加文件。',
    couldNotLoadBoard: '板子加载失败。',
    uploading: '上传中…',
  },
  ur: {
    appName: 'CoreTask',
    searchPlaceholder: 'پروجیکٹس، ٹاسکس، لوگوں کو تلاش کریں...',
    toggleDarkMode: 'ڈارک موڈ تبدیل کریں',
    notifications: 'اطلاعات',
    signedInAs: 'سائن ان بطور',
    logout: 'لاگ آؤٹ',
    loginTitle: 'لاگ ان',
    loginSubtitle: 'جاری رکھنے کے لیے اپنی ورک اسپیس کی اسناد استعمال کریں۔',
    email: 'ای میل',
    password: 'پاس ورڈ',
    loginSessionHint:
      'آپ الگ ٹیبز یا ونڈوز میں مختلف صارفین کے طور پر سائن ان کر سکتے ہیں — ہر سیشن آزاد رہتا ہے۔ لاگ آؤٹ صرف اس ٹیب کو صاف کرتا ہے۔',
    signIn: 'سائن ان',
    createAccount: 'اکاؤنٹ بنائیں',
    signingIn: 'سائن ان ہو رہا ہے…',
    creatingAccount: 'اکاؤنٹ بنایا جا رہا ہے…',
    loginValidationBoth: 'براہ کرم ای میل اور پاس ورڈ دونوں درج کریں۔',
    checkingInviteLink: 'دعوت چیک کی جا رہی ہے…',
    invalidOrExpiredInvite: 'یہ دعوتی لنک غلط ہے یا ختم ہو چکا ہے۔',
    setYourPasswordTitle: 'خوش آمدید — اپنا پاس ورڈ سیٹ کریں',
    inviteLinkProblemTitle: 'دعوتی لنک کا مسئلہ',
    finishAndSignIn: 'سیٹ اپ مکمل کریں اور سائن ان کریں',
    inviteRoleLabel: 'آپ کا کردار',
    acceptInviteExistingTitle: 'اپنی ٹیم ورک اسپیس میں شامل ہوں',
    acceptInviteExistingBlurb:
      'یہ دعوت آپ کے موجودہ CoreTask اکاؤنٹ کو استعمال کرتی ہے۔ وہ پاس ورڈ درج کریں جو آپ پہلے سے سائن ان کے لیے استعمال کرتے ہیں، نیا پاس ورڈ نہیں۔',
    invitePasswordLabelNew: 'پاس ورڈ منتخب کریں (8 یا زیادہ حروف)',
    invitePasswordLabelExisting: 'آپ کا CoreTask پاس ورڈ',
    joinWorkspaceSubmitting: 'آپ کو سائن ان کیا جا رہا ہے…',
    joinWorkspaceButton: 'دعوت قبول کریں اور سائن ان کریں',
    language: 'زبان',
    english: 'انگریزی',
    arabic: 'عربی',
    navDashboard: 'ڈیش بورڈ',
    navProjects: 'پروجیکٹس',
    navAITasker: 'AI Tasker',
    navAIAssistance: 'AI معاونت',
    navAIVideo: 'AI ویڈیو',
    aiVideoHeroTitle: 'متن سے ویڈیو بنائیں',
    aiVideoHeroBody: 'ایک منظر بیان کریں اور Google Veo اسے حقیقت بنائے۔',
    aiVideoPromptLabel: 'اپنی ویڈیو بیان کریں',
    aiVideoPromptPlaceholder: 'غروب آفتاب پر ایک پرسکون ساحل جہاں ہلکی لہریں اور سنہری روشنی ہو…',
    aiVideoAspectRatio: 'پہلو تناسب',
    aiVideoDuration: 'مدت',
    aiVideoGenerate: 'ویڈیو بنائیں',
    aiVideoGenerating: 'بن رہی ہے…',
    aiVideoWaitHint: 'ویڈیو بنانے میں عموماً 1 سے 3 منٹ لگتے ہیں۔',
    aiVideoDownload: 'ویڈیو ڈاؤن لوڈ کریں',
    aiVideoTipsTitle: 'وصف کی تجاویز',
    aiVideoTip1: 'روشنی، موڈ اور کیمرے کے زاویے کے بارے میں واضح رہیں۔',
    aiVideoTip2: 'طرز بتائیں: سینماٹک، اینیمیٹڈ، حقیقی…',
    aiVideoTip3: 'حرکت بیان کریں: آہستہ پیننگ، کلوز اپ، ٹائم لیپس…',
    aiVideoTip4: 'بہترین نتائج کے لیے وصف 200 حروف سے کم رکھیں۔',
    aiVideoDisclaimer: 'Google Veo بذریعہ Gemini API کے ذریعے تقویت یافتہ۔ تخلیق میں 5 منٹ تک لگ سکتے ہیں۔',
    navNotes: 'نوٹس اور ملاقاتیں',
    notesTabNotes: 'نوٹس',
    notesTabAppointments: 'ملاقاتیں',
    notesSearchPlaceholder: 'نوٹس تلاش کریں…',
    notesNewNote: 'نیا نوٹ',
    notesNewAppointment: 'نئی ملاقات',
    notesCatAll: 'سب',
    notesCatWork: 'کام',
    notesCatPersonal: 'ذاتی',
    notesCatClients: 'کلائنٹس',
    notesCatOther: 'دیگر',
    notesTypeText: 'متن',
    notesTypeVoice: 'آواز',
    notesTitleLabel: 'عنوان',
    notesTitlePlaceholder: 'نوٹ کا عنوان…',
    notesContentLabel: 'مواد',
    notesContentPlaceholder: 'یہاں نوٹ لکھیں…',
    notesColorLabel: 'رنگ',
    notesTagsLabel: 'ٹیگز',
    notesTagsPlaceholder: 'ٹیگ1، ٹیگ2…',
    notesPinned: 'پن کریں',
    notesSave: 'محفوظ کریں',
    notesDelete: 'حذف کریں',
    notesEdit: 'ترمیم',
    notesPin: 'پن کریں',
    notesUnpin: 'پن ہٹائیں',
    notesRecordStart: 'ریکارڈنگ شروع کریں',
    notesRecordStop: 'ریکارڈنگ روکیں',
    notesRecording: 'ریکارڈ ہو رہا ہے…',
    notesRecordPlay: 'چلائیں',
    notesRecordPause: 'رکیں',
    notesEmpty: 'کوئی نوٹ نہیں۔ شروع کرنے کے لیے "نیا نوٹ" دبائیں۔',
    apptTitleLabel: 'عنوان',
    apptTitlePlaceholder: 'ملاقات کا عنوان…',
    apptDescLabel: 'تفصیل',
    apptDescPlaceholder: 'مزید معلومات…',
    apptDateLabel: 'تاریخ اور وقت',
    apptReminderLabel: 'یاد دہانی',
    apptReminderNone: 'کوئی یاد دہانی نہیں',
    apptReminder5: '5 منٹ پہلے',
    apptReminder10: '10 منٹ پہلے',
    apptReminder15: '15 منٹ پہلے',
    apptReminder30: '30 منٹ پہلے',
    apptReminder60: '1 گھنٹہ پہلے',
    apptReminder1440: '1 دن پہلے',
    apptLinkNoteLabel: 'نوٹ سے جوڑیں',
    apptLinkNotePlaceholder: 'نوٹ منتخب کریں (اختیاری)',
    apptStatusLabel: 'حالت',
    apptStatusUpcoming: 'آنے والی',
    apptStatusCompleted: 'مکمل',
    apptStatusCancelled: 'منسوخ',
    apptMarkComplete: 'مکمل نشان زد کریں',
    apptMarkCancel: 'ملاقات منسوخ کریں',
    apptEmpty: 'کوئی ملاقات نہیں۔ شروع کرنے کے لیے "نئی ملاقات" دبائیں۔',
    navTeam: 'ٹیم ممبرز',
    navMessages: 'پیغامات',
    navNotifications: 'اطلاعات',
    navSettings: 'سیٹنگز',
    navProfile: 'پروفائل',
    pageDashboardTitle: 'ڈیش بورڈ',
    pageProjectsTitle: 'پروجیکٹس',
    pageTeamTitle: 'ٹیم ممبرز',
    pageMessagesTitle: 'پیغامات',
    pageNotificationsTitle: 'اطلاعات',
    pageSettingsTitle: 'سیٹنگز',
    pageProfileTitle: 'پروفائل',
    projectsDashboardTitle: 'پروجیکٹس ڈیش بورڈ',
    newProject: 'نیا پروجیکٹ',
    teamMembersTitle: 'ٹیم ممبرز',
    inviteMember: 'ممبر کو مدعو کریں',
    messagesTitle: 'پیغامات',
    notificationsTitle: 'اطلاعات',
    settingsTitle: 'سیٹنگز',
    createNewProject: 'نیا پروجیکٹ بنائیں',
    close: 'بند کریں',
    cancel: 'منسوخ کریں',
    fillSampleData: 'نمونہ ڈیٹا بھریں',
    loginPresetSuperAdmin: 'سپر ایڈمن',
    loginPresetAdmin: 'ایڈمن',
    loginPresetMember: 'رکن',
    loginPresetHint: 'فوری پُر کریں:',
    audioAttachmentTitle: 'آڈیو شامل کریں',
    audioAttachmentSubtitle: 'فائل اپ لوڈ کریں یا مختصر وائس نوٹ ریکارڈ کریں۔',
    uploadAudioFile: 'آڈیو فائل اپ لوڈ کریں',
    recordVoiceNote: 'آواز ریکارڈ کریں',
    recordingTapStop: 'مکمل ہونے پر اسٹاپ دبائیں۔ آپ کا کلپ خودکار طور پر اپ لوڈ ہو جائے گا۔',
    stopRecording: 'روکیں',
    recordingNotSupported: 'اس براؤزر میں ریکارڈنگ سپورٹ نہیں ہے۔',
    micDenied: 'مائیکروفون کی اجازت مسترد کر دی گئی۔',
    voiceChat: 'آواز سے بات چیت',
    voiceChatTitle: 'AI سے بات کریں',
    voiceChatHint: 'مائیکروفون دبائیں اور قدرتی طریقے سے بولیں',
    voiceChatUnsupported: 'اس براؤزر میں آواز سے بات چیت سپورٹ نہیں ہے۔',
    tapToSpeak: 'بولنے کے لیے دبائیں',
    listening: 'سن رہا ہے…',
    aiSpeaking: 'AI بول رہا ہے…',
    youSaid: 'آپ نے کہا',
    stopListeningBtn: 'روکیں',
    emptyRecording: 'کچھ بھی ریکارڈ نہیں ہوا۔',
    uploadDocument: 'دستاویز',
    taskDescriptionPlaceholder: 'نوٹس، قبولیت کے معیار، لنکس شامل کریں…',
    createProject: 'پروجیکٹ بنائیں',
    markAllAsRead: 'سب کو پڑھا ہوا نشان زد کریں',
    saveProfile: 'پروفائل محفوظ کریں',
    settingsProfile: 'پروفائل سیٹنگز',
    settingsNotifications: 'اطلاعات کی سیٹنگز',
    settingsWorkspace: 'ورک اسپیس سیٹنگز',
    dashboardHeroTitle: 'ڈیش بورڈ',
    dashboardHeroSubtitle: 'واپسی پر خوش آمدید۔ یہاں آپ کے پروجیکٹس، ٹاسکس، اور ٹیم کی پیش رفت کا فوری خلاصہ ہے۔',
    dashboardActionNewTask: '+ نیا ٹاسک',
    dashboardActionCreateProject: 'پروجیکٹ بنائیں',
    dashboardActionInviteTeam: 'ٹیم کو مدعو کریں',
    dashboardOpen: 'ڈیش بورڈ کھولیں',
    dashboardCreateTask: 'ٹاسک بنائیں',
    dashboardInviteTeamCard: 'ٹیم کو مدعو کریں',
    saveChanges: 'تبدیلیاں محفوظ کریں',
    shareMeetingLink: 'میٹنگ لنک شیئر کریں',
    send: 'بھیجیں',
    submit: 'جمع کریں',
    addTask: 'ٹاسک شامل کریں',
    addSubtask: 'سب ٹاسک شامل کریں',
    closeButton: 'بند کریں',
    startConversation: 'گفتگو شروع کریں',
    noMessages: 'کوئی پیغام نہیں',
    noMessagesDesc: 'اپنے ٹیم میٹ کے ساتھ گفتگو شروع کریں۔',
    composerImage: 'تصویر',
    composerVideo: 'ویڈیو',
    composerAudio: 'آڈیو',
    composerDocument: 'دستاویز',
    messageComposerPlaceholder: 'فارمیٹ شدہ متن لکھیں… نیچے ٹول بار سے فائلیں منسلک کریں۔',
    couldNotLoadBoard: 'بورڈ لوڈ نہیں ہو سکا۔',
    uploading: 'اپ لوڈ ہو رہا ہے…',
  },
};

const stepOneTranslations = {
  en: {
    coretaskLogoAlt: 'CoreTask logo',
    loginHeroBadge: 'CoreTask Workspace',
    loginHeroTitle: 'Welcome back',
    loginHeroBody: 'Sign in to manage projects, collaborate with your team, and track delivery progress.',
    loginHeroBulletDashboard: 'Unified project dashboard',
    loginHeroBulletMessages: 'Channel discussions and direct messages',
    loginHeroBulletPlanning: 'Kanban, list, and timeline planning',
    loginFeatureDashboardDesc: 'Get a complete overview of your projects and team\'s progress.',
    loginFeatureMessagesDesc: 'Stay connected with your team in real time.',
    loginFeaturePlanningDesc: 'Plan, organize, and track tasks your way.',
    forgotPassword: 'Forgot password?',
    emailPlaceholder: 'name@company.com',
    passwordPlaceholder: 'Enter your password',
    dashboardStatTotalProjects: 'Total Projects',
    dashboardStatOpenTasks: 'Open Tasks',
    dashboardStatCompletedTasks: 'Completed Tasks',
    dashboardStatTeamMembers: 'Team Members',
    dashboardThisMonth: 'this month',
    dashboardNoNewProjectsThisMonth: 'No new projects this month',
    dashboardDueNext7Days: 'due in next 7 days',
    dashboardNothingDueThisWeek: 'Nothing due this week',
    dashboardTodayUtc: 'today (UTC)',
    dashboardNoCompletionsToday: 'No completions today',
    dashboardOnline: 'online',
    dashboardNoneMarkedOnline: 'None marked online',
    dashboardInviteFromTeam: 'Invite from Team',
    dashboardLoading: 'Loading dashboard…',
    dashboardProjectProgress: 'Project Progress',
    viewAll: 'View All',
    dashboardNoProjectsYetCreate: 'No projects yet. Create one from the Projects page.',
    dashboardUpcomingTasks: 'Upcoming Tasks',
    dashboardNoDatedOpenTasks: 'No dated open tasks. Add due dates from the board.',
    dashboardWeeklyThroughput: 'Weekly Throughput',
    dashboardTasksCreatedNoCompletionsYet: 'Tasks created (no completions yet)',
    dashboardChartDay: 'Day',
    dashboardRecentActivity: 'Recent Activity',
    today: 'Today',
    tomorrow: 'Tomorrow',
    yesterday: 'Yesterday',
    projectDefaultDescription: 'Newly created project ready for tasks and collaboration.',
    projectName: 'Project Name',
    description: 'Description',
    colorPicker: 'Color Picker',
    membersMultiSelect: 'Members (multi-select)',
    startDate: 'Start Date',
    endDate: 'End Date',
    loadingProjectBoard: 'Loading project board…',
    backToProjects: 'Back to projects',
    projectsBreadcrumb: 'Projects',
    projectBoard: 'Project Board',
    viewBoard: 'Board',
    viewList: 'List',
    viewChannel: 'Channel',
    viewTimeline: 'Timeline',
    categoriesAndLabels: 'Categories & Labels',
    allAssignees: 'All Assignees',
    allPriorities: 'All Priorities',
    searchBoardTasks: 'Search board tasks...',
    advancedFilters: 'Advanced Filters',
    assignee: 'Assignee',
    priority: 'Priority',
    status: 'Status',
    category: 'Category',
    label: 'Label',
    selectAssignee: 'Select assignee',
    selectPriority: 'Select priority',
    selectStatus: 'Select status',
    selectCategory: 'Select category',
    selectLabel: 'Select label',
    dueDateRange: 'Due Date Range',
    dueDateRangePlaceholder: 'e.g. May 10 - May 20',
    zoom: 'Zoom',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    groupBy: 'Group by',
    task: 'Task',
    actions: 'Actions',
    edit: 'Edit',
    comment: 'Comment',
    assign: 'Assign',
    noTasksInColumn: 'No tasks in column',
    addTaskWorkflowStage: 'Add a task to start this workflow stage.',
    noTasksInStageYet: 'No tasks in this stage yet.',
    media: 'Media',
    unassigned: 'Unassigned',
    simulateDrag: 'Simulate drag',
    moveLeft: 'Move Left',
    moveRight: 'Move Right',
    quickAddTask: 'Quick add task...',
    projectsDashboardSubtitle: 'Monitor project progress, statuses, and team collaboration in one view.',
    statActiveTasks: 'Active Tasks',
    statCompleted: 'Completed',
    filterAll: 'All',
    statusActive: 'Active',
    statusCompleted: 'Completed',
    statusOnHold: 'On Hold',
    noProjectsYet: 'No projects yet',
    noProjectsEmptyCreate: 'Create your first project to start tracking tasks and team progress.',
    noProjectsEmptyInvited: 'When you are invited to projects, they will appear here.',
    createProjectCta: '+ Create Project',
    progress: 'Progress',
    taskSingular: 'task',
    taskPlural: 'tasks',
    openProject: 'Open Project',
    newProjectServerIdError: 'Could not read new project id from the server.',
    createdDateRange: 'Created Date Range',
    moveFailed: 'Move failed',
    couldNotCreateTask: 'Could not create task.',
    renameColumnPrompt: 'Rename column',
    newColumnNamePrompt: 'New column name',
    clearAllFilters: 'Clear All Filters',
    saveFilterViewPrompt: 'Save filter view as',
    saveFilterAsView: 'Save Filter as View',
    newTimelineTask: 'New Timeline Task',
    selected: 'selected',
    assignTo: 'Assign To',
    changeStatus: 'Change Status',
    delete: 'Delete',
    moveToProject: 'Move to Project',
    sort: 'Sort',
    taskNumber: 'Task #',
    taskTitle: 'Task Title',
    dueDate: 'Due Date',
    subtasks: 'Subtasks',
    dependencySingular: 'dependency',
    dependencyPlural: 'dependencies',
    subtaskFor: 'Subtask',
    for: 'for',
    ungrouped: 'Ungrouped',
    priorityLow: 'Low',
    priorityMedium: 'Medium',
    priorityHigh: 'High',
    columnBacklog: 'Backlog',
    columnToDo: 'To Do',
    columnInProgress: 'In Progress',
    columnInReview: 'In Review',
    columnDone: 'Done',
  },
  ar: {
    coretaskLogoAlt: 'شعار كور تاسك',
    loginHeroBadge: 'مساحة عمل كور تاسك',
    loginHeroTitle: 'مرحبًا بعودتك',
    loginHeroBody: 'سجّل الدخول لإدارة المشاريع، والتعاون مع فريقك، ومتابعة تقدم التسليم.',
    loginHeroBulletDashboard: 'لوحة مشاريع موحدة',
    loginHeroBulletMessages: 'نقاشات القنوات والرسائل المباشرة',
    loginHeroBulletPlanning: 'تخطيط كانبان وقوائم وجداول زمنية',
    loginFeatureDashboardDesc: 'احصل على نظرة شاملة لمشاريعك وتقدم فريقك.',
    loginFeatureMessagesDesc: 'ابقَ على تواصل مع فريقك في الوقت الفعلي.',
    loginFeaturePlanningDesc: 'خطّط ونظّم وتابع المهام بالطريقة التي تناسبك.',
    forgotPassword: 'نسيت كلمة المرور؟',
    emailPlaceholder: 'name@company.com',
    passwordPlaceholder: 'أدخل كلمة المرور',
    dashboardStatTotalProjects: 'إجمالي المشاريع',
    dashboardStatOpenTasks: 'المهام المفتوحة',
    dashboardStatCompletedTasks: 'المهام المكتملة',
    dashboardStatTeamMembers: 'أعضاء الفريق',
    dashboardThisMonth: 'هذا الشهر',
    dashboardNoNewProjectsThisMonth: 'لا توجد مشاريع جديدة هذا الشهر',
    dashboardDueNext7Days: 'مستحقة خلال 7 أيام',
    dashboardNothingDueThisWeek: 'لا يوجد شيء مستحق هذا الأسبوع',
    dashboardTodayUtc: 'اليوم (UTC)',
    dashboardNoCompletionsToday: 'لا توجد إكمالات اليوم',
    dashboardOnline: 'متصل',
    dashboardNoneMarkedOnline: 'لا أحد محدد كمتصل',
    dashboardInviteFromTeam: 'ادعُ من صفحة الفريق',
    dashboardLoading: 'جاري تحميل لوحة التحكم…',
    dashboardProjectProgress: 'تقدم المشاريع',
    viewAll: 'عرض الكل',
    dashboardNoProjectsYetCreate: 'لا توجد مشاريع بعد. أنشئ مشروعًا من صفحة المشاريع.',
    dashboardUpcomingTasks: 'المهام القادمة',
    dashboardNoDatedOpenTasks: 'لا توجد مهام مفتوحة بتاريخ. أضف تواريخ استحقاق من اللوحة.',
    dashboardWeeklyThroughput: 'الإنتاجية الأسبوعية',
    dashboardTasksCreatedNoCompletionsYet: 'مهام تم إنشاؤها (لا توجد إكمالات بعد)',
    dashboardChartDay: 'اليوم',
    dashboardRecentActivity: 'النشاط الأخير',
    today: 'اليوم',
    tomorrow: 'غدًا',
    yesterday: 'أمس',
    projectDefaultDescription: 'مشروع جديد جاهز للمهام والتعاون.',
    projectName: 'اسم المشروع',
    description: 'الوصف',
    colorPicker: 'اختيار اللون',
    membersMultiSelect: 'الأعضاء (اختيار متعدد)',
    startDate: 'تاريخ البدء',
    endDate: 'تاريخ الانتهاء',
    loadingProjectBoard: 'جاري تحميل لوحة المشروع…',
    backToProjects: 'العودة إلى المشاريع',
    projectsBreadcrumb: 'المشاريع',
    projectBoard: 'لوحة المشروع',
    viewBoard: 'اللوحة',
    viewList: 'القائمة',
    viewChannel: 'القناة',
    viewTimeline: 'الجدول الزمني',
    categoriesAndLabels: 'الفئات والتسميات',
    allAssignees: 'كل المسؤولين',
    allPriorities: 'كل الأولويات',
    searchBoardTasks: 'ابحث في مهام اللوحة...',
    advancedFilters: 'فلاتر متقدمة',
    assignee: 'المسؤول',
    priority: 'الأولوية',
    status: 'الحالة',
    category: 'الفئة',
    label: 'التسمية',
    selectAssignee: 'اختر المسؤول',
    selectPriority: 'اختر الأولوية',
    selectStatus: 'اختر الحالة',
    selectCategory: 'اختر الفئة',
    selectLabel: 'اختر التسمية',
    dueDateRange: 'نطاق تاريخ الاستحقاق',
    dueDateRangePlaceholder: 'مثال: 10 مايو - 20 مايو',
    zoom: 'التكبير',
    day: 'يوم',
    week: 'أسبوع',
    month: 'شهر',
    groupBy: 'تجميع حسب',
    task: 'المهمة',
    actions: 'الإجراءات',
    edit: 'تعديل',
    comment: 'تعليق',
    assign: 'تعيين',
    noTasksInColumn: 'لا توجد مهام في العمود',
    addTaskWorkflowStage: 'أضف مهمة لبدء مرحلة سير العمل هذه.',
    noTasksInStageYet: 'لا توجد مهام في هذه المرحلة بعد.',
    media: 'وسائط',
    unassigned: 'غير معين',
    simulateDrag: 'محاكاة السحب',
    moveLeft: 'نقل لليسار',
    moveRight: 'نقل لليمين',
    quickAddTask: 'إضافة مهمة سريعة...',
    projectsDashboardSubtitle: 'راقب تقدم المشاريع والحالات وتعاون الفريق في عرض واحد.',
    statActiveTasks: 'المهام النشطة',
    statCompleted: 'مكتمل',
    filterAll: 'الكل',
    statusActive: 'نشط',
    statusCompleted: 'مكتمل',
    statusOnHold: 'معلق',
    noProjectsYet: 'لا توجد مشاريع بعد',
    noProjectsEmptyCreate: 'أنشئ مشروعك الأول لبدء متابعة المهام وتقدم الفريق.',
    noProjectsEmptyInvited: 'عندما تتم دعوتك إلى مشاريع، ستظهر هنا.',
    createProjectCta: '+ إنشاء مشروع',
    progress: 'التقدم',
    taskSingular: 'مهمة',
    taskPlural: 'مهام',
    openProject: 'فتح المشروع',
    newProjectServerIdError: 'تعذر قراءة رقم المشروع الجديد من الخادم.',
    createdDateRange: 'نطاق تاريخ الإنشاء',
    moveFailed: 'فشل النقل',
    couldNotCreateTask: 'تعذر إنشاء المهمة.',
    renameColumnPrompt: 'إعادة تسمية العمود',
    newColumnNamePrompt: 'اسم العمود الجديد',
    clearAllFilters: 'مسح كل الفلاتر',
    saveFilterViewPrompt: 'حفظ عرض الفلتر باسم',
    saveFilterAsView: 'حفظ الفلتر كعرض',
    newTimelineTask: 'مهمة جديدة على الجدول الزمني',
    selected: 'محدد',
    assignTo: 'تعيين إلى',
    changeStatus: 'تغيير الحالة',
    delete: 'حذف',
    moveToProject: 'نقل إلى مشروع',
    sort: 'ترتيب',
    taskNumber: 'رقم المهمة',
    taskTitle: 'عنوان المهمة',
    dueDate: 'تاريخ الاستحقاق',
    subtasks: 'المهام الفرعية',
    dependencySingular: 'اعتماد',
    dependencyPlural: 'اعتمادات',
    subtaskFor: 'مهمة فرعية',
    for: 'لـ',
    ungrouped: 'غير مجمع',
    priorityLow: 'منخفضة',
    priorityMedium: 'متوسطة',
    priorityHigh: 'عالية',
    columnBacklog: 'الأعمال المتراكمة',
    columnToDo: 'قيد التنفيذ لاحقًا',
    columnInProgress: 'قيد العمل',
    columnInReview: 'قيد المراجعة',
    columnDone: 'منجز',
  },
  fr: {
    coretaskLogoAlt: 'Logo CoreTask',
    loginHeroBadge: 'Espace de travail CoreTask',
    loginHeroTitle: 'Bon retour',
    loginHeroBody: 'Connectez-vous pour gérer les projets, collaborer avec votre équipe et suivre la livraison.',
    loginHeroBulletDashboard: 'Tableau de bord projet unifié',
    loginHeroBulletMessages: 'Discussions de canaux et messages directs',
    loginHeroBulletPlanning: 'Planification Kanban, liste et chronologie',
    loginFeatureDashboardDesc: 'Vue complète de vos projets et de l\'avancement de votre équipe.',
    loginFeatureMessagesDesc: 'Restez connecté avec votre équipe en temps réel.',
    loginFeaturePlanningDesc: 'Planifiez, organisez et suivez les tâches à votre façon.',
    forgotPassword: 'Mot de passe oublié ?',
    emailPlaceholder: 'nom@entreprise.com',
    passwordPlaceholder: 'Saisissez votre mot de passe',
    dashboardStatTotalProjects: 'Total des projets',
    dashboardStatOpenTasks: 'Tâches ouvertes',
    dashboardStatCompletedTasks: 'Tâches terminées',
    dashboardStatTeamMembers: "Membres de l'équipe",
    dashboardThisMonth: 'ce mois-ci',
    dashboardNoNewProjectsThisMonth: 'Aucun nouveau projet ce mois-ci',
    dashboardDueNext7Days: 'à échéance dans les 7 prochains jours',
    dashboardNothingDueThisWeek: 'Rien à échéance cette semaine',
    dashboardTodayUtc: "aujourd'hui (UTC)",
    dashboardNoCompletionsToday: "Aucune tâche terminée aujourd'hui",
    dashboardOnline: 'en ligne',
    dashboardNoneMarkedOnline: "Personne n'est marqué en ligne",
    dashboardInviteFromTeam: "Inviter depuis l'équipe",
    dashboardLoading: 'Chargement du tableau de bord…',
    dashboardProjectProgress: 'Avancement des projets',
    viewAll: 'Tout voir',
    dashboardNoProjectsYetCreate: 'Aucun projet pour le moment. Créez-en un depuis la page Projets.',
    dashboardUpcomingTasks: 'Tâches à venir',
    dashboardNoDatedOpenTasks: "Aucune tâche ouverte datée. Ajoutez des dates d'échéance depuis le tableau.",
    dashboardWeeklyThroughput: 'Débit hebdomadaire',
    dashboardTasksCreatedNoCompletionsYet: 'Tâches créées (aucune terminée pour le moment)',
    dashboardChartDay: 'Jour',
    dashboardRecentActivity: 'Activité récente',
    today: "Aujourd'hui",
    tomorrow: 'Demain',
    yesterday: 'Hier',
    projectDefaultDescription: 'Nouveau projet prêt pour les tâches et la collaboration.',
    projectName: 'Nom du projet',
    description: 'Description',
    colorPicker: 'Sélecteur de couleur',
    membersMultiSelect: 'Membres (sélection multiple)',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    loadingProjectBoard: 'Chargement du tableau du projet…',
    backToProjects: 'Retour aux projets',
    projectsBreadcrumb: 'Projets',
    projectBoard: 'Tableau du projet',
    viewBoard: 'Tableau',
    viewList: 'Liste',
    viewChannel: 'Canal',
    viewTimeline: 'Chronologie',
    categoriesAndLabels: 'Catégories et libellés',
    allAssignees: 'Tous les responsables',
    allPriorities: 'Toutes les priorités',
    searchBoardTasks: 'Rechercher dans les tâches du tableau...',
    advancedFilters: 'Filtres avancés',
    assignee: 'Responsable',
    priority: 'Priorité',
    status: 'Statut',
    category: 'Catégorie',
    label: 'Libellé',
    selectAssignee: 'Sélectionner un responsable',
    selectPriority: 'Sélectionner une priorité',
    selectStatus: 'Sélectionner un statut',
    selectCategory: 'Sélectionner une catégorie',
    selectLabel: 'Sélectionner un libellé',
    dueDateRange: "Plage d'échéance",
    dueDateRangePlaceholder: 'ex. 10 mai - 20 mai',
    zoom: 'Zoom',
    day: 'Jour',
    week: 'Semaine',
    month: 'Mois',
    groupBy: 'Grouper par',
    task: 'Tâche',
    actions: 'Actions',
    edit: 'Modifier',
    comment: 'Commenter',
    assign: 'Assigner',
    noTasksInColumn: 'Aucune tâche dans la colonne',
    addTaskWorkflowStage: 'Ajoutez une tâche pour démarrer cette étape du workflow.',
    noTasksInStageYet: 'Aucune tâche à cette étape pour le moment.',
    media: 'Média',
    unassigned: 'Non assigné',
    simulateDrag: 'Simuler le glisser',
    moveLeft: 'Déplacer à gauche',
    moveRight: 'Déplacer à droite',
    quickAddTask: 'Ajout rapide de tâche...',
    projectsDashboardSubtitle: "Surveillez l'avancement, les statuts et la collaboration de l'équipe dans une seule vue.",
    statActiveTasks: 'Tâches actives',
    statCompleted: 'Terminé',
    filterAll: 'Tout',
    statusActive: 'Actif',
    statusCompleted: 'Terminé',
    statusOnHold: 'En pause',
    noProjectsYet: 'Aucun projet pour le moment',
    noProjectsEmptyCreate: "Créez votre premier projet pour suivre les tâches et l'avancement de l'équipe.",
    noProjectsEmptyInvited: 'Lorsque vous serez invité à des projets, ils apparaîtront ici.',
    createProjectCta: '+ Créer un projet',
    progress: 'Progression',
    taskSingular: 'tâche',
    taskPlural: 'tâches',
    openProject: 'Ouvrir le projet',
    newProjectServerIdError: "Impossible de lire l'identifiant du nouveau projet depuis le serveur.",
    createdDateRange: 'Plage de date de création',
    moveFailed: 'Déplacement impossible',
    couldNotCreateTask: 'Impossible de créer la tâche.',
    renameColumnPrompt: 'Renommer la colonne',
    newColumnNamePrompt: 'Nom de la nouvelle colonne',
    clearAllFilters: 'Effacer tous les filtres',
    saveFilterViewPrompt: 'Enregistrer la vue de filtre sous',
    saveFilterAsView: 'Enregistrer le filtre comme vue',
    newTimelineTask: 'Nouvelle tâche de chronologie',
    selected: 'sélectionné(s)',
    assignTo: 'Assigner à',
    changeStatus: 'Changer le statut',
    delete: 'Supprimer',
    moveToProject: 'Déplacer vers un projet',
    sort: 'Tri',
    taskNumber: 'N° tâche',
    taskTitle: 'Titre de la tâche',
    dueDate: "Date d'échéance",
    subtasks: 'Sous-tâches',
    dependencySingular: 'dépendance',
    dependencyPlural: 'dépendances',
    subtaskFor: 'Sous-tâche',
    for: 'pour',
    ungrouped: 'Non groupé',
    priorityLow: 'Faible',
    priorityMedium: 'Moyenne',
    priorityHigh: 'Élevée',
    columnBacklog: 'Backlog',
    columnToDo: 'À faire',
    columnInProgress: 'En cours',
    columnInReview: 'En révision',
    columnDone: 'Terminé',
  },
  zh: {
    coretaskLogoAlt: 'CoreTask 标志',
    loginHeroBadge: 'CoreTask 工作区',
    loginHeroTitle: '欢迎回来',
    loginHeroBody: '登录以管理项目、与团队协作并跟踪交付进度。',
    loginHeroBulletDashboard: '统一的项目仪表板',
    loginHeroBulletMessages: '频道讨论和直接消息',
    loginHeroBulletPlanning: '看板、列表和时间线规划',
    loginFeatureDashboardDesc: '全面了解您的项目和团队进度。',
    loginFeatureMessagesDesc: '与团队实时保持联系。',
    loginFeaturePlanningDesc: '按您的方式规划、组织和跟踪任务。',
    forgotPassword: '忘记密码？',
    emailPlaceholder: 'name@company.com',
    passwordPlaceholder: '输入你的密码',
    dashboardStatTotalProjects: '项目总数',
    dashboardStatOpenTasks: '开放任务',
    dashboardStatCompletedTasks: '已完成任务',
    dashboardStatTeamMembers: '团队成员',
    dashboardThisMonth: '本月',
    dashboardNoNewProjectsThisMonth: '本月没有新项目',
    dashboardDueNext7Days: '未来 7 天到期',
    dashboardNothingDueThisWeek: '本周没有到期事项',
    dashboardTodayUtc: '今天 (UTC)',
    dashboardNoCompletionsToday: '今天没有完成项',
    dashboardOnline: '在线',
    dashboardNoneMarkedOnline: '没有成员标记为在线',
    dashboardInviteFromTeam: '从团队页面邀请',
    dashboardLoading: '正在加载仪表板…',
    dashboardProjectProgress: '项目进度',
    viewAll: '查看全部',
    dashboardNoProjectsYetCreate: '还没有项目。请从项目页面创建一个。',
    dashboardUpcomingTasks: '即将到期的任务',
    dashboardNoDatedOpenTasks: '没有带日期的开放任务。请从看板添加截止日期。',
    dashboardWeeklyThroughput: '每周吞吐量',
    dashboardTasksCreatedNoCompletionsYet: '已创建任务（尚无完成项）',
    dashboardChartDay: '第',
    dashboardRecentActivity: '最近活动',
    today: '今天',
    tomorrow: '明天',
    yesterday: '昨天',
    projectDefaultDescription: '新创建的项目已准备好进行任务和协作。',
    projectName: '项目名称',
    description: '描述',
    colorPicker: '颜色选择器',
    membersMultiSelect: '成员（多选）',
    startDate: '开始日期',
    endDate: '结束日期',
    loadingProjectBoard: '正在加载项目看板…',
    backToProjects: '返回项目',
    projectsBreadcrumb: '项目',
    projectBoard: '项目看板',
    viewBoard: '看板',
    viewList: '列表',
    viewChannel: '频道',
    viewTimeline: '时间线',
    categoriesAndLabels: '分类和标签',
    allAssignees: '所有负责人',
    allPriorities: '所有优先级',
    searchBoardTasks: '搜索看板任务...',
    advancedFilters: '高级筛选',
    assignee: '负责人',
    priority: '优先级',
    status: '状态',
    category: '分类',
    label: '标签',
    selectAssignee: '选择负责人',
    selectPriority: '选择优先级',
    selectStatus: '选择状态',
    selectCategory: '选择分类',
    selectLabel: '选择标签',
    dueDateRange: '截止日期范围',
    dueDateRangePlaceholder: '例如：5月10日 - 5月20日',
    zoom: '缩放',
    day: '日',
    week: '周',
    month: '月',
    groupBy: '分组依据',
    task: '任务',
    actions: '操作',
    edit: '编辑',
    comment: '评论',
    assign: '分配',
    noTasksInColumn: '此列没有任务',
    addTaskWorkflowStage: '添加任务以开始此工作流阶段。',
    noTasksInStageYet: '此阶段还没有任务。',
    media: '媒体',
    unassigned: '未分配',
    simulateDrag: '模拟拖动',
    moveLeft: '左移',
    moveRight: '右移',
    quickAddTask: '快速添加任务...',
    projectsDashboardSubtitle: '在一个视图中监控项目进度、状态和团队协作。',
    statActiveTasks: '活跃任务',
    statCompleted: '已完成',
    filterAll: '全部',
    statusActive: '活跃',
    statusCompleted: '已完成',
    statusOnHold: '暂停',
    noProjectsYet: '还没有项目',
    noProjectsEmptyCreate: '创建第一个项目，开始跟踪任务和团队进度。',
    noProjectsEmptyInvited: '当你被邀请加入项目时，它们会显示在这里。',
    createProjectCta: '+ 创建项目',
    progress: '进度',
    taskSingular: '个任务',
    taskPlural: '个任务',
    openProject: '打开项目',
    newProjectServerIdError: '无法从服务器读取新项目 ID。',
    createdDateRange: '创建日期范围',
    moveFailed: '移动失败',
    couldNotCreateTask: '无法创建任务。',
    renameColumnPrompt: '重命名列',
    newColumnNamePrompt: '新列名称',
    clearAllFilters: '清除所有筛选',
    saveFilterViewPrompt: '将筛选视图保存为',
    saveFilterAsView: '将筛选保存为视图',
    newTimelineTask: '新时间线任务',
    selected: '已选择',
    assignTo: '分配给',
    changeStatus: '更改状态',
    delete: '删除',
    moveToProject: '移动到项目',
    sort: '排序',
    taskNumber: '任务 #',
    taskTitle: '任务标题',
    dueDate: '截止日期',
    subtasks: '子任务',
    dependencySingular: '个依赖',
    dependencyPlural: '个依赖',
    subtaskFor: '子任务',
    for: '用于',
    ungrouped: '未分组',
    priorityLow: '低',
    priorityMedium: '中',
    priorityHigh: '高',
    columnBacklog: '待办积压',
    columnToDo: '待办',
    columnInProgress: '进行中',
    columnInReview: '审核中',
    columnDone: '完成',
  },
  ur: {
    coretaskLogoAlt: 'CoreTask لوگو',
    loginHeroBadge: 'CoreTask ورک اسپیس',
    loginHeroTitle: 'واپسی پر خوش آمدید',
    loginHeroBody: 'پروجیکٹس منظم کرنے، ٹیم کے ساتھ تعاون کرنے، اور ڈیلیوری کی پیش رفت ٹریک کرنے کے لیے سائن ان کریں۔',
    loginHeroBulletDashboard: 'متحد پروجیکٹ ڈیش بورڈ',
    loginHeroBulletMessages: 'چینل گفتگو اور براہ راست پیغامات',
    loginHeroBulletPlanning: 'کانبان، فہرست، اور ٹائم لائن پلاننگ',
    loginFeatureDashboardDesc: 'اپنے پروجیکٹس اور ٹیم کی پیش رفت کا مکمل جائزہ لیں۔',
    loginFeatureMessagesDesc: 'ریئل ٹائم میں اپنی ٹیم سے جڑے رہیں۔',
    loginFeaturePlanningDesc: 'اپنے طریقے سے کام کی منصوبہ بندی، ترتیب اور ٹریکنگ کریں۔',
    forgotPassword: 'پاس ورڈ بھول گئے؟',
    emailPlaceholder: 'name@company.com',
    passwordPlaceholder: 'اپنا پاس ورڈ درج کریں',
    dashboardStatTotalProjects: 'کل پروجیکٹس',
    dashboardStatOpenTasks: 'کھلے ٹاسکس',
    dashboardStatCompletedTasks: 'مکمل ٹاسکس',
    dashboardStatTeamMembers: 'ٹیم ممبرز',
    dashboardThisMonth: 'اس ماہ',
    dashboardNoNewProjectsThisMonth: 'اس ماہ کوئی نیا پروجیکٹ نہیں',
    dashboardDueNext7Days: 'اگلے 7 دن میں واجب',
    dashboardNothingDueThisWeek: 'اس ہفتے کچھ واجب نہیں',
    dashboardTodayUtc: 'آج (UTC)',
    dashboardNoCompletionsToday: 'آج کوئی تکمیل نہیں',
    dashboardOnline: 'آن لائن',
    dashboardNoneMarkedOnline: 'کوئی آن لائن نشان زد نہیں',
    dashboardInviteFromTeam: 'ٹیم سے مدعو کریں',
    dashboardLoading: 'ڈیش بورڈ لوڈ ہو رہا ہے…',
    dashboardProjectProgress: 'پروجیکٹ کی پیش رفت',
    viewAll: 'سب دیکھیں',
    dashboardNoProjectsYetCreate: 'ابھی کوئی پروجیکٹ نہیں۔ پروجیکٹس صفحہ سے ایک بنائیں۔',
    dashboardUpcomingTasks: 'آنے والے ٹاسکس',
    dashboardNoDatedOpenTasks: 'تاریخ والے کھلے ٹاسکس نہیں۔ بورڈ سے due dates شامل کریں۔',
    dashboardWeeklyThroughput: 'ہفتہ وار کارکردگی',
    dashboardTasksCreatedNoCompletionsYet: 'ٹاسکس بنائے گئے (ابھی کوئی مکمل نہیں)',
    dashboardChartDay: 'دن',
    dashboardRecentActivity: 'حالیہ سرگرمی',
    today: 'آج',
    tomorrow: 'کل',
    yesterday: 'گزشتہ روز',
    projectDefaultDescription: 'نیا پروجیکٹ ٹاسکس اور تعاون کے لیے تیار ہے۔',
    projectName: 'پروجیکٹ کا نام',
    description: 'تفصیل',
    colorPicker: 'رنگ منتخب کریں',
    membersMultiSelect: 'ممبرز (متعدد انتخاب)',
    startDate: 'شروع کی تاریخ',
    endDate: 'اختتامی تاریخ',
    loadingProjectBoard: 'پروجیکٹ بورڈ لوڈ ہو رہا ہے…',
    backToProjects: 'پروجیکٹس پر واپس',
    projectsBreadcrumb: 'پروجیکٹس',
    projectBoard: 'پروجیکٹ بورڈ',
    viewBoard: 'بورڈ',
    viewList: 'فہرست',
    viewChannel: 'چینل',
    viewTimeline: 'ٹائم لائن',
    categoriesAndLabels: 'کیٹیگریز اور لیبلز',
    allAssignees: 'تمام ذمہ داران',
    allPriorities: 'تمام ترجیحات',
    searchBoardTasks: 'بورڈ ٹاسکس تلاش کریں...',
    advancedFilters: 'اعلیٰ فلٹرز',
    assignee: 'ذمہ دار',
    priority: 'ترجیح',
    status: 'حالت',
    category: 'کیٹیگری',
    label: 'لیبل',
    selectAssignee: 'ذمہ دار منتخب کریں',
    selectPriority: 'ترجیح منتخب کریں',
    selectStatus: 'حالت منتخب کریں',
    selectCategory: 'کیٹیگری منتخب کریں',
    selectLabel: 'لیبل منتخب کریں',
    dueDateRange: 'Due Date رینج',
    dueDateRangePlaceholder: 'مثلاً 10 مئی - 20 مئی',
    zoom: 'زوم',
    day: 'دن',
    week: 'ہفتہ',
    month: 'مہینہ',
    groupBy: 'گروپ کریں',
    task: 'ٹاسک',
    actions: 'کارروائیاں',
    edit: 'ترمیم',
    comment: 'تبصرہ',
    assign: 'تفویض',
    noTasksInColumn: 'اس کالم میں کوئی ٹاسک نہیں',
    addTaskWorkflowStage: 'اس ورک فلو مرحلے کو شروع کرنے کے لیے ٹاسک شامل کریں۔',
    noTasksInStageYet: 'اس مرحلے میں ابھی کوئی ٹاسک نہیں۔',
    media: 'میڈیا',
    unassigned: 'غیر تفویض شدہ',
    simulateDrag: 'ڈریگ کی نقل',
    moveLeft: 'بائیں منتقل کریں',
    moveRight: 'دائیں منتقل کریں',
    quickAddTask: 'فوری ٹاسک شامل کریں...',
    projectsDashboardSubtitle: 'ایک ہی منظر میں پروجیکٹ کی پیش رفت، حالت، اور ٹیم تعاون دیکھیں۔',
    statActiveTasks: 'فعال ٹاسکس',
    statCompleted: 'مکمل',
    filterAll: 'سب',
    statusActive: 'فعال',
    statusCompleted: 'مکمل',
    statusOnHold: 'روکا ہوا',
    noProjectsYet: 'ابھی کوئی پروجیکٹ نہیں',
    noProjectsEmptyCreate: 'ٹاسکس اور ٹیم کی پیش رفت ٹریک کرنے کے لیے اپنا پہلا پروجیکٹ بنائیں۔',
    noProjectsEmptyInvited: 'جب آپ کو پروجیکٹس میں مدعو کیا جائے گا، وہ یہاں نظر آئیں گے۔',
    createProjectCta: '+ پروجیکٹ بنائیں',
    progress: 'پیش رفت',
    taskSingular: 'ٹاسک',
    taskPlural: 'ٹاسکس',
    openProject: 'پروجیکٹ کھولیں',
    newProjectServerIdError: 'سرور سے نئے پروجیکٹ کا id نہیں پڑھا جا سکا۔',
    createdDateRange: 'بننے کی تاریخ کی رینج',
    moveFailed: 'منتقلی ناکام ہو گئی',
    couldNotCreateTask: 'ٹاسک نہیں بنایا جا سکا۔',
    renameColumnPrompt: 'کالم کا نام تبدیل کریں',
    newColumnNamePrompt: 'نئے کالم کا نام',
    clearAllFilters: 'تمام فلٹرز صاف کریں',
    saveFilterViewPrompt: 'فلٹر ویو اس نام سے محفوظ کریں',
    saveFilterAsView: 'فلٹر کو ویو کے طور پر محفوظ کریں',
    newTimelineTask: 'نیا ٹائم لائن ٹاسک',
    selected: 'منتخب',
    assignTo: 'تفویض کریں',
    changeStatus: 'حالت تبدیل کریں',
    delete: 'حذف کریں',
    moveToProject: 'پروجیکٹ میں منتقل کریں',
    sort: 'ترتیب',
    taskNumber: 'ٹاسک #',
    taskTitle: 'ٹاسک کا عنوان',
    dueDate: 'Due Date',
    subtasks: 'سب ٹاسکس',
    dependencySingular: 'انحصار',
    dependencyPlural: 'انحصارات',
    subtaskFor: 'سب ٹاسک',
    for: 'برائے',
    ungrouped: 'بغیر گروپ',
    priorityLow: 'کم',
    priorityMedium: 'درمیانی',
    priorityHigh: 'زیادہ',
    columnBacklog: 'بیک لاگ',
    columnToDo: 'کرنا ہے',
    columnInProgress: 'جاری',
    columnInReview: 'جائزہ میں',
    columnDone: 'مکمل',
  },
};

Object.entries(stepOneTranslations).forEach(([language, entries]) => {
  translations[language] = { ...(translations[language] || {}), ...entries };
});

const stepTwoTranslations = {
  en: {
    aiTaskerExampleDescription:
      'Build a customer support portal where users can submit tickets, support agents can respond, admins can manage categories, and managers can track SLA performance.',
    aiTaskerDescriptionTooShort: 'Please describe the project with a little more detail.',
    aiTaskerCreatedToast: 'AI Tasker created your project and tasks.',
    aiTaskerFailed: 'AI Tasker failed to create the project.',
    aiTaskerRetrying: 'Retry',
    aiTaskerHeroTitle: 'Describe your project. Let AI create the work plan.',
    aiTaskerHeroBody:
      'Gemini turns your idea into a CoreTask project, creates detailed tasks, and assigns them to accepted team members in your workspace.',
    aiTaskerApiKeyHint: 'Keep your Gemini API key on the API server. Never expose it in React.',
    aiTaskerProjectDescriptionTitle: 'Project Description',
    aiTaskerProjectDescriptionHelp: 'Explain the product, users, goals, and any required features.',
    aiTaskerTaskCount: 'Number of tasks',
    useExample: 'Use example',
    creating: 'Creating...',
    createWithAI: 'Create with AI',
    howItWorks: 'How it works',
    aiTaskerStepOne: '1. Gemini reads your project description and creates a structured task plan.',
    aiTaskerStepTwo: '2. The API creates one CoreTask project and a default board.',
    aiTaskerStepThree: '3. Tasks are added to To Do and assigned to accepted team members when Gemini selects a match.',
    aiTaskerInviteHint: 'Invited members can only receive task assignments after they accept the invitation and have a user account.',
    projectCreated: 'Project created',
    openProjects: 'Open Projects',
    assignedTo: 'Assigned to',
    aiTaskerAvailableUsersPrefix: 'AI Tasker could assign tasks across',
    aiTaskerAvailableUsersSingular: 'available workspace user.',
    aiTaskerAvailableUsersPlural: 'available workspace users.',
    aiAssistanceStarterOne: 'How should I split a new ecommerce app into tasks?',
    aiAssistanceStarterTwo: 'Write a good task description for implementing login with JWT.',
    aiAssistanceStarterThree: 'Give me a checklist for reviewing project progress with my team.',
    aiAssistanceWelcomeMessage:
      'Hi, I am CoreTask AI Assistance. Ask me about planning projects, writing task descriptions, team workflows, or how to use CoreTask.',
    aiAssistanceEmptyAnswer: 'I could not generate a useful answer. Try asking in a different way.',
    aiAssistanceFailed: 'AI Assistance failed to respond.',
    aiAssistanceHeroTitle: 'Ask for help with planning, tasks, and teamwork.',
    aiAssistanceHeroBody:
      'Use Gemini as a CoreTask assistant for task descriptions, project planning, meeting checklists, and workflow ideas. It answers questions only; use AI Tasker when you want to create a project automatically.',
    aiAssistanceApiKeyHint: 'Your Gemini API key stays on the ASP.NET API server.',
    coretaskAssistant: 'CoreTask Assistant',
    aiAssistanceCardHelp: 'Ask a question and get a practical answer.',
    thinking: 'Thinking...',
    askAiAssistance: 'Ask AI Assistance...',
    tryAsking: 'Try asking',
    aiAssistanceReadonlyHint:
      'AI Assistance can suggest plans and wording, but it does not create or update records. Use AI Tasker for project creation.',
    contextUser: 'User',
    contextAssistant: 'Assistant',
  },
  ar: {
    aiTaskerExampleDescription:
      'ابنِ بوابة دعم عملاء حيث يمكن للمستخدمين إرسال التذاكر، ويرد مسؤولو الدعم، ويدير المشرفون الفئات، ويتابع المديرون أداء اتفاقيات الخدمة.',
    aiTaskerDescriptionTooShort: 'يرجى وصف المشروع بتفاصيل أكثر قليلًا.',
    aiTaskerCreatedToast: 'أنشأ AI Tasker المشروع والمهام.',
    aiTaskerFailed: 'فشل AI Tasker في إنشاء المشروع.',
    aiTaskerRetrying: 'إعادة المحاولة',
    aiTaskerHeroTitle: 'صف مشروعك ودع الذكاء الاصطناعي ينشئ خطة العمل.',
    aiTaskerHeroBody:
      'يحوّل Gemini فكرتك إلى مشروع في CoreTask، وينشئ مهامًا مفصلة، ويوزعها على أعضاء الفريق الذين قبلوا الدعوة في مساحة العمل.',
    aiTaskerApiKeyHint: 'احتفظ بمفتاح Gemini API على خادم الـ API. لا تعرضه أبدًا داخل React.',
    aiTaskerProjectDescriptionTitle: 'وصف المشروع',
    aiTaskerProjectDescriptionHelp: 'اشرح المنتج والمستخدمين والأهداف وأي مزايا مطلوبة.',
    aiTaskerTaskCount: 'عدد المهام',
    useExample: 'استخدم المثال',
    creating: 'جاري الإنشاء...',
    createWithAI: 'إنشاء بالذكاء الاصطناعي',
    howItWorks: 'كيف يعمل',
    aiTaskerStepOne: '1. يقرأ Gemini وصف المشروع وينشئ خطة مهام منظمة.',
    aiTaskerStepTwo: '2. ينشئ الـ API مشروع CoreTask واحدًا ولوحة افتراضية.',
    aiTaskerStepThree: '3. تُضاف المهام إلى To Do وتُسند للأعضاء المقبولين عندما يختار Gemini تطابقًا مناسبًا.',
    aiTaskerInviteHint: 'لا يمكن إسناد المهام للأعضاء المدعوين إلا بعد قبول الدعوة وامتلاك حساب مستخدم.',
    projectCreated: 'تم إنشاء المشروع',
    openProjects: 'فتح المشاريع',
    assignedTo: 'مسند إلى',
    aiTaskerAvailableUsersPrefix: 'استطاع AI Tasker توزيع المهام على',
    aiTaskerAvailableUsersSingular: 'مستخدم متاح في مساحة العمل.',
    aiTaskerAvailableUsersPlural: 'مستخدمين متاحين في مساحة العمل.',
    aiAssistanceStarterOne: 'كيف أقسم تطبيق تجارة إلكترونية جديد إلى مهام؟',
    aiAssistanceStarterTwo: 'اكتب وصف مهمة جيد لتنفيذ تسجيل الدخول باستخدام JWT.',
    aiAssistanceStarterThree: 'أعطني قائمة مراجعة لمتابعة تقدم المشروع مع فريقي.',
    aiAssistanceWelcomeMessage:
      'مرحبًا، أنا مساعد CoreTask الذكي. اسألني عن تخطيط المشاريع، وكتابة أوصاف المهام، وسير عمل الفريق، أو كيفية استخدام CoreTask.',
    aiAssistanceEmptyAnswer: 'لم أتمكن من إنشاء إجابة مفيدة. جرّب صياغة السؤال بطريقة مختلفة.',
    aiAssistanceFailed: 'فشل AI Assistance في الرد.',
    aiAssistanceHeroTitle: 'اطلب المساعدة في التخطيط والمهام والعمل الجماعي.',
    aiAssistanceHeroBody:
      'استخدم Gemini كمساعد داخل CoreTask لأوصاف المهام، وتخطيط المشاريع، وقوائم الاجتماعات، وأفكار سير العمل. يجيب على الأسئلة فقط؛ استخدم AI Tasker عندما تريد إنشاء مشروع تلقائيًا.',
    aiAssistanceApiKeyHint: 'يبقى مفتاح Gemini API على خادم ASP.NET API.',
    coretaskAssistant: 'مساعد CoreTask',
    aiAssistanceCardHelp: 'اطرح سؤالًا واحصل على إجابة عملية.',
    thinking: 'جاري التفكير...',
    askAiAssistance: 'اسأل AI Assistance...',
    tryAsking: 'جرّب أن تسأل',
    aiAssistanceReadonlyHint:
      'يمكن لـ AI Assistance اقتراح الخطط والصياغة، لكنه لا ينشئ أو يحدّث السجلات. استخدم AI Tasker لإنشاء المشاريع.',
    contextUser: 'المستخدم',
    contextAssistant: 'المساعد',
  },
  fr: {
    aiTaskerExampleDescription:
      'Créer un portail de support client où les utilisateurs soumettent des tickets, les agents répondent, les admins gèrent les catégories et les managers suivent les SLA.',
    aiTaskerDescriptionTooShort: 'Veuillez décrire le projet avec un peu plus de détails.',
    aiTaskerCreatedToast: 'AI Tasker a créé votre projet et vos tâches.',
    aiTaskerFailed: "AI Tasker n'a pas pu créer le projet.",
    aiTaskerRetrying: 'Nouvelle tentative',
    aiTaskerHeroTitle: "Décrivez votre projet. L'IA crée le plan de travail.",
    aiTaskerHeroBody:
      'Gemini transforme votre idée en projet CoreTask, crée des tâches détaillées et les assigne aux membres acceptés dans votre espace de travail.',
    aiTaskerApiKeyHint: "Gardez votre clé Gemini API sur le serveur API. Ne l'exposez jamais dans React.",
    aiTaskerProjectDescriptionTitle: 'Description du projet',
    aiTaskerProjectDescriptionHelp: 'Expliquez le produit, les utilisateurs, les objectifs et les fonctionnalités requises.',
    aiTaskerTaskCount: 'Nombre de tâches',
    useExample: "Utiliser l'exemple",
    creating: 'Création...',
    createWithAI: "Créer avec l'IA",
    howItWorks: 'Fonctionnement',
    aiTaskerStepOne: '1. Gemini lit la description du projet et crée un plan de tâches structuré.',
    aiTaskerStepTwo: "2. L'API crée un projet CoreTask et un tableau par défaut.",
    aiTaskerStepThree: '3. Les tâches sont ajoutées à À faire et assignées aux membres acceptés quand Gemini trouve un bon profil.',
    aiTaskerInviteHint: "Les membres invités ne peuvent recevoir des tâches qu'après avoir accepté l'invitation et créé un compte.",
    projectCreated: 'Projet créé',
    openProjects: 'Ouvrir les projets',
    assignedTo: 'Assigné à',
    aiTaskerAvailableUsersPrefix: 'AI Tasker pouvait assigner les tâches à',
    aiTaskerAvailableUsersSingular: "utilisateur disponible dans l'espace de travail.",
    aiTaskerAvailableUsersPlural: "utilisateurs disponibles dans l'espace de travail.",
    aiAssistanceStarterOne: 'Comment découper une nouvelle application e-commerce en tâches ?',
    aiAssistanceStarterTwo: 'Rédige une bonne description de tâche pour implémenter une connexion avec JWT.',
    aiAssistanceStarterThree: "Donne-moi une checklist pour suivre l'avancement du projet avec mon équipe.",
    aiAssistanceWelcomeMessage:
      "Bonjour, je suis CoreTask AI Assistance. Posez-moi des questions sur la planification, les descriptions de tâches, les workflows d'équipe ou l'utilisation de CoreTask.",
    aiAssistanceEmptyAnswer: "Je n'ai pas pu générer de réponse utile. Essayez de reformuler votre question.",
    aiAssistanceFailed: "AI Assistance n'a pas pu répondre.",
    aiAssistanceHeroTitle: "Demandez de l'aide pour la planification, les tâches et le travail d'équipe.",
    aiAssistanceHeroBody:
      'Utilisez Gemini comme assistant CoreTask pour les descriptions de tâches, la planification, les checklists de réunion et les idées de workflow. Il répond seulement aux questions ; utilisez AI Tasker pour créer un projet automatiquement.',
    aiAssistanceApiKeyHint: 'Votre clé Gemini API reste sur le serveur ASP.NET API.',
    coretaskAssistant: 'Assistant CoreTask',
    aiAssistanceCardHelp: 'Posez une question et obtenez une réponse pratique.',
    thinking: 'Réflexion...',
    askAiAssistance: 'Demander à AI Assistance...',
    tryAsking: 'Essayez de demander',
    aiAssistanceReadonlyHint:
      'AI Assistance peut suggérer des plans et des formulations, mais ne crée ni ne modifie de données. Utilisez AI Tasker pour créer des projets.',
    contextUser: 'Utilisateur',
    contextAssistant: 'Assistant',
  },
  zh: {
    aiTaskerExampleDescription:
      '构建一个客户支持门户，用户可以提交工单，支持人员可以回复，管理员可以管理分类，经理可以跟踪 SLA 表现。',
    aiTaskerDescriptionTooShort: '请更详细地描述项目。',
    aiTaskerCreatedToast: 'AI Tasker 已创建项目和任务。',
    aiTaskerFailed: 'AI Tasker 创建项目失败。',
    aiTaskerRetrying: '重试',
    aiTaskerHeroTitle: '描述你的项目，让 AI 创建工作计划。',
    aiTaskerHeroBody:
      'Gemini 会把你的想法转换为 CoreTask 项目，创建详细任务，并分配给工作区中已接受邀请的团队成员。',
    aiTaskerApiKeyHint: '请把 Gemini API 密钥保存在 API 服务器上。不要在 React 中暴露。',
    aiTaskerProjectDescriptionTitle: '项目描述',
    aiTaskerProjectDescriptionHelp: '说明产品、用户、目标以及任何必需功能。',
    aiTaskerTaskCount: '任务数量',
    useExample: '使用示例',
    creating: '正在创建...',
    createWithAI: '用 AI 创建',
    howItWorks: '工作方式',
    aiTaskerStepOne: '1. Gemini 读取项目描述并创建结构化任务计划。',
    aiTaskerStepTwo: '2. API 创建一个 CoreTask 项目和默认看板。',
    aiTaskerStepThree: '3. 任务会添加到待办，并在 Gemini 选择匹配成员时分配给已接受邀请的成员。',
    aiTaskerInviteHint: '被邀请的成员只有在接受邀请并拥有用户账户后才能收到任务分配。',
    projectCreated: '项目已创建',
    openProjects: '打开项目',
    assignedTo: '分配给',
    aiTaskerAvailableUsersPrefix: 'AI Tasker 可在以下数量的工作区用户中分配任务：',
    aiTaskerAvailableUsersSingular: '个可用用户。',
    aiTaskerAvailableUsersPlural: '个可用用户。',
    aiAssistanceStarterOne: '我应该如何把新的电商应用拆分成任务？',
    aiAssistanceStarterTwo: '为实现 JWT 登录写一个好的任务描述。',
    aiAssistanceStarterThree: '给我一份与团队检查项目进度的清单。',
    aiAssistanceWelcomeMessage:
      '你好，我是 CoreTask AI Assistance。你可以问我项目规划、任务描述、团队流程或如何使用 CoreTask。',
    aiAssistanceEmptyAnswer: '我无法生成有用答案。请换一种方式提问。',
    aiAssistanceFailed: 'AI Assistance 回复失败。',
    aiAssistanceHeroTitle: '获取规划、任务和团队协作方面的帮助。',
    aiAssistanceHeroBody:
      '使用 Gemini 作为 CoreTask 助手，帮助编写任务描述、项目计划、会议清单和工作流想法。它只回答问题；如果要自动创建项目，请使用 AI Tasker。',
    aiAssistanceApiKeyHint: '你的 Gemini API 密钥保存在 ASP.NET API 服务器上。',
    coretaskAssistant: 'CoreTask 助手',
    aiAssistanceCardHelp: '提出问题并获得实用回答。',
    thinking: '正在思考...',
    askAiAssistance: '询问 AI Assistance...',
    tryAsking: '试着提问',
    aiAssistanceReadonlyHint:
      'AI Assistance 可以建议计划和措辞，但不会创建或更新记录。请使用 AI Tasker 创建项目。',
    contextUser: '用户',
    contextAssistant: '助手',
  },
  ur: {
    aiTaskerExampleDescription:
      'ایک کسٹمر سپورٹ پورٹل بنائیں جہاں صارفین ٹکٹ جمع کر سکیں، سپورٹ ایجنٹس جواب دیں، ایڈمنز کیٹیگریز سنبھالیں، اور مینیجرز SLA کارکردگی ٹریک کریں۔',
    aiTaskerDescriptionTooShort: 'براہ کرم پروجیکٹ کو تھوڑی مزید تفصیل سے بیان کریں۔',
    aiTaskerCreatedToast: 'AI Tasker نے آپ کا پروجیکٹ اور ٹاسکس بنا دیے۔',
    aiTaskerFailed: 'AI Tasker پروجیکٹ بنانے میں ناکام رہا۔',
    aiTaskerRetrying: 'دوبارہ کوشش',
    aiTaskerHeroTitle: 'اپنا پروجیکٹ بیان کریں۔ AI ورک پلان بنائے گا۔',
    aiTaskerHeroBody:
      'Gemini آپ کے آئیڈیا کو CoreTask پروجیکٹ میں بدلتا ہے، تفصیلی ٹاسکس بناتا ہے، اور انہیں ورک اسپیس کے قبول شدہ ممبرز کو تفویض کرتا ہے۔',
    aiTaskerApiKeyHint: 'اپنی Gemini API key کو API سرور پر رکھیں۔ اسے React میں کبھی ظاہر نہ کریں۔',
    aiTaskerProjectDescriptionTitle: 'پروجیکٹ کی تفصیل',
    aiTaskerProjectDescriptionHelp: 'پروڈکٹ، صارفین، اہداف، اور مطلوبہ فیچرز بیان کریں۔',
    aiTaskerTaskCount: 'ٹاسکس کی تعداد',
    useExample: 'مثال استعمال کریں',
    creating: 'بنایا جا رہا ہے...',
    createWithAI: 'AI سے بنائیں',
    howItWorks: 'یہ کیسے کام کرتا ہے',
    aiTaskerStepOne: '1. Gemini پروجیکٹ کی تفصیل پڑھتا ہے اور structured task plan بناتا ہے۔',
    aiTaskerStepTwo: '2. API ایک CoreTask پروجیکٹ اور default board بناتا ہے۔',
    aiTaskerStepThree: '3. ٹاسکس To Do میں شامل ہوتے ہیں اور مناسب match پر accepted ممبرز کو assigned ہوتے ہیں۔',
    aiTaskerInviteHint: 'مدعو ممبرز کو ٹاسکس صرف دعوت قبول کرنے اور صارف اکاؤنٹ رکھنے کے بعد assigned ہو سکتے ہیں۔',
    projectCreated: 'پروجیکٹ بن گیا',
    openProjects: 'پروجیکٹس کھولیں',
    assignedTo: 'تفویض شدہ',
    aiTaskerAvailableUsersPrefix: 'AI Tasker نے ٹاسکس تفویض کرنے کے لیے استعمال کیا',
    aiTaskerAvailableUsersSingular: 'دستیاب ورک اسپیس صارف۔',
    aiTaskerAvailableUsersPlural: 'دستیاب ورک اسپیس صارفین۔',
    aiAssistanceStarterOne: 'نئی ecommerce app کو ٹاسکس میں کیسے تقسیم کروں؟',
    aiAssistanceStarterTwo: 'JWT login implement کرنے کے لیے اچھی task description لکھیں۔',
    aiAssistanceStarterThree: 'ٹیم کے ساتھ project progress review کرنے کے لیے checklist دیں۔',
    aiAssistanceWelcomeMessage:
      'ہیلو، میں CoreTask AI Assistance ہوں۔ project planning، task descriptions، team workflows، یا CoreTask استعمال کرنے کے بارے میں پوچھیں۔',
    aiAssistanceEmptyAnswer: 'میں مفید جواب نہیں بنا سکا۔ سوال کو مختلف انداز میں پوچھیں۔',
    aiAssistanceFailed: 'AI Assistance جواب دینے میں ناکام رہا۔',
    aiAssistanceHeroTitle: 'planning، tasks، اور teamwork میں مدد حاصل کریں۔',
    aiAssistanceHeroBody:
      'Gemini کو CoreTask assistant کے طور پر task descriptions، project planning، meeting checklists، اور workflow ideas کے لیے استعمال کریں۔ یہ صرف سوالات کے جواب دیتا ہے؛ project خودکار بنانے کے لیے AI Tasker استعمال کریں۔',
    aiAssistanceApiKeyHint: 'آپ کی Gemini API key ASP.NET API server پر رہتی ہے۔',
    coretaskAssistant: 'CoreTask Assistant',
    aiAssistanceCardHelp: 'سوال پوچھیں اور عملی جواب حاصل کریں۔',
    thinking: 'سوچا جا رہا ہے...',
    askAiAssistance: 'AI Assistance سے پوچھیں...',
    tryAsking: 'یہ پوچھ کر دیکھیں',
    aiAssistanceReadonlyHint:
      'AI Assistance plans اور wording تجویز کر سکتا ہے، مگر records create یا update نہیں کرتا۔ project creation کے لیے AI Tasker استعمال کریں۔',
    contextUser: 'صارف',
    contextAssistant: 'Assistant',
  },
};

Object.entries(stepTwoTranslations).forEach(([language, entries]) => {
  translations[language] = { ...(translations[language] || {}), ...entries };
});

const stepThreeTranslations = {
  en: {
    teamManageSubtitle: 'Manage people, roles, and assignments across all active projects.',
    teamReadonlySubtitle: 'People on the workspaces you belong to.',
    online: 'Online',
    offline: 'Offline',
    byRole: 'By Role',
    searchMembers: 'Search members...',
    noMembersFound: 'No members found',
    teamEmptyFixIssue: 'Fix the issue above then refresh.',
    teamEmptyInvitePeople: 'Invite people to appear on your workspace roster.',
    teamEmptyNoSearch: 'No roster entries matched your search.',
    changeRole: 'Change Role',
    removeMember: 'Remove member',
    viewProfile: 'View Profile',
    activeProjects: 'Active Projects',
    tasks: 'Tasks',
    fullName: 'Full name',
    role: 'Role',
    projectAccess: 'Project access',
    createProjectFirstAccess: 'Create a project first to assign access.',
    saveMember: 'Save member',
    joined: 'Joined',
    assignedTasks: 'Assigned Tasks',
    noTasksAssignedYet: 'No tasks assigned yet.',
    activityLast5Buckets: 'Activity (last 5 buckets)',
    removeFromRoster: 'Remove from roster…',
    updateFailed: 'Update failed',
    couldNotUpdateRole: 'Could not update role.',
    couldNotDeleteMember: 'Could not delete member.',
    couldNotDelete: 'Could not delete.',
    inviteFullNameEmailRequired: 'Enter full name and email.',
    inviteFailed: 'Invite failed.',
    removeMemberConfirmPrefix: 'Remove',
    removeMemberConfirmSuffix: 'from your team roster?',
    roleAdmin: 'Admin',
    roleManager: 'Manager',
    roleDeveloper: 'Developer',
    roleDesigner: 'Designer',
    roleViewer: 'Viewer',
    filterUnread: 'Unread',
    filterMentions: 'Mentions',
    filterAssignments: 'Assignments',
    noNotificationsNow: 'No notifications right now.',
    notificationTaskAssigned: 'Task Assigned',
    notificationCommentMentioned: 'Comment Mentioned',
    notificationTaskStatusChanged: 'Task Status Changed',
    notificationDueDateApproaching: 'Due Date Approaching',
    notificationNewProjectMember: 'New Project Member',
    notificationMeetingLinkShared: 'Meeting Link Shared',
    notificationNewPostInChannel: 'New Post in Channel',
    thisWeek: 'This Week',
    direct: 'Direct',
    groups: 'Groups',
    newMessage: '+ New Message',
    newGroup: '+ New Group',
    searchConversations: 'Search conversations...',
    loadingConversations: 'Loading conversations…',
    noConversationsYet: 'No conversations yet.',
    members: 'members',
    loadingMessages: 'Loading messages…',
    meetingLinkShared: 'Meeting Link Shared',
    read: 'Read',
    sent: 'Sent',
    react: 'React',
    reply: 'Reply',
    replyTo: 'Reply to',
    selectConversation: 'Select a conversation to start messaging.',
    meetingLinkPlaceholder: 'https://zoom.us/j/...',
    share: 'Share',
    statusOpen: 'Open',
    channelNamePrompt: 'Channel name',
    newProjectChannel: 'New project channel',
    channels: 'Channels',
    add: '+ Add',
    searchInChannel: 'Search in channel...',
    noPostsInChannel: 'No posts in this channel yet.',
    reaction: '+ Reaction',
    newPost: 'New Post',
    postComposer: 'Post Composer',
    postTitleOptional: 'Title (optional)',
    writeYourPost: 'Write your post...',
    replyToThread: 'Reply to thread...',
    globalSearchPlaceholder: 'Search tasks, projects, members... (Ctrl+K)',
    recentSearches: 'Recent Searches',
    noMatches: 'No matches',
    onboardingTitle: 'Welcome to CoreTask',
    onboardingStep: 'Step',
    onboardingSystemTitle: 'Advanced Task Management System',
    onboardingSystemSubtitle: 'Plan projects, collaborate in channels, and track execution in one place.',
    onboardingCreateFirstProject: 'Create your first project',
    onboardingInviteTeamMembers: 'Invite team members',
    back: 'Back',
    next: 'Next',
    onboardingDone: "You're all set",
    profileFallbackDescription: 'Update your user profile, role, and communication preferences.',
    post: 'Post',
    teamMember: 'Team Member',
    channelPosts: 'Channel Posts',
    taskComment: 'Task comment',
    meetingLinkBody: 'Meeting link',
    addPeopleForDm: 'Add people on the Team page to start a direct message.',
    start: 'Start',
    groupName: 'Group name',
    groupAvatarInitials: 'Group avatar initials',
    createGroup: 'Create Group',
    loadingAttachment: 'Loading attachment…',
    viewImageFullScreen: 'View image full screen',
    fullScreenVideo: 'Full screen video',
    download: 'Download',
    messageFallback: 'Message',
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
    file: 'File',
    meeting: 'Meeting',
  },
  ar: {
    teamManageSubtitle: 'إدارة الأشخاص والأدوار والتعيينات عبر كل المشاريع النشطة.',
    teamReadonlySubtitle: 'الأشخاص في مساحات العمل التي تنتمي إليها.',
    online: 'متصل',
    offline: 'غير متصل',
    byRole: 'حسب الدور',
    searchMembers: 'ابحث عن الأعضاء...',
    noMembersFound: 'لم يتم العثور على أعضاء',
    teamEmptyFixIssue: 'أصلح المشكلة أعلاه ثم حدّث الصفحة.',
    teamEmptyInvitePeople: 'ادعُ أشخاصًا ليظهروا في قائمة مساحة العمل.',
    teamEmptyNoSearch: 'لا توجد نتائج مطابقة في القائمة.',
    changeRole: 'تغيير الدور',
    removeMember: 'إزالة العضو',
    viewProfile: 'عرض الملف',
    activeProjects: 'المشاريع النشطة',
    tasks: 'المهام',
    fullName: 'الاسم الكامل',
    role: 'الدور',
    projectAccess: 'صلاحية المشروع',
    createProjectFirstAccess: 'أنشئ مشروعًا أولًا لتعيين الصلاحية.',
    saveMember: 'حفظ العضو',
    joined: 'انضم في',
    assignedTasks: 'المهام المسندة',
    noTasksAssignedYet: 'لا توجد مهام مسندة بعد.',
    activityLast5Buckets: 'النشاط (آخر 5 فترات)',
    removeFromRoster: 'إزالة من القائمة…',
    updateFailed: 'فشل التحديث',
    couldNotUpdateRole: 'تعذر تحديث الدور.',
    couldNotDeleteMember: 'تعذر حذف العضو.',
    couldNotDelete: 'تعذر الحذف.',
    inviteFullNameEmailRequired: 'أدخل الاسم الكامل والبريد الإلكتروني.',
    inviteFailed: 'فشلت الدعوة.',
    removeMemberConfirmPrefix: 'إزالة',
    removeMemberConfirmSuffix: 'من قائمة فريقك؟',
    roleAdmin: 'مدير النظام',
    roleManager: 'مدير',
    roleDeveloper: 'مطوّر',
    roleDesigner: 'مصمم',
    roleViewer: 'مشاهد',
    filterUnread: 'غير مقروء',
    filterMentions: 'الإشارات',
    filterAssignments: 'التعيينات',
    noNotificationsNow: 'لا توجد إشعارات الآن.',
    notificationTaskAssigned: 'تم تعيين مهمة',
    notificationCommentMentioned: 'تم ذكرك في تعليق',
    notificationTaskStatusChanged: 'تغيرت حالة المهمة',
    notificationDueDateApproaching: 'اقترب تاريخ الاستحقاق',
    notificationNewProjectMember: 'عضو مشروع جديد',
    notificationMeetingLinkShared: 'تمت مشاركة رابط اجتماع',
    notificationNewPostInChannel: 'منشور جديد في القناة',
    thisWeek: 'هذا الأسبوع',
    direct: 'مباشر',
    groups: 'المجموعات',
    newMessage: '+ رسالة جديدة',
    newGroup: '+ مجموعة جديدة',
    searchConversations: 'ابحث في المحادثات...',
    loadingConversations: 'جاري تحميل المحادثات…',
    noConversationsYet: 'لا توجد محادثات بعد.',
    members: 'أعضاء',
    loadingMessages: 'جاري تحميل الرسائل…',
    meetingLinkShared: 'تمت مشاركة رابط اجتماع',
    read: 'مقروء',
    sent: 'مرسل',
    react: 'تفاعل',
    reply: 'رد',
    replyTo: 'رد على',
    selectConversation: 'اختر محادثة لبدء المراسلة.',
    meetingLinkPlaceholder: 'https://zoom.us/j/...',
    share: 'مشاركة',
    statusOpen: 'مفتوحة',
    channelNamePrompt: 'اسم القناة',
    newProjectChannel: 'قناة مشروع جديدة',
    channels: 'القنوات',
    add: '+ إضافة',
    searchInChannel: 'ابحث في القناة...',
    noPostsInChannel: 'لا توجد منشورات في هذه القناة بعد.',
    reaction: '+ تفاعل',
    newPost: 'منشور جديد',
    postComposer: 'محرر المنشور',
    postTitleOptional: 'العنوان (اختياري)',
    writeYourPost: 'اكتب منشورك...',
    replyToThread: 'رد على السلسلة...',
    globalSearchPlaceholder: 'ابحث عن المهام والمشاريع والأعضاء... (Ctrl+K)',
    recentSearches: 'عمليات البحث الأخيرة',
    noMatches: 'لا توجد نتائج',
    onboardingTitle: 'مرحبًا بك في CoreTask',
    onboardingStep: 'الخطوة',
    onboardingSystemTitle: 'نظام متقدم لإدارة المهام',
    onboardingSystemSubtitle: 'خطط للمشاريع، تعاون في القنوات، وتابع التنفيذ في مكان واحد.',
    onboardingCreateFirstProject: 'أنشئ مشروعك الأول',
    onboardingInviteTeamMembers: 'ادعُ أعضاء الفريق',
    back: 'رجوع',
    next: 'التالي',
    onboardingDone: 'أنت جاهز',
    profileFallbackDescription: 'حدّث ملفك الشخصي ودورك وتفضيلات التواصل.',
    post: 'نشر',
    teamMember: 'عضو الفريق',
    channelPosts: 'منشورات القناة',
    taskComment: 'تعليق مهمة',
    meetingLinkBody: 'رابط الاجتماع',
    addPeopleForDm: 'أضف أشخاصًا في صفحة الفريق لبدء رسالة مباشرة.',
    start: 'بدء',
    groupName: 'اسم المجموعة',
    groupAvatarInitials: 'أحرف صورة المجموعة',
    createGroup: 'إنشاء مجموعة',
    loadingAttachment: 'جاري تحميل المرفق…',
    viewImageFullScreen: 'عرض الصورة بملء الشاشة',
    fullScreenVideo: 'فيديو بملء الشاشة',
    download: 'تنزيل',
    messageFallback: 'رسالة',
    image: 'صورة',
    video: 'فيديو',
    audio: 'صوت',
    file: 'ملف',
    meeting: 'اجتماع',
  },
  fr: {
    teamManageSubtitle: 'Gérez les personnes, les rôles et les affectations sur tous les projets actifs.',
    teamReadonlySubtitle: 'Personnes dans les espaces de travail auxquels vous appartenez.',
    online: 'En ligne',
    offline: 'Hors ligne',
    byRole: 'Par rôle',
    searchMembers: 'Rechercher des membres...',
    noMembersFound: 'Aucun membre trouvé',
    teamEmptyFixIssue: 'Corrigez le problème ci-dessus puis actualisez.',
    teamEmptyInvitePeople: 'Invitez des personnes pour les voir dans votre équipe.',
    teamEmptyNoSearch: 'Aucune entrée ne correspond à votre recherche.',
    changeRole: 'Changer le rôle',
    removeMember: 'Retirer le membre',
    viewProfile: 'Voir le profil',
    activeProjects: 'Projets actifs',
    tasks: 'Tâches',
    fullName: 'Nom complet',
    role: 'Rôle',
    projectAccess: 'Accès aux projets',
    createProjectFirstAccess: "Créez d'abord un projet pour attribuer l'accès.",
    saveMember: 'Enregistrer le membre',
    joined: 'Inscrit le',
    assignedTasks: 'Tâches assignées',
    noTasksAssignedYet: 'Aucune tâche assignée pour le moment.',
    activityLast5Buckets: 'Activité (5 dernières périodes)',
    removeFromRoster: 'Retirer de la liste…',
    updateFailed: 'Mise à jour impossible',
    couldNotUpdateRole: 'Impossible de mettre à jour le rôle.',
    couldNotDeleteMember: 'Impossible de supprimer le membre.',
    couldNotDelete: 'Suppression impossible.',
    inviteFullNameEmailRequired: "Saisissez le nom complet et l'e-mail.",
    inviteFailed: 'Invitation impossible.',
    removeMemberConfirmPrefix: 'Retirer',
    removeMemberConfirmSuffix: 'de votre équipe ?',
    roleAdmin: 'Admin',
    roleManager: 'Manager',
    roleDeveloper: 'Développeur',
    roleDesigner: 'Designer',
    roleViewer: 'Observateur',
    filterUnread: 'Non lus',
    filterMentions: 'Mentions',
    filterAssignments: 'Affectations',
    noNotificationsNow: 'Aucune notification pour le moment.',
    notificationTaskAssigned: 'Tâche assignée',
    notificationCommentMentioned: 'Mention dans un commentaire',
    notificationTaskStatusChanged: 'Statut de tâche modifié',
    notificationDueDateApproaching: 'Échéance proche',
    notificationNewProjectMember: 'Nouveau membre de projet',
    notificationMeetingLinkShared: 'Lien de réunion partagé',
    notificationNewPostInChannel: 'Nouveau post dans le canal',
    thisWeek: 'Cette semaine',
    direct: 'Direct',
    groups: 'Groupes',
    newMessage: '+ Nouveau message',
    newGroup: '+ Nouveau groupe',
    searchConversations: 'Rechercher des conversations...',
    loadingConversations: 'Chargement des conversations…',
    noConversationsYet: 'Aucune conversation pour le moment.',
    members: 'membres',
    loadingMessages: 'Chargement des messages…',
    meetingLinkShared: 'Lien de réunion partagé',
    read: 'Lu',
    sent: 'Envoyé',
    react: 'Réagir',
    reply: 'Répondre',
    replyTo: 'Répondre à',
    selectConversation: 'Sélectionnez une conversation pour commencer.',
    meetingLinkPlaceholder: 'https://zoom.us/j/...',
    share: 'Partager',
    statusOpen: 'Ouverte',
    channelNamePrompt: 'Nom du canal',
    newProjectChannel: 'Nouveau canal de projet',
    channels: 'Canaux',
    add: '+ Ajouter',
    searchInChannel: 'Rechercher dans le canal...',
    noPostsInChannel: 'Aucun post dans ce canal pour le moment.',
    reaction: '+ Réaction',
    newPost: 'Nouveau post',
    postComposer: 'Éditeur de post',
    postTitleOptional: 'Titre (facultatif)',
    writeYourPost: 'Écrivez votre post...',
    replyToThread: 'Répondre au fil...',
    globalSearchPlaceholder: 'Rechercher tâches, projets, membres... (Ctrl+K)',
    recentSearches: 'Recherches récentes',
    noMatches: 'Aucun résultat',
    onboardingTitle: 'Bienvenue dans CoreTask',
    onboardingStep: 'Étape',
    onboardingSystemTitle: 'Système avancé de gestion des tâches',
    onboardingSystemSubtitle: "Planifiez les projets, collaborez dans les canaux et suivez l'exécution au même endroit.",
    onboardingCreateFirstProject: 'Créez votre premier projet',
    onboardingInviteTeamMembers: "Inviter des membres de l'équipe",
    back: 'Retour',
    next: 'Suivant',
    onboardingDone: 'Tout est prêt',
    profileFallbackDescription: 'Mettez à jour votre profil, votre rôle et vos préférences de communication.',
    post: 'Publier',
    teamMember: "Membre de l'équipe",
    channelPosts: 'Posts de canal',
    taskComment: 'Commentaire de tâche',
    meetingLinkBody: 'Lien de réunion',
    addPeopleForDm: 'Ajoutez des personnes dans la page Équipe pour démarrer un message direct.',
    start: 'Démarrer',
    groupName: 'Nom du groupe',
    groupAvatarInitials: 'Initiales du groupe',
    createGroup: 'Créer le groupe',
    loadingAttachment: 'Chargement de la pièce jointe…',
    viewImageFullScreen: "Voir l'image en plein écran",
    fullScreenVideo: 'Vidéo plein écran',
    download: 'Télécharger',
    messageFallback: 'Message',
    image: 'Image',
    video: 'Vidéo',
    audio: 'Audio',
    file: 'Fichier',
    meeting: 'Réunion',
  },
  zh: {
    teamManageSubtitle: '管理所有活跃项目中的人员、角色和分配。',
    teamReadonlySubtitle: '你所属工作区中的人员。',
    online: '在线',
    offline: '离线',
    byRole: '按角色',
    searchMembers: '搜索成员...',
    noMembersFound: '未找到成员',
    teamEmptyFixIssue: '修复上方问题后刷新。',
    teamEmptyInvitePeople: '邀请人员后会显示在工作区名单中。',
    teamEmptyNoSearch: '没有匹配的名单条目。',
    changeRole: '更改角色',
    removeMember: '移除成员',
    viewProfile: '查看资料',
    activeProjects: '活跃项目',
    tasks: '任务',
    fullName: '全名',
    role: '角色',
    projectAccess: '项目访问权限',
    createProjectFirstAccess: '请先创建项目再分配访问权限。',
    saveMember: '保存成员',
    joined: '加入时间',
    assignedTasks: '已分配任务',
    noTasksAssignedYet: '尚未分配任务。',
    activityLast5Buckets: '活动（最近 5 段）',
    removeFromRoster: '从名单中移除…',
    updateFailed: '更新失败',
    couldNotUpdateRole: '无法更新角色。',
    couldNotDeleteMember: '无法删除成员。',
    couldNotDelete: '无法删除。',
    inviteFullNameEmailRequired: '请输入全名和电子邮箱。',
    inviteFailed: '邀请失败。',
    removeMemberConfirmPrefix: '从团队名单中移除',
    removeMemberConfirmSuffix: '？',
    roleAdmin: '管理员',
    roleManager: '经理',
    roleDeveloper: '开发者',
    roleDesigner: '设计师',
    roleViewer: '查看者',
    filterUnread: '未读',
    filterMentions: '提及',
    filterAssignments: '分配',
    noNotificationsNow: '当前没有通知。',
    notificationTaskAssigned: '任务已分配',
    notificationCommentMentioned: '评论中提及',
    notificationTaskStatusChanged: '任务状态已更改',
    notificationDueDateApproaching: '截止日期临近',
    notificationNewProjectMember: '新项目成员',
    notificationMeetingLinkShared: '会议链接已分享',
    notificationNewPostInChannel: '频道中新帖子',
    thisWeek: '本周',
    direct: '直接消息',
    groups: '群组',
    newMessage: '+ 新消息',
    newGroup: '+ 新群组',
    searchConversations: '搜索会话...',
    loadingConversations: '正在加载会话…',
    noConversationsYet: '还没有会话。',
    members: '成员',
    loadingMessages: '正在加载消息…',
    meetingLinkShared: '会议链接已分享',
    read: '已读',
    sent: '已发送',
    react: '反应',
    reply: '回复',
    replyTo: '回复',
    selectConversation: '选择一个会话开始消息。',
    meetingLinkPlaceholder: 'https://zoom.us/j/...',
    share: '分享',
    statusOpen: '开放',
    channelNamePrompt: '频道名称',
    newProjectChannel: '新项目频道',
    channels: '频道',
    add: '+ 添加',
    searchInChannel: '在频道中搜索...',
    noPostsInChannel: '此频道还没有帖子。',
    reaction: '+ 反应',
    newPost: '新帖子',
    postComposer: '帖子编辑器',
    postTitleOptional: '标题（可选）',
    writeYourPost: '写下你的帖子...',
    replyToThread: '回复线程...',
    globalSearchPlaceholder: '搜索任务、项目、成员... (Ctrl+K)',
    recentSearches: '最近搜索',
    noMatches: '没有匹配项',
    onboardingTitle: '欢迎使用 CoreTask',
    onboardingStep: '步骤',
    onboardingSystemTitle: '高级任务管理系统',
    onboardingSystemSubtitle: '在一个地方规划项目、在频道中协作并跟踪执行。',
    onboardingCreateFirstProject: '创建你的第一个项目',
    onboardingInviteTeamMembers: '邀请团队成员',
    back: '返回',
    next: '下一步',
    onboardingDone: '全部设置完成',
    profileFallbackDescription: '更新你的用户资料、角色和沟通偏好。',
    post: '发布',
    teamMember: '团队成员',
    channelPosts: '频道帖子',
    taskComment: '任务评论',
    meetingLinkBody: '会议链接',
    addPeopleForDm: '在团队页面添加人员以开始直接消息。',
    start: '开始',
    groupName: '群组名称',
    groupAvatarInitials: '群组头像首字母',
    createGroup: '创建群组',
    loadingAttachment: '正在加载附件…',
    viewImageFullScreen: '全屏查看图片',
    fullScreenVideo: '全屏视频',
    download: '下载',
    messageFallback: '消息',
    image: '图片',
    video: '视频',
    audio: '音频',
    file: '文件',
    meeting: '会议',
  },
  ur: {
    teamManageSubtitle: 'تمام active projects میں لوگوں، roles، اور assignments کو manage کریں۔',
    teamReadonlySubtitle: 'وہ لوگ جو آپ کی workspaces میں ہیں۔',
    online: 'آن لائن',
    offline: 'آف لائن',
    byRole: 'role کے حساب سے',
    searchMembers: 'members تلاش کریں...',
    noMembersFound: 'کوئی member نہیں ملا',
    teamEmptyFixIssue: 'اوپر والا مسئلہ حل کریں پھر refresh کریں۔',
    teamEmptyInvitePeople: 'لوگوں کو invite کریں تاکہ وہ workspace roster میں نظر آئیں۔',
    teamEmptyNoSearch: 'آپ کی تلاش سے کوئی roster entry match نہیں ہوئی۔',
    changeRole: 'role تبدیل کریں',
    removeMember: 'member ہٹائیں',
    viewProfile: 'profile دیکھیں',
    activeProjects: 'active projects',
    tasks: 'tasks',
    fullName: 'پورا نام',
    role: 'role',
    projectAccess: 'project access',
    createProjectFirstAccess: 'access assign کرنے کے لیے پہلے project بنائیں۔',
    saveMember: 'member محفوظ کریں',
    joined: 'شامل ہوا',
    assignedTasks: 'assigned tasks',
    noTasksAssignedYet: 'ابھی کوئی task assigned نہیں۔',
    activityLast5Buckets: 'activity (آخری 5 buckets)',
    removeFromRoster: 'roster سے ہٹائیں…',
    updateFailed: 'update ناکام',
    couldNotUpdateRole: 'role update نہیں ہو سکا۔',
    couldNotDeleteMember: 'member delete نہیں ہو سکا۔',
    couldNotDelete: 'delete نہیں ہو سکا۔',
    inviteFullNameEmailRequired: 'پورا نام اور email درج کریں۔',
    inviteFailed: 'invite ناکام۔',
    removeMemberConfirmPrefix: 'ہٹائیں',
    removeMemberConfirmSuffix: 'اپنے team roster سے؟',
    roleAdmin: 'Admin',
    roleManager: 'Manager',
    roleDeveloper: 'Developer',
    roleDesigner: 'Designer',
    roleViewer: 'Viewer',
    filterUnread: 'Unread',
    filterMentions: 'Mentions',
    filterAssignments: 'Assignments',
    noNotificationsNow: 'اس وقت کوئی notification نہیں۔',
    notificationTaskAssigned: 'Task Assigned',
    notificationCommentMentioned: 'Comment Mentioned',
    notificationTaskStatusChanged: 'Task Status Changed',
    notificationDueDateApproaching: 'Due Date Approaching',
    notificationNewProjectMember: 'New Project Member',
    notificationMeetingLinkShared: 'Meeting Link Shared',
    notificationNewPostInChannel: 'New Post in Channel',
    thisWeek: 'اس ہفتے',
    direct: 'Direct',
    groups: 'Groups',
    newMessage: '+ نیا message',
    newGroup: '+ نیا group',
    searchConversations: 'conversations تلاش کریں...',
    loadingConversations: 'conversations load ہو رہی ہیں…',
    noConversationsYet: 'ابھی کوئی conversation نہیں۔',
    members: 'members',
    loadingMessages: 'messages load ہو رہے ہیں…',
    meetingLinkShared: 'Meeting Link Shared',
    read: 'Read',
    sent: 'Sent',
    react: 'React',
    reply: 'Reply',
    replyTo: 'Reply to',
    selectConversation: 'messaging شروع کرنے کے لیے conversation منتخب کریں۔',
    meetingLinkPlaceholder: 'https://zoom.us/j/...',
    share: 'Share',
    meetingLinkBody: 'Meeting link',
    addPeopleForDm: 'direct message شروع کرنے کے لیے Team page پر لوگ شامل کریں۔',
    start: 'Start',
    groupName: 'Group name',
    groupAvatarInitials: 'Group avatar initials',
    createGroup: 'Create Group',
    loadingAttachment: 'attachment load ہو رہی ہے…',
    viewImageFullScreen: 'image full screen دیکھیں',
    fullScreenVideo: 'full screen video',
    download: 'Download',
    messageFallback: 'Message',
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
    file: 'File',
    meeting: 'Meeting',
  },
};

Object.entries(stepThreeTranslations).forEach(([language, entries]) => {
  translations[language] = { ...(translations[language] || {}), ...entries };
});

const stepFourTranslations = {
  en: {
    settingsSecurity: 'Account Security',
    settingsAppearance: 'Appearance',
    settingsIntegrations: 'Integrations',
    loadingSettings: 'Loading settings…',
    profileSettingsTitle: 'Profile Settings',
    comingSoon: 'Coming soon',
    uploadAvatar: 'Upload Avatar',
    displayName: 'Display name',
    signInEmailTitle: 'Sign-in email (managed with your account)',
    timezonePlaceholder: 'Timezone (e.g. UTC, Asia/Riyadh)',
    bio: 'Bio',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    passwordMismatch: 'New password and confirmation must match.',
    passwordMinLength: 'New password must be at least 8 characters.',
    passwordUpdated: 'Password updated.',
    updatePassword: 'Update password',
    twoFactorPreference: 'Enable two-factor authentication (preference only)',
    activeSessions: 'Active Sessions',
    sessionsComingSoon: 'Session listing is coming in a future update.',
    settingsNotificationsTitle: 'Notifications Settings',
    type: 'Type',
    inApp: 'In-App',
    push: 'Push',
    accentColor: 'Accent Color',
    fontSize: 'Font Size',
    sidebarCompactMode: 'Sidebar compact mode',
    modeLight: 'Light Mode',
    modeDark: 'Dark Mode',
    modeSystem: 'System Mode',
    fontSmall: 'Small',
    fontMedium: 'Medium',
    fontLarge: 'Large',
    workspaceSettingsAdmin: 'Workspace Settings (Admin)',
    workspaceName: 'Workspace name',
    defaultProjectPermission: 'Default project permission',
    memberInvitationSettings: 'Member invitation settings',
    connected: 'Connected',
    disconnected: 'Disconnected',
    connect: 'Connect',
    disconnect: 'Disconnect',
    inviteEnterPassword: 'Please enter your CoreTask password.',
    inviteIncompleteResponse: 'Invitation accepted but the server response was incomplete.',
    inviteSetupFailed: 'Could not finish setup.',
    priorityCritical: 'Critical',
    taskReference: 'Task reference',
    loadingTask: 'Loading task…',
    uploads: 'Uploads',
    subtasks: 'Subtasks',
    comments: 'Comments',
    attachments: 'Attachments',
    loadingPreview: 'Loading preview…',
    noFilesUploadedYet: 'No files uploaded yet.',
    writeCommentMention: 'Write a comment… (@ to mention team)',
    writeReply: 'Reply…',
    deleteCommentConfirm: 'Delete comment?',
    noTeamRosterMentions: 'No team roster loaded — mentions still work if you type @ exactly as the name on the team list.',
    assigneeInviteHint: 'Only teammates who accepted an invite and signed in can be assigned. Pending invites appear on Team but not here.',
    dueDate: 'Due date',
    project: 'Project',
    saveLink: 'Save link',
    save: 'Save',
    me: 'Me',
    editorFormatting: 'Formatting',
    editorBold: 'Bold',
    editorItalic: 'Italic',
    editorUnderline: 'Underline',
    editorStrikethrough: 'Strikethrough',
    editorHeading: 'Heading',
    editorBulletedList: 'Bulleted list',
    editorNumberedList: 'Numbered list',
    editorQuote: 'Quote',
    editorInlineCode: 'Inline code',
    editorEditRemoveLink: 'Edit / remove link',
    editorInsertLink: 'Insert link',
    editorLinkUrl: 'Link URL',
    editorUndo: 'Undo',
    editorRedo: 'Redo',
    writeDescription: 'Write description…',
    categoriesLabels: 'Categories & Labels',
    categories: 'Categories',
    labels: 'Labels',
    searchCategories: 'Search categories...',
    searchLabels: 'Search labels...',
    taskCountLabel: 'tasks',
    renameCategory: 'Rename category',
    newCategory: '+ New Category',
    categoryName: 'Category name',
    iconOrEmoji: 'Icon or emoji',
    createCategory: 'Create Category',
    newLabel: '+ New Label',
    labelName: 'Label name',
    createLabel: 'Create Label',
    manageCategoriesLabels: 'Manage Categories & Labels',
    activityComments: 'Activity / Comments',
    reporter: 'Reporter',
    category: 'Category',
    labelsTags: 'Labels/Tags',
    writeReplyFull: 'Write a reply...',
    writeCommentFull: 'Write a comment...',
    emoji: 'Emoji',
    attachImage: 'Attach Image',
    attachVideo: 'Attach Video',
    attachAudio: 'Attach Audio',
    mention: 'Mention',
    preview: 'Preview',
    share: 'Share',
  },
  ar: {
    settingsSecurity: 'أمان الحساب',
    settingsAppearance: 'المظهر',
    settingsIntegrations: 'التكاملات',
    loadingSettings: 'جاري تحميل الإعدادات…',
    profileSettingsTitle: 'إعدادات الملف الشخصي',
    comingSoon: 'قريبًا',
    uploadAvatar: 'رفع الصورة الشخصية',
    displayName: 'اسم العرض',
    signInEmailTitle: 'بريد تسجيل الدخول (يُدار من حسابك)',
    timezonePlaceholder: 'المنطقة الزمنية (مثل UTC, Asia/Riyadh)',
    bio: 'نبذة',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmNewPassword: 'تأكيد كلمة المرور الجديدة',
    passwordMismatch: 'يجب أن تتطابق كلمة المرور الجديدة مع التأكيد.',
    passwordMinLength: 'يجب أن تكون كلمة المرور الجديدة 8 أحرف على الأقل.',
    passwordUpdated: 'تم تحديث كلمة المرور.',
    updatePassword: 'تحديث كلمة المرور',
    twoFactorPreference: 'تفعيل المصادقة الثنائية (تفضيل فقط)',
    activeSessions: 'الجلسات النشطة',
    sessionsComingSoon: 'قائمة الجلسات ستتوفر في تحديث لاحق.',
    settingsNotificationsTitle: 'إعدادات الإشعارات',
    type: 'النوع',
    inApp: 'داخل التطبيق',
    push: 'دفع',
    accentColor: 'لون التمييز',
    fontSize: 'حجم الخط',
    sidebarCompactMode: 'وضع الشريط الجانبي المضغوط',
    modeLight: 'الوضع الفاتح',
    modeDark: 'الوضع الداكن',
    modeSystem: 'وضع النظام',
    fontSmall: 'صغير',
    fontMedium: 'متوسط',
    fontLarge: 'كبير',
    workspaceSettingsAdmin: 'إعدادات مساحة العمل (مدير)',
    workspaceName: 'اسم مساحة العمل',
    defaultProjectPermission: 'صلاحية المشروع الافتراضية',
    memberInvitationSettings: 'إعدادات دعوة الأعضاء',
    connected: 'متصل',
    disconnected: 'غير متصل',
    connect: 'اتصال',
    disconnect: 'قطع الاتصال',
    inviteEnterPassword: 'أدخل كلمة مرور CoreTask.',
    inviteIncompleteResponse: 'تم قبول الدعوة لكن استجابة الخادم غير مكتملة.',
    inviteSetupFailed: 'تعذر إكمال الإعداد.',
    priorityCritical: 'حرجة',
    taskReference: 'مرجع المهمة',
    loadingTask: 'جاري تحميل المهمة…',
    uploads: 'الملفات المرفوعة',
    subtasks: 'المهام الفرعية',
    comments: 'التعليقات',
    attachments: 'المرفقات',
    loadingPreview: 'جاري تحميل المعاينة…',
    noFilesUploadedYet: 'لم يتم رفع ملفات بعد.',
    writeCommentMention: 'اكتب تعليقًا… (@ لذكر الفريق)',
    writeReply: 'رد…',
    deleteCommentConfirm: 'حذف التعليق؟',
    noTeamRosterMentions: 'لم يتم تحميل قائمة الفريق — ستعمل الإشارات إذا كتبت @ بنفس الاسم الموجود في قائمة الفريق.',
    assigneeInviteHint: 'يمكن تعيين الزملاء الذين قبلوا الدعوة وسجلوا الدخول فقط. تظهر الدعوات المعلقة في صفحة الفريق وليس هنا.',
    dueDate: 'تاريخ الاستحقاق',
    project: 'المشروع',
    saveLink: 'حفظ الرابط',
    save: 'حفظ',
    me: 'أنا',
    editorFormatting: 'التنسيق',
    editorBold: 'عريض',
    editorItalic: 'مائل',
    editorUnderline: 'تحته خط',
    editorStrikethrough: 'يتوسطه خط',
    editorHeading: 'عنوان',
    editorBulletedList: 'قائمة نقطية',
    editorNumberedList: 'قائمة مرقمة',
    editorQuote: 'اقتباس',
    editorInlineCode: 'كود داخل السطر',
    editorEditRemoveLink: 'تعديل / إزالة الرابط',
    editorInsertLink: 'إدراج رابط',
    editorLinkUrl: 'رابط URL',
    editorUndo: 'تراجع',
    editorRedo: 'إعادة',
    writeDescription: 'اكتب الوصف…',
    categoriesLabels: 'الفئات والتسميات',
    categories: 'الفئات',
    labels: 'التسميات',
    searchCategories: 'ابحث في الفئات...',
    searchLabels: 'ابحث في التسميات...',
    taskCountLabel: 'مهام',
    renameCategory: 'إعادة تسمية الفئة',
    newCategory: '+ فئة جديدة',
    categoryName: 'اسم الفئة',
    iconOrEmoji: 'أيقونة أو رمز تعبيري',
    createCategory: 'إنشاء فئة',
    newLabel: '+ تسمية جديدة',
    labelName: 'اسم التسمية',
    createLabel: 'إنشاء تسمية',
    manageCategoriesLabels: 'إدارة الفئات والتسميات',
    activityComments: 'النشاط / التعليقات',
    reporter: 'المبلّغ',
    category: 'الفئة',
    labelsTags: 'التسميات/الوسوم',
    writeReplyFull: 'اكتب ردًا...',
    writeCommentFull: 'اكتب تعليقًا...',
    emoji: 'رموز تعبيرية',
    attachImage: 'إرفاق صورة',
    attachVideo: 'إرفاق فيديو',
    attachAudio: 'إرفاق صوت',
    mention: 'إشارة',
    preview: 'معاينة',
    share: 'مشاركة',
  },
  fr: {
    settingsSecurity: 'Sécurité du compte',
    settingsAppearance: 'Apparence',
    settingsIntegrations: 'Intégrations',
    loadingSettings: 'Chargement des paramètres…',
    profileSettingsTitle: 'Paramètres du profil',
    comingSoon: 'Bientôt disponible',
    uploadAvatar: "Téléverser l'avatar",
    displayName: 'Nom affiché',
    signInEmailTitle: 'E-mail de connexion (géré avec votre compte)',
    timezonePlaceholder: 'Fuseau horaire (ex. UTC, Asia/Riyadh)',
    bio: 'Bio',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmNewPassword: 'Confirmer le nouveau mot de passe',
    passwordMismatch: 'Le nouveau mot de passe et la confirmation doivent correspondre.',
    passwordMinLength: 'Le nouveau mot de passe doit contenir au moins 8 caractères.',
    passwordUpdated: 'Mot de passe mis à jour.',
    updatePassword: 'Mettre à jour le mot de passe',
    twoFactorPreference: "Activer l'authentification à deux facteurs (préférence seulement)",
    activeSessions: 'Sessions actives',
    sessionsComingSoon: 'La liste des sessions arrivera dans une prochaine mise à jour.',
    settingsNotificationsTitle: 'Paramètres de notifications',
    type: 'Type',
    inApp: "Dans l'app",
    push: 'Push',
    accentColor: "Couleur d'accent",
    fontSize: 'Taille de police',
    sidebarCompactMode: 'Mode barre latérale compacte',
    modeLight: 'Mode clair',
    modeDark: 'Mode sombre',
    modeSystem: 'Mode système',
    fontSmall: 'Petite',
    fontMedium: 'Moyenne',
    fontLarge: 'Grande',
    workspaceSettingsAdmin: "Paramètres de l'espace de travail (Admin)",
    workspaceName: "Nom de l'espace de travail",
    defaultProjectPermission: 'Autorisation de projet par défaut',
    memberInvitationSettings: "Paramètres d'invitation des membres",
    connected: 'Connecté',
    disconnected: 'Déconnecté',
    connect: 'Connecter',
    disconnect: 'Déconnecter',
    inviteEnterPassword: 'Veuillez saisir votre mot de passe CoreTask.',
    inviteIncompleteResponse: 'Invitation acceptée, mais la réponse du serveur est incomplète.',
    inviteSetupFailed: 'Impossible de terminer la configuration.',
    priorityCritical: 'Critique',
    taskReference: 'Référence de tâche',
    loadingTask: 'Chargement de la tâche…',
    uploads: 'Téléversements',
    subtasks: 'Sous-tâches',
    comments: 'Commentaires',
    attachments: 'Pièces jointes',
    loadingPreview: "Chargement de l'aperçu…",
    noFilesUploadedYet: 'Aucun fichier téléversé pour le moment.',
    writeCommentMention: "Écrire un commentaire… (@ pour mentionner l'équipe)",
    writeReply: 'Répondre…',
    deleteCommentConfirm: 'Supprimer le commentaire ?',
    noTeamRosterMentions: "Aucune liste d'équipe chargée — les mentions fonctionnent si vous tapez @ exactement comme le nom dans la liste.",
    assigneeInviteHint: "Seuls les coéquipiers ayant accepté une invitation et s'étant connectés peuvent être assignés. Les invitations en attente apparaissent dans Équipe, mais pas ici.",
    dueDate: 'Échéance',
    project: 'Projet',
    saveLink: 'Enregistrer le lien',
    save: 'Enregistrer',
    me: 'Moi',
    editorFormatting: 'Mise en forme',
    editorBold: 'Gras',
    editorItalic: 'Italique',
    editorUnderline: 'Souligné',
    editorStrikethrough: 'Barré',
    editorHeading: 'Titre',
    editorBulletedList: 'Liste à puces',
    editorNumberedList: 'Liste numérotée',
    editorQuote: 'Citation',
    editorInlineCode: 'Code en ligne',
    editorEditRemoveLink: 'Modifier / retirer le lien',
    editorInsertLink: 'Insérer un lien',
    editorLinkUrl: 'URL du lien',
    editorUndo: 'Annuler',
    editorRedo: 'Rétablir',
    writeDescription: 'Écrire une description…',
    categoriesLabels: 'Catégories et libellés',
    categories: 'Catégories',
    labels: 'Libellés',
    searchCategories: 'Rechercher des catégories...',
    searchLabels: 'Rechercher des libellés...',
    taskCountLabel: 'tâches',
    renameCategory: 'Renommer la catégorie',
    newCategory: '+ Nouvelle catégorie',
    categoryName: 'Nom de catégorie',
    iconOrEmoji: 'Icône ou emoji',
    createCategory: 'Créer la catégorie',
    newLabel: '+ Nouveau libellé',
    labelName: 'Nom du libellé',
    createLabel: 'Créer le libellé',
    manageCategoriesLabels: 'Gérer les catégories et libellés',
    activityComments: 'Activité / Commentaires',
    reporter: 'Rapporteur',
    category: 'Catégorie',
    labelsTags: 'Libellés/Tags',
    writeReplyFull: 'Écrire une réponse...',
    writeCommentFull: 'Écrire un commentaire...',
    emoji: 'Emoji',
    attachImage: 'Joindre une image',
    attachVideo: 'Joindre une vidéo',
    attachAudio: 'Joindre un audio',
    mention: 'Mention',
    preview: 'Aperçu',
    share: 'Partager',
  },
  zh: {
    settingsSecurity: '账户安全',
    settingsAppearance: '外观',
    settingsIntegrations: '集成',
    loadingSettings: '正在加载设置…',
    profileSettingsTitle: '个人资料设置',
    comingSoon: '即将推出',
    uploadAvatar: '上传头像',
    displayName: '显示名称',
    signInEmailTitle: '登录邮箱（由你的账户管理）',
    timezonePlaceholder: '时区（例如 UTC, Asia/Riyadh）',
    bio: '简介',
    currentPassword: '当前密码',
    newPassword: '新密码',
    confirmNewPassword: '确认新密码',
    passwordMismatch: '新密码和确认密码必须一致。',
    passwordMinLength: '新密码至少需要 8 个字符。',
    passwordUpdated: '密码已更新。',
    updatePassword: '更新密码',
    twoFactorPreference: '启用双因素认证（仅偏好设置）',
    activeSessions: '活跃会话',
    sessionsComingSoon: '会话列表将在后续更新中提供。',
    settingsNotificationsTitle: '通知设置',
    type: '类型',
    inApp: '应用内',
    push: '推送',
    accentColor: '强调色',
    fontSize: '字体大小',
    sidebarCompactMode: '紧凑侧边栏模式',
    modeLight: '浅色模式',
    modeDark: '深色模式',
    modeSystem: '跟随系统',
    fontSmall: '小',
    fontMedium: '中',
    fontLarge: '大',
    workspaceSettingsAdmin: '工作区设置（管理员）',
    workspaceName: '工作区名称',
    defaultProjectPermission: '默认项目权限',
    memberInvitationSettings: '成员邀请设置',
    connected: '已连接',
    disconnected: '未连接',
    connect: '连接',
    disconnect: '断开连接',
    inviteEnterPassword: '请输入你的 CoreTask 密码。',
    inviteIncompleteResponse: '邀请已接受，但服务器响应不完整。',
    inviteSetupFailed: '无法完成设置。',
    priorityCritical: '紧急',
    taskReference: '任务引用',
    loadingTask: '正在加载任务…',
    uploads: '上传',
    subtasks: '子任务',
    comments: '评论',
    attachments: '附件',
    loadingPreview: '正在加载预览…',
    noFilesUploadedYet: '尚未上传文件。',
    writeCommentMention: '写评论…（输入 @ 提及团队）',
    writeReply: '回复…',
    deleteCommentConfirm: '删除评论？',
    noTeamRosterMentions: '未加载团队名单，如果你输入与团队列表中完全相同的 @ 名称，提及仍可使用。',
    assigneeInviteHint: '只能分配给已接受邀请并登录的队友。待接受邀请会显示在团队页面，但不会显示在这里。',
    dueDate: '截止日期',
    project: '项目',
    saveLink: '保存链接',
    save: '保存',
    me: '我',
    editorFormatting: '格式',
    editorBold: '加粗',
    editorItalic: '斜体',
    editorUnderline: '下划线',
    editorStrikethrough: '删除线',
    editorHeading: '标题',
    editorBulletedList: '项目符号列表',
    editorNumberedList: '编号列表',
    editorQuote: '引用',
    editorInlineCode: '行内代码',
    editorEditRemoveLink: '编辑 / 移除链接',
    editorInsertLink: '插入链接',
    editorLinkUrl: '链接 URL',
    editorUndo: '撤销',
    editorRedo: '重做',
    writeDescription: '填写描述…',
    categoriesLabels: '分类和标签',
    categories: '分类',
    labels: '标签',
    searchCategories: '搜索分类...',
    searchLabels: '搜索标签...',
    taskCountLabel: '任务',
    renameCategory: '重命名分类',
    newCategory: '+ 新分类',
    categoryName: '分类名称',
    iconOrEmoji: '图标或表情',
    createCategory: '创建分类',
    newLabel: '+ 新标签',
    labelName: '标签名称',
    createLabel: '创建标签',
    manageCategoriesLabels: '管理分类和标签',
    activityComments: '活动 / 评论',
    reporter: '报告人',
    category: '分类',
    labelsTags: '标签',
    writeReplyFull: '写回复...',
    writeCommentFull: '写评论...',
    emoji: '表情',
    attachImage: '附加图片',
    attachVideo: '附加视频',
    attachAudio: '附加音频',
    mention: '提及',
    preview: '预览',
    share: '分享',
  },
  ur: {
    settingsSecurity: 'اکاؤنٹ سکیورٹی',
    settingsAppearance: 'ظاہری شکل',
    settingsIntegrations: 'Integrations',
    loadingSettings: 'settings load ہو رہی ہیں…',
    profileSettingsTitle: 'profile settings',
    comingSoon: 'جلد آ رہا ہے',
    uploadAvatar: 'avatar upload کریں',
    displayName: 'display name',
    signInEmailTitle: 'sign-in email (آپ کے account سے managed)',
    timezonePlaceholder: 'timezone (مثلاً UTC, Asia/Riyadh)',
    bio: 'bio',
    currentPassword: 'موجودہ password',
    newPassword: 'نیا password',
    confirmNewPassword: 'نئے password کی تصدیق',
    passwordMismatch: 'نیا password اور confirmation match ہونے چاہئیں۔',
    passwordMinLength: 'نیا password کم از کم 8 characters کا ہونا چاہیے۔',
    passwordUpdated: 'password update ہو گیا۔',
    updatePassword: 'password update کریں',
    twoFactorPreference: 'two-factor authentication enable کریں (صرف preference)',
    activeSessions: 'active sessions',
    sessionsComingSoon: 'session listing future update میں آئے گی۔',
    settingsNotificationsTitle: 'notifications settings',
    type: 'type',
    inApp: 'in-app',
    push: 'push',
    accentColor: 'accent color',
    fontSize: 'font size',
    sidebarCompactMode: 'sidebar compact mode',
    modeLight: 'light mode',
    modeDark: 'dark mode',
    modeSystem: 'system mode',
    fontSmall: 'small',
    fontMedium: 'medium',
    fontLarge: 'large',
    workspaceSettingsAdmin: 'workspace settings (Admin)',
    workspaceName: 'workspace name',
    defaultProjectPermission: 'default project permission',
    memberInvitationSettings: 'member invitation settings',
    connected: 'connected',
    disconnected: 'disconnected',
    connect: 'connect',
    disconnect: 'disconnect',
    inviteEnterPassword: 'اپنا CoreTask password درج کریں۔',
    inviteIncompleteResponse: 'دعوت accept ہو گئی لیکن server response incomplete ہے۔',
    inviteSetupFailed: 'setup مکمل نہیں ہو سکا۔',
    priorityCritical: 'Critical',
    taskReference: 'task reference',
    loadingTask: 'task load ہو رہا ہے…',
    uploads: 'uploads',
    subtasks: 'subtasks',
    comments: 'comments',
    attachments: 'attachments',
    loadingPreview: 'preview load ہو رہا ہے…',
    noFilesUploadedYet: 'ابھی کوئی file upload نہیں ہوئی۔',
    writeCommentMention: 'comment لکھیں… (@ team mention کے لیے)',
    writeReply: 'reply…',
    deleteCommentConfirm: 'comment delete کریں؟',
    noTeamRosterMentions: 'team roster load نہیں ہوا — mentions تب بھی کام کریں گے اگر آپ @ کے بعد team list والا exact name لکھیں۔',
    assigneeInviteHint: 'صرف وہ teammates assign ہو سکتے ہیں جنہوں نے invite accept کر کے sign in کیا ہو۔ Pending invites Team پر نظر آتے ہیں، یہاں نہیں۔',
    dueDate: 'due date',
    project: 'project',
    saveLink: 'link محفوظ کریں',
    save: 'محفوظ کریں',
    me: 'میں',
    editorFormatting: 'formatting',
    editorBold: 'bold',
    editorItalic: 'italic',
    editorUnderline: 'underline',
    editorStrikethrough: 'strikethrough',
    editorHeading: 'heading',
    editorBulletedList: 'bulleted list',
    editorNumberedList: 'numbered list',
    editorQuote: 'quote',
    editorInlineCode: 'inline code',
    editorEditRemoveLink: 'link edit / remove کریں',
    editorInsertLink: 'link insert کریں',
    editorLinkUrl: 'link URL',
    editorUndo: 'undo',
    editorRedo: 'redo',
    writeDescription: 'description لکھیں…',
    categoriesLabels: 'Categories & Labels',
    categories: 'Categories',
    labels: 'Labels',
    searchCategories: 'categories تلاش کریں...',
    searchLabels: 'labels تلاش کریں...',
    taskCountLabel: 'tasks',
    renameCategory: 'category rename کریں',
    newCategory: '+ نئی category',
    categoryName: 'category name',
    iconOrEmoji: 'icon یا emoji',
    createCategory: 'category بنائیں',
    newLabel: '+ نیا label',
    labelName: 'label name',
    createLabel: 'label بنائیں',
    manageCategoriesLabels: 'Categories & Labels manage کریں',
    activityComments: 'activity / comments',
    reporter: 'reporter',
    category: 'category',
    labelsTags: 'labels/tags',
    writeReplyFull: 'reply لکھیں...',
    writeCommentFull: 'comment لکھیں...',
    emoji: 'emoji',
    attachImage: 'image attach کریں',
    attachVideo: 'video attach کریں',
    attachAudio: 'audio attach کریں',
    mention: 'mention',
    preview: 'preview',
    share: 'share',
    statusOpen: 'open',
    channelNamePrompt: 'channel name',
    newProjectChannel: 'نیا project channel',
    channels: 'channels',
    add: '+ add',
    searchInChannel: 'channel میں تلاش کریں...',
    noPostsInChannel: 'اس channel میں ابھی کوئی post نہیں۔',
    reaction: '+ reaction',
    newPost: 'نئی post',
    postComposer: 'post composer',
    postTitleOptional: 'title (optional)',
    writeYourPost: 'اپنی post لکھیں...',
    replyToThread: 'thread کا reply...',
    globalSearchPlaceholder: 'tasks، projects، members تلاش کریں... (Ctrl+K)',
    recentSearches: 'recent searches',
    noMatches: 'کوئی match نہیں',
    onboardingTitle: 'CoreTask میں خوش آمدید',
    onboardingStep: 'step',
    onboardingSystemTitle: 'advanced task management system',
    onboardingSystemSubtitle: 'projects plan کریں، channels میں collaborate کریں، اور execution ایک جگہ track کریں۔',
    onboardingCreateFirstProject: 'اپنا پہلا project بنائیں',
    onboardingInviteTeamMembers: 'team members کو invite کریں',
    back: 'back',
    next: 'next',
    onboardingDone: 'آپ تیار ہیں',
    profileFallbackDescription: 'اپنا user profile، role، اور communication preferences update کریں۔',
    post: 'post',
    teamMember: 'team member',
    channelPosts: 'channel posts',
    taskComment: 'task comment',
  },
};

Object.entries(stepFourTranslations).forEach(([language, entries]) => {
  translations[language] = { ...(translations[language] || {}), ...entries };
});

const stepFiveTranslations = {
  en: {
    navSuperAdmin: 'Super Admin',
    superAdminBadge: 'Super Admin Panel',
    superAdminTitle: 'Admin User Management',
    superAdminSubtitle: 'Create and manage Admin accounts. Log in as any Admin to troubleshoot on their behalf.',
    superAdminAddAdmin: 'Add Admin',
    superAdminCreate: 'Create',
    superAdminDelete: 'Delete',
    superAdminConfirmDelete: 'Confirm Delete',
    superAdminLoginAs: 'Login as this account',
    superAdminEmail: 'Email address',
    superAdminPassword: 'Password',
    superAdminPasswordPlaceholder: 'Min. 8 chars, uppercase, digit',
    superAdminCreateValidation: 'Email and password are required.',
    superAdminCreateError: 'Failed to create admin.',
    superAdminDeleteError: 'Failed to delete admin.',
    superAdminImpersonateError: 'Failed to impersonate user.',
    superAdminLoadError: 'Failed to load admins.',
    superAdminCreatedToast: 'Admin account created.',
    superAdminDeletedToast: 'Admin account deleted.',
    superAdminImpersonateToast: 'Logged in as admin.',
    superAdminEmpty: 'No admin accounts yet. Add one to get started.',
    superAdminTotalAdmins: 'Total Admins',
    superAdminRoleLabel: 'Your Role',
    superAdminScopeLabel: 'Scope',
    superAdminScopeValue: 'All Workspaces',
    refresh: 'Refresh',
    // Privacy Policy
    navPrivacyPolicy: 'Privacy Policy',
    privacyTitle: 'Privacy Policy',
    privacyLastUpdated: 'Last updated: May 2026',
    privacyIntro: 'CoreTask is committed to protecting your personal data. This Privacy Policy explains what information we collect, how we use it, and your rights regarding that information.',
    privacySec1Title: 'Information We Collect',
    privacySec1Body: 'We collect information you provide directly, such as your name, email address, and content you create within the platform (projects, tasks, notes, messages). We also collect usage data such as login timestamps and feature interactions to improve the service.',
    privacySec2Title: 'How We Use Your Information',
    privacySec2Body: 'Your data is used solely to operate and improve CoreTask. We do not sell or rent your personal information to third parties. Data may be used for sending workspace notifications, system emails, and analytics to improve the product.',
    privacySec3Title: 'Data Storage & Security',
    privacySec3Body: 'All data is stored on secured servers with encryption at rest and in transit. Passwords are hashed using industry-standard algorithms and are never stored in plain text. Access is restricted to authorised personnel only.',
    privacySec4Title: 'Cookies & Local Storage',
    privacySec4Body: 'CoreTask uses browser session storage to maintain your login session within a single tab. No third-party tracking cookies are used. Theme and language preferences are stored in localStorage to persist your settings.',
    privacySec5Title: 'Your Rights',
    privacySec5Body: 'You have the right to access, correct, or delete your personal data at any time. You may also request an export of your data. To exercise these rights, contact us via the Contact page or by emailing support.',
    privacySec6Title: 'Changes to This Policy',
    privacySec6Body: 'We may update this policy from time to time. We will notify users of significant changes via email or an in-app notification. Continued use of the service constitutes acceptance of the updated policy.',
    privacyContact: 'Questions? Contact us at support@coretask.io or use the Contact Messages form.',
    // Contact Messages
    navContactMessages: 'Contact Messages',
    contactTitle: 'Contact Messages',
    contactSubtitle: 'Inbox of messages submitted through the contact form.',
    contactNew: 'New',
    contactMarkRead: 'Mark as read',
    contactReply: 'Reply by email',
    contactFilterAll: 'All',
    contactFilterUnread: 'Unread',
    contactFilterRead: 'Read',
    contactUnreadHint: 'unread',
    contactEmpty: 'No messages yet.',
    contactLoadError: 'Failed to load messages.',
    contactMarkReadError: 'Failed to mark as read.',
    contactDeletedToast: 'Message deleted.',
    contactDeleteError: 'Failed to delete message.',
  },
  ar: {
    navSuperAdmin: 'المشرف الأعلى',
    superAdminBadge: 'لوحة المشرف الأعلى',
    superAdminTitle: 'إدارة حسابات المدراء',
    superAdminSubtitle: 'أنشئ حسابات مدراء وأدرها. سجّل الدخول بوصفك أي مدير لحل المشكلات نيابةً عنه.',
    superAdminAddAdmin: 'إضافة مدير',
    superAdminCreate: 'إنشاء',
    superAdminDelete: 'حذف',
    superAdminConfirmDelete: 'تأكيد الحذف',
    superAdminLoginAs: 'تسجيل الدخول بهذا الحساب',
    superAdminEmail: 'البريد الإلكتروني',
    superAdminPassword: 'كلمة المرور',
    superAdminPasswordPlaceholder: '8 أحرف على الأقل، حرف كبير، رقم',
    superAdminCreateValidation: 'البريد الإلكتروني وكلمة المرور مطلوبان.',
    superAdminCreateError: 'فشل إنشاء حساب المدير.',
    superAdminDeleteError: 'فشل حذف حساب المدير.',
    superAdminImpersonateError: 'فشل انتحال هوية المستخدم.',
    superAdminLoadError: 'فشل تحميل قائمة المدراء.',
    superAdminCreatedToast: 'تم إنشاء حساب المدير.',
    superAdminDeletedToast: 'تم حذف حساب المدير.',
    superAdminImpersonateToast: 'تم تسجيل الدخول بوصفك مدير.',
    superAdminEmpty: 'لا توجد حسابات مدراء بعد. أضف واحدًا للبدء.',
    superAdminTotalAdmins: 'إجمالي المدراء',
    superAdminRoleLabel: 'دورك',
    superAdminScopeLabel: 'النطاق',
    superAdminScopeValue: 'جميع مساحات العمل',
    refresh: 'تحديث',
    navPrivacyPolicy: 'سياسة الخصوصية',
    privacyTitle: 'سياسة الخصوصية',
    privacyLastUpdated: 'آخر تحديث: مايو 2026',
    privacyIntro: 'تلتزم CoreTask بحماية بياناتك الشخصية. توضح سياسة الخصوصية هذه المعلومات التي نجمعها وكيفية استخدامها وحقوقك بشأنها.',
    privacySec1Title: 'المعلومات التي نجمعها',
    privacySec1Body: 'نجمع المعلومات التي تقدمها مباشرةً، كاسمك وعنوان بريدك الإلكتروني والمحتوى الذي تنشئه داخل المنصة. كما نجمع بيانات الاستخدام لتحسين الخدمة.',
    privacySec2Title: 'كيفية استخدام معلوماتك',
    privacySec2Body: 'تُستخدم بياناتك حصراً لتشغيل CoreTask وتحسينها. لا نبيع معلوماتك الشخصية ولا نؤجرها لجهات خارجية.',
    privacySec3Title: 'تخزين البيانات وأمانها',
    privacySec3Body: 'تُخزَّن جميع البيانات على خوادم مؤمَّنة مع تشفير البيانات أثناء الراحة وأثناء النقل. كلمات المرور مُجزَّأة ولا تُخزَّن أبداً كنصٍّ عادي.',
    privacySec4Title: 'ملفات تعريف الارتباط والتخزين المحلي',
    privacySec4Body: 'تستخدم CoreTask تخزين الجلسة في المتصفح للحفاظ على جلسة تسجيل الدخول. لا تُستخدم ملفات تعريف ارتباط تتبع تابعة لجهات خارجية.',
    privacySec5Title: 'حقوقك',
    privacySec5Body: 'يحق لك الوصول إلى بياناتك الشخصية وتصحيحها أو حذفها في أي وقت. تواصل معنا عبر صفحة الاتصال لممارسة هذه الحقوق.',
    privacySec6Title: 'التغييرات على هذه السياسة',
    privacySec6Body: 'قد نحدِّث هذه السياسة من وقت لآخر. سنُخطر المستخدمين بالتغييرات المهمة عبر البريد الإلكتروني أو إشعار داخل التطبيق.',
    privacyContact: 'أسئلة؟ تواصل معنا على support@coretask.io أو من خلال نموذج الاتصال.',
    navContactMessages: 'رسائل التواصل',
    contactTitle: 'رسائل التواصل',
    contactSubtitle: 'صندوق الرسائل المرسلة عبر نموذج الاتصال.',
    contactNew: 'جديد',
    contactMarkRead: 'تحديد كمقروء',
    contactReply: 'الرد بالبريد الإلكتروني',
    contactFilterAll: 'الكل',
    contactFilterUnread: 'غير مقروء',
    contactFilterRead: 'مقروء',
    contactUnreadHint: 'غير مقروء',
    contactEmpty: 'لا توجد رسائل بعد.',
    contactLoadError: 'فشل تحميل الرسائل.',
    contactMarkReadError: 'فشل تحديد الرسالة كمقروءة.',
    contactDeletedToast: 'تم حذف الرسالة.',
    contactDeleteError: 'فشل حذف الرسالة.',
  },
  fr: {
    navSuperAdmin: 'Super Admin',
    superAdminBadge: 'Panneau Super Admin',
    superAdminTitle: 'Gestion des comptes Admin',
    superAdminSubtitle: "Créez et gérez les comptes Admin. Connectez-vous en tant qu'Admin pour résoudre les problèmes en son nom.",
    superAdminAddAdmin: 'Ajouter un Admin',
    superAdminCreate: 'Créer',
    superAdminDelete: 'Supprimer',
    superAdminConfirmDelete: 'Confirmer la suppression',
    superAdminLoginAs: 'Se connecter avec ce compte',
    superAdminEmail: 'Adresse e-mail',
    superAdminPassword: 'Mot de passe',
    superAdminPasswordPlaceholder: 'Min. 8 car., majuscule, chiffre',
    superAdminCreateValidation: "L'e-mail et le mot de passe sont requis.",
    superAdminCreateError: "Impossible de créer l'admin.",
    superAdminDeleteError: "Impossible de supprimer l'admin.",
    superAdminImpersonateError: "Impossible d'usurper l'identité.",
    superAdminLoadError: 'Impossible de charger les admins.',
    superAdminCreatedToast: 'Compte Admin créé.',
    superAdminDeletedToast: 'Compte Admin supprimé.',
    superAdminImpersonateToast: 'Connecté en tant qu\'Admin.',
    superAdminEmpty: 'Aucun compte Admin. Ajoutez-en un pour commencer.',
    superAdminTotalAdmins: 'Total Admins',
    superAdminRoleLabel: 'Votre rôle',
    superAdminScopeLabel: 'Portée',
    superAdminScopeValue: 'Tous les espaces',
    refresh: 'Actualiser',
    navPrivacyPolicy: 'Politique de confidentialité',
    privacyTitle: 'Politique de confidentialité',
    privacyLastUpdated: 'Dernière mise à jour : mai 2026',
    privacyIntro: 'CoreTask est engagé à protéger vos données personnelles. Cette politique explique les informations que nous collectons, comment nous les utilisons et vos droits.',
    privacySec1Title: 'Informations collectées',
    privacySec1Body: "Nous collectons les informations que vous fournissez directement (nom, e-mail, contenu créé) ainsi que des données d'utilisation pour améliorer le service.",
    privacySec2Title: 'Utilisation de vos données',
    privacySec2Body: "Vos données servent uniquement à exploiter et améliorer CoreTask. Nous ne vendons ni ne louons vos informations à des tiers.",
    privacySec3Title: 'Stockage et sécurité',
    privacySec3Body: "Les données sont stockées sur des serveurs sécurisés avec chiffrement au repos et en transit. Les mots de passe sont hachés et jamais stockés en clair.",
    privacySec4Title: 'Cookies et stockage local',
    privacySec4Body: "CoreTask utilise le sessionStorage du navigateur pour maintenir votre session. Aucun cookie de suivi tiers n'est utilisé.",
    privacySec5Title: 'Vos droits',
    privacySec5Body: "Vous pouvez accéder, corriger ou supprimer vos données à tout moment. Contactez-nous via la page Contact pour exercer ces droits.",
    privacySec6Title: 'Modifications de cette politique',
    privacySec6Body: "Nous pouvons mettre à jour cette politique. Les utilisateurs seront informés des changements importants par e-mail ou notification intégrée.",
    privacyContact: 'Questions ? Contactez-nous à support@coretask.io ou via le formulaire de contact.',
    navContactMessages: 'Messages de contact',
    contactTitle: 'Messages de contact',
    contactSubtitle: 'Boîte de réception des messages soumis via le formulaire de contact.',
    contactNew: 'Nouveau',
    contactMarkRead: 'Marquer comme lu',
    contactReply: 'Répondre par e-mail',
    contactFilterAll: 'Tous',
    contactFilterUnread: 'Non lus',
    contactFilterRead: 'Lus',
    contactUnreadHint: 'non lus',
    contactEmpty: 'Aucun message pour le moment.',
    contactLoadError: 'Échec du chargement des messages.',
    contactMarkReadError: 'Impossible de marquer comme lu.',
    contactDeletedToast: 'Message supprimé.',
    contactDeleteError: 'Impossible de supprimer le message.',
  },
  zh: {
    navSuperAdmin: '超级管理员',
    superAdminBadge: '超级管理员面板',
    superAdminTitle: '管理员用户管理',
    superAdminSubtitle: '创建和管理管理员账户。以任意管理员身份登录以代其排查问题。',
    superAdminAddAdmin: '添加管理员',
    superAdminCreate: '创建',
    superAdminDelete: '删除',
    superAdminConfirmDelete: '确认删除',
    superAdminLoginAs: '以此账户登录',
    superAdminEmail: '电子邮件地址',
    superAdminPassword: '密码',
    superAdminPasswordPlaceholder: '至少8位，含大写字母和数字',
    superAdminCreateValidation: '电子邮件和密码为必填项。',
    superAdminCreateError: '创建管理员失败。',
    superAdminDeleteError: '删除管理员失败。',
    superAdminImpersonateError: '模拟用户身份失败。',
    superAdminLoadError: '加载管理员列表失败。',
    superAdminCreatedToast: '管理员账户已创建。',
    superAdminDeletedToast: '管理员账户已删除。',
    superAdminImpersonateToast: '已以管理员身份登录。',
    superAdminEmpty: '暂无管理员账户，请添加一个。',
    superAdminTotalAdmins: '管理员总数',
    superAdminRoleLabel: '您的角色',
    superAdminScopeLabel: '范围',
    superAdminScopeValue: '所有工作区',
    refresh: '刷新',
    navPrivacyPolicy: '隐私政策',
    privacyTitle: '隐私政策',
    privacyLastUpdated: '最后更新：2026年5月',
    privacyIntro: 'CoreTask 致力于保护您的个人数据。本隐私政策说明我们收集哪些信息、如何使用以及您的相关权利。',
    privacySec1Title: '我们收集的信息',
    privacySec1Body: '我们收集您直接提供的信息（姓名、电子邮件、创建的内容）以及使用数据以改进服务。',
    privacySec2Title: '信息使用方式',
    privacySec2Body: '您的数据仅用于运营和改进 CoreTask，我们不会向第三方出售或出租您的个人信息。',
    privacySec3Title: '数据存储与安全',
    privacySec3Body: '所有数据存储在加密的安全服务器上，密码经过哈希处理，从不以明文存储。',
    privacySec4Title: 'Cookie 与本地存储',
    privacySec4Body: 'CoreTask 使用浏览器会话存储维持登录会话，不使用第三方跟踪 Cookie。',
    privacySec5Title: '您的权利',
    privacySec5Body: '您可以随时访问、更正或删除您的个人数据。请通过联系页面或发送电子邮件行使这些权利。',
    privacySec6Title: '政策变更',
    privacySec6Body: '我们可能不时更新本政策，并通过电子邮件或应用内通知告知用户重大变更。',
    privacyContact: '有疑问？请发送邮件至 support@coretask.io 或使用联系表单。',
    navContactMessages: '联系消息',
    contactTitle: '联系消息',
    contactSubtitle: '通过联系表单提交的消息收件箱。',
    contactNew: '新',
    contactMarkRead: '标为已读',
    contactReply: '通过邮件回复',
    contactFilterAll: '全部',
    contactFilterUnread: '未读',
    contactFilterRead: '已读',
    contactUnreadHint: '未读',
    contactEmpty: '暂无消息。',
    contactLoadError: '加载消息失败。',
    contactMarkReadError: '标为已读失败。',
    contactDeletedToast: '消息已删除。',
    contactDeleteError: '删除消息失败。',
  },
  ur: {
    navSuperAdmin: 'سپر ایڈمن',
    superAdminBadge: 'سپر ایڈمن پینل',
    superAdminTitle: 'ایڈمن صارف کا انتظام',
    superAdminSubtitle: 'ایڈمن اکاؤنٹس بنائیں اور ان کا انتظام کریں۔ کسی بھی ایڈمن کی جانب سے مسائل حل کرنے کے لیے ان کے اکاؤنٹ سے لاگ ان کریں۔',
    superAdminAddAdmin: 'ایڈمن شامل کریں',
    superAdminCreate: 'بنائیں',
    superAdminDelete: 'حذف کریں',
    superAdminConfirmDelete: 'حذف کی تصدیق کریں',
    superAdminLoginAs: 'اس اکاؤنٹ سے لاگ ان کریں',
    superAdminEmail: 'ای میل ایڈریس',
    superAdminPassword: 'پاس ورڈ',
    superAdminPasswordPlaceholder: 'کم از کم 8 حروف، بڑا حرف، نمبر',
    superAdminCreateValidation: 'ای میل اور پاس ورڈ ضروری ہیں۔',
    superAdminCreateError: 'ایڈمن بنانے میں ناکامی۔',
    superAdminDeleteError: 'ایڈمن حذف کرنے میں ناکامی۔',
    superAdminImpersonateError: 'صارف کی شناخت اختیار کرنے میں ناکامی۔',
    superAdminLoadError: 'ایڈمن فہرست لوڈ کرنے میں ناکامی۔',
    superAdminCreatedToast: 'ایڈمن اکاؤنٹ بنایا گیا۔',
    superAdminDeletedToast: 'ایڈمن اکاؤنٹ حذف کر دیا گیا۔',
    superAdminImpersonateToast: 'ایڈمن کے طور پر لاگ ان ہو گئے۔',
    superAdminEmpty: 'ابھی کوئی ایڈمن اکاؤنٹ نہیں۔ شروع کرنے کے لیے ایک شامل کریں۔',
    superAdminTotalAdmins: 'کل ایڈمنز',
    superAdminRoleLabel: 'آپ کا کردار',
    superAdminScopeLabel: 'دائرہ کار',
    superAdminScopeValue: 'تمام ورک اسپیسز',
    refresh: 'تازہ کریں',
    navPrivacyPolicy: 'رازداری کی پالیسی',
    privacyTitle: 'رازداری کی پالیسی',
    privacyLastUpdated: 'آخری اپ ڈیٹ: مئی 2026',
    privacyIntro: 'CoreTask آپ کے ذاتی ڈیٹا کی حفاظت کے لیے پرعزم ہے۔ یہ پالیسی بتاتی ہے کہ ہم کون سی معلومات اکٹھی کرتے ہیں اور آپ کے حقوق کیا ہیں۔',
    privacySec1Title: 'ہم کون سی معلومات اکٹھی کرتے ہیں',
    privacySec1Body: 'ہم آپ کا نام، ای میل اور پلیٹ فارم پر بنایا گیا مواد اکٹھا کرتے ہیں، نیز سروس بہتر بنانے کے لیے استعمال کا ڈیٹا بھی۔',
    privacySec2Title: 'آپ کی معلومات کا استعمال',
    privacySec2Body: 'آپ کا ڈیٹا صرف CoreTask چلانے اور بہتر بنانے کے لیے استعمال ہوتا ہے۔ ہم اسے کسی تیسرے فریق کو فروخت یا کرائے پر نہیں دیتے۔',
    privacySec3Title: 'ڈیٹا اسٹوریج اور سیکیورٹی',
    privacySec3Body: 'تمام ڈیٹا محفوظ سرورز پر انکرپٹ کیا جاتا ہے۔ پاس ورڈ ہیش کیے جاتے ہیں اور سادہ متن میں ہرگز محفوظ نہیں ہوتے۔',
    privacySec4Title: 'کوکیز اور لوکل اسٹوریج',
    privacySec4Body: 'CoreTask لاگ ان سیشن برقرار رکھنے کے لیے براؤزر سیشن اسٹوریج استعمال کرتا ہے۔ کوئی تھرڈ پارٹی ٹریکنگ کوکی استعمال نہیں ہوتی۔',
    privacySec5Title: 'آپ کے حقوق',
    privacySec5Body: 'آپ کسی بھی وقت اپنا ذاتی ڈیٹا دیکھ، درست یا حذف کر سکتے ہیں۔ ان حقوق کے لیے ہم سے رابطہ صفحے کے ذریعے رابطہ کریں۔',
    privacySec6Title: 'اس پالیسی میں تبدیلیاں',
    privacySec6Body: 'ہم وقتاً فوقتاً اس پالیسی کو اپ ڈیٹ کر سکتے ہیں۔ اہم تبدیلیوں کی اطلاع ای میل یا ان ایپ نوٹیفیکیشن سے دی جائے گی۔',
    privacyContact: 'سوالات؟ support@coretask.io پر یا رابطہ فارم کے ذریعے ہم سے رابطہ کریں۔',
    navContactMessages: 'رابطہ پیغامات',
    contactTitle: 'رابطہ پیغامات',
    contactSubtitle: 'رابطہ فارم کے ذریعے موصول پیغامات کا ان باکس۔',
    contactNew: 'نیا',
    contactMarkRead: 'پڑھا ہوا نشان کریں',
    contactReply: 'ای میل سے جواب دیں',
    contactFilterAll: 'سب',
    contactFilterUnread: 'نہ پڑھا',
    contactFilterRead: 'پڑھا ہوا',
    contactUnreadHint: 'نہ پڑھا',
    contactEmpty: 'ابھی کوئی پیغام نہیں۔',
    contactLoadError: 'پیغامات لوڈ کرنے میں ناکامی۔',
    contactMarkReadError: 'پڑھا ہوا نشان کرنے میں ناکامی۔',
    contactDeletedToast: 'پیغام حذف ہو گیا۔',
    contactDeleteError: 'پیغام حذف کرنے میں ناکامی۔',
  },
};

Object.entries(stepFiveTranslations).forEach(([language, entries]) => {
  translations[language] = { ...(translations[language] || {}), ...entries };
});

const supportedLocales = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl' },
  { code: 'fr', label: 'French', nativeLabel: 'Français', dir: 'ltr' },
  { code: 'zh', label: 'Chinese', nativeLabel: '中文', dir: 'ltr' },
  { code: 'ur', label: 'Urdu', nativeLabel: 'اردو', dir: 'rtl' },
];

const LANGUAGE_STORAGE_KEY = 'coretask_language';

function getLocaleMetadata(language) {
  return supportedLocales.find((locale) => locale.code === language) || supportedLocales[0];
}

function normalizeLanguage(language) {
  return getLocaleMetadata(language).code;
}

function useLocale() {
  return useContext(LocaleContext);
}

const statusTranslationKeys = {
  All: 'filterAll',
  Active: 'statusActive',
  Completed: 'statusCompleted',
  'On Hold': 'statusOnHold',
};

const priorityTranslationKeys = {
  Low: 'priorityLow',
  Medium: 'priorityMedium',
  High: 'priorityHigh',
  Critical: 'priorityCritical',
};

const projectViewTranslationKeys = {
  board: 'viewBoard',
  list: 'viewList',
  channel: 'viewChannel',
  timeline: 'viewTimeline',
};

const columnTranslationKeys = {
  Backlog: 'columnBacklog',
  'To Do': 'columnToDo',
  'In Progress': 'columnInProgress',
  'In Review': 'columnInReview',
  Done: 'columnDone',
};

function translatedStatus(t, status) {
  return t(statusTranslationKeys[status] || status);
}

function translatedPriority(t, priority) {
  return t(priorityTranslationKeys[priority] || priority);
}

function translatedColumnName(t, columnName) {
  return t(columnTranslationKeys[columnName] || columnName);
}

const navItems = [
  { id: 'dashboard', labelKey: 'navDashboard', icon: LayoutDashboard },
  { id: 'projects', labelKey: 'navProjects', icon: FolderKanban },
  { id: 'ai-tasker', labelKey: 'navAITasker', icon: Sparkles },
  { id: 'ai-assistance', labelKey: 'navAIAssistance', icon: MessageCircle },
  { id: 'notes', labelKey: 'navNotes', icon: NotebookPen },
  { id: 'team', labelKey: 'navTeam', icon: Users },
  { id: 'messages', labelKey: 'navMessages', icon: MessageSquare },
  { id: 'notifications', labelKey: 'navNotifications', icon: Bell },
  { id: 'settings', labelKey: 'navSettings', icon: Settings },
  { id: 'profile', labelKey: 'navProfile', icon: UserCircle2 },
];

const teamMembers = [
  { id: 'u1', name: 'Ava Johnson' },
  { id: 'u2', name: 'Noah Smith' },
  { id: 'u3', name: 'Mia Patel' },
  { id: 'u4', name: 'Liam Chen' },
  { id: 'u5', name: 'Emma Davis' },
  { id: 'u6', name: 'Lucas Brown' },
];

const seedProjects = [
  {
    id: 'p1',
    name: 'Core Platform Revamp',
    description: 'Modernize API contracts and improve team delivery workflows.',
    status: 'Active',
    progress: 72,
    taskCount: 36,
    members: ['u1', 'u2', 'u3'],
    color: '#1A5FB4',
  },
  {
    id: 'p2',
    name: 'Mobile Experience Refresh',
    description: 'Redesign key mobile screens and increase retention conversion.',
    status: 'On Hold',
    progress: 41,
    taskCount: 22,
    members: ['u4', 'u5'],
    color: '#D49A24',
  },
  {
    id: 'p3',
    name: 'Payment Reliability',
    description: 'Reduce failed transactions and add observability checkpoints.',
    status: 'Active',
    progress: 58,
    taskCount: 28,
    members: ['u2', 'u6', 'u5'],
    color: '#1A5FB4',
  },
  {
    id: 'p4',
    name: 'Design System 2.0',
    description: 'Unify component tokens and improve accessibility coverage.',
    status: 'Completed',
    progress: 100,
    taskCount: 19,
    members: ['u1', 'u4'],
    color: '#D49A24',
  },
  {
    id: 'p5',
    name: 'AI Assistant Integration',
    description: 'Ship an intelligent helper for issue triage and summarization.',
    status: 'Active',
    progress: 64,
    taskCount: 31,
    members: ['u3', 'u5', 'u6'],
    color: '#1A5FB4',
  },
  {
    id: 'p6',
    name: 'Compliance Automation',
    description: 'Automate policy checks and monthly reporting workflow.',
    status: 'On Hold',
    progress: 33,
    taskCount: 17,
    members: ['u2', 'u4'],
    color: '#D49A24',
  },
];

const statusStyles = {
  Active:
    'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700/50',
  'On Hold':
    'bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-700/50',
  Completed:
    'bg-blue-100 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700/50',
};

const priorityStyles = {
  Critical: 'bg-pink-100 text-pink-700 ring-1 ring-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:ring-pink-700/50',
  High:     'bg-pink-100 text-pink-600 ring-1 ring-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:ring-pink-700/50',
  Medium:   'bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-700/50',
  Low:      'bg-green-100 text-green-700 ring-1 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-700/50',
};

const boardColumnSeed = [
  { id: 'backlog',    name: 'Backlog',     tone: 'bg-slate-500'  },
  { id: 'todo',       name: 'To Do',       tone: 'bg-brand-500'  },
  { id: 'inprogress', name: 'In Progress', tone: 'bg-amber-500'  },
  { id: 'inreview',   name: 'In Review',   tone: 'bg-pink-500'   },
  { id: 'done',       name: 'Done',        tone: 'bg-green-500'  },
];

const boardTaskSeed = [
  {
    id: 't1',
    title: 'Map legacy API endpoints',
    priority: 'High',
    assigneeId: 'u2',
    dueDate: 'May 14',
    subtaskDone: 2,
    subtaskTotal: 5,
    category: 'Backend',
    hasAttachment: true,
    columnId: 'backlog',
  },
  {
    id: 't2',
    title: 'Design sprint planning template',
    priority: 'Medium',
    assigneeId: 'u1',
    dueDate: 'May 18',
    subtaskDone: 4,
    subtaskTotal: 6,
    category: 'Design',
    hasAttachment: false,
    columnId: 'todo',
  },
  {
    id: 't3',
    title: 'Implement webhook retries',
    priority: 'Critical',
    assigneeId: 'u6',
    dueDate: 'May 12',
    subtaskDone: 1,
    subtaskTotal: 4,
    category: 'DevOps',
    hasAttachment: true,
    columnId: 'inprogress',
  },
  {
    id: 't4',
    title: 'Cross-browser QA checklist',
    priority: 'Low',
    assigneeId: 'u5',
    dueDate: 'May 21',
    subtaskDone: 5,
    subtaskTotal: 5,
    category: 'QA',
    hasAttachment: false,
    columnId: 'inreview',
  },
  {
    id: 't5',
    title: 'Update release notes draft',
    priority: 'Medium',
    assigneeId: 'u3',
    dueDate: 'May 10',
    subtaskDone: 3,
    subtaskTotal: 3,
    category: 'Docs',
    hasAttachment: false,
    columnId: 'done',
  },
  {
    id: 't6',
    title: 'Refactor auth middleware',
    priority: 'High',
    assigneeId: 'u4',
    dueDate: 'May 16',
    subtaskDone: 2,
    subtaskTotal: 7,
    category: 'Security',
    hasAttachment: true,
    columnId: 'todo',
  },
];

const boardColumnTones = boardColumnSeed.map((column) => column.tone);

function mapApiBoardToKanban(apiBoard) {
  const rawCols = apiBoard.columns ?? apiBoard.Columns ?? [];
  const readOnly = Boolean(apiBoard.readOnly ?? apiBoard.ReadOnly);
  const projectNumber = String(apiBoard.projectNumber ?? apiBoard.ProjectNumber ?? '');
  const columns = rawCols.map((col, index) => ({
    id: String(col.id ?? col.Id),
    name: col.title ?? col.Title,
    tone: boardColumnTones[index % boardColumnTones.length],
  }));
  const tasks = [];
  rawCols.forEach((col) => {
    const cid = String(col.id ?? col.Id);
    const items = col.workItems ?? col.WorkItems ?? [];
    items.forEach((w) => {
      const due = w.dueDateUtc ?? w.DueDateUtc;
      const attachmentCount = Number(w.attachmentCount ?? w.AttachmentCount ?? 0) || 0;
      tasks.push({
        id: String(w.id ?? w.Id),
        taskNumber: String(w.taskNumber ?? w.TaskNumber ?? ''),
        title: w.title ?? w.Title,
        description: w.description ?? w.Description ?? '',
        priority: w.priority ?? w.Priority ?? 'Medium',
        assigneeId: w.assigneeUserId ?? w.AssigneeUserId ?? '',
        assigneeUserId: w.assigneeUserId ?? w.AssigneeUserId ?? '',
        dueDate: due
          ? new Date(due).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : '—',
        dueDateIso: due ? new Date(due).toISOString().slice(0, 10) : '',
        subtaskDone: 0,
        subtaskTotal: 1,
        category: 'General',
        hasAttachment: attachmentCount > 0,
        attachmentCount,
        labels: [],
        columnId: cid,
        timelineStart: 1,
        timelineDuration: 3,
        dependencies: [],
      });
    });
  });
  return { columns, tasks, readOnly, projectNumber };
}

function mapApiProjectSummaryToCard(apiRow) {
  return {
    id: String(apiRow.id ?? apiRow.Id),
    projectNumber: String(apiRow.projectNumber ?? apiRow.ProjectNumber ?? ''),
    name: apiRow.name ?? apiRow.Name,
    description: (apiRow.description ?? apiRow.Description) || '',
    readOnly: Boolean(apiRow.readOnly ?? apiRow.ReadOnly),
    status: 'Active',
    progress: 0,
    taskCount: 0,
    members: [],
    color: '#1A5FB4',
  };
}

const defaultCategories = [
  { id: 'cat-bug', name: 'Bug', color: '#EF4444', icon: '🐞', taskCount: 8 },
  { id: 'cat-feature', name: 'Feature', color: '#1A5FB4', icon: '✨', taskCount: 12 },
  { id: 'cat-enhancement', name: 'Enhancement', color: '#0EA5E9', icon: '⚡', taskCount: 6 },
  { id: 'cat-design', name: 'Design', color: '#EC4899', icon: '🎨', taskCount: 4 },
  { id: 'cat-backend', name: 'Backend', color: '#14B8A6', icon: '🛠', taskCount: 10 },
  { id: 'cat-frontend', name: 'Frontend', color: '#6366F1', icon: '🧩', taskCount: 9 },
  { id: 'cat-devops', name: 'DevOps', color: '#F97316', icon: '🚀', taskCount: 5 },
  { id: 'cat-research', name: 'Research', color: '#64748B', icon: '🔎', taskCount: 3 },
];

const defaultLabels = [
  { id: 'lbl-urgent', name: 'urgent', color: '#EF4444' },
  { id: 'lbl-ui', name: 'ui', color: '#A855F7' },
  { id: 'lbl-api', name: 'api', color: '#06B6D4' },
  { id: 'lbl-qa', name: 'qa', color: '#10B981' },
  { id: 'lbl-refactor', name: 'refactor', color: '#F59E0B' },
];

const teamMemberDirectorySeed = [
  {
    id: 'u1',
    name: 'Ava Johnson',
    role: 'Admin',
    email: 'ava@coretask.dev',
    online: true,
    activeProjects: 4,
    taskCount: 12,
    joinedDate: '2024-02-12',
    assignedTasks: ['Core Platform Revamp - Sprint Planning', 'Design System 2.0 - Button Audit'],
    activity: [4, 6, 5, 7, 8],
  },
  {
    id: 'u2',
    name: 'Noah Smith',
    role: 'Manager',
    email: 'noah@coretask.dev',
    online: true,
    activeProjects: 5,
    taskCount: 17,
    joinedDate: '2023-10-03',
    assignedTasks: ['Payment Reliability - API Mapping', 'Compliance Automation - Policy Review'],
    activity: [3, 5, 6, 5, 7],
  },
  {
    id: 'u3',
    name: 'Mia Patel',
    role: 'Developer',
    email: 'mia@coretask.dev',
    online: false,
    activeProjects: 3,
    taskCount: 10,
    joinedDate: '2024-06-18',
    assignedTasks: ['AI Assistant Integration - Prompt UX', 'Release Notes Draft'],
    activity: [2, 4, 5, 6, 5],
  },
  {
    id: 'u4',
    name: 'Liam Chen',
    role: 'Designer',
    email: 'liam@coretask.dev',
    online: true,
    activeProjects: 2,
    taskCount: 8,
    joinedDate: '2025-01-09',
    assignedTasks: ['Mobile Experience Refresh - Visual QA', 'Design System 2.0 - Token Review'],
    activity: [5, 4, 3, 5, 6],
  },
  {
    id: 'u5',
    name: 'Emma Davis',
    role: 'Developer',
    email: 'emma@coretask.dev',
    online: false,
    activeProjects: 4,
    taskCount: 11,
    joinedDate: '2024-08-22',
    assignedTasks: ['Cross-browser QA Checklist', 'Auth Middleware Refactor'],
    activity: [4, 3, 4, 6, 7],
  },
  {
    id: 'u6',
    name: 'Lucas Brown',
    role: 'Viewer',
    email: 'lucas@coretask.dev',
    online: true,
    activeProjects: 1,
    taskCount: 4,
    joinedDate: '2025-03-15',
    assignedTasks: ['Observability Dashboard Feedback'],
    activity: [1, 2, 3, 2, 4],
  },
];

function Sidebar({ activeView, onChangeView, expanded, onToggle, isSuperAdmin }) {
  const { t, dir } = useLocale();
  const superAdminNavItems = [
    { id: 'super-admin',       labelKey: 'navSuperAdmin',       icon: ShieldCheck },
    { id: 'contact-messages',  labelKey: 'navContactMessages',  icon: MailOpen    },
    { id: 'privacy-policy',    labelKey: 'navPrivacyPolicy',    icon: FileText    },
    { id: 'settings',          labelKey: 'navSettings',         icon: Settings    },
  ];
  const allNavItems = isSuperAdmin ? superAdminNavItems : navItems;
  /* cycle logo colors per nav-item index for active indicator */
  const ITEM_COLORS = [
    'bg-brand-500 shadow-brand',   // blue
    'bg-green-500 shadow-green',   // green
    'bg-pink-500 shadow-pink',     // pink
    'bg-amber-500 shadow-amber',   // amber
  ];

  return (
    <aside
      className={`fixed top-0 z-20 h-screen bg-[#1A1A1A] transition-all duration-300 ${
        dir === 'rtl' ? 'right-0' : 'left-0'
      } ${expanded ? 'w-[260px]' : 'w-16'}`}
    >
      {/* header */}
      <div
        className={`flex items-center border-b border-white/10 ${
          expanded ? 'h-16 justify-between px-3' : 'flex-col gap-2 py-3'
        }`}
      >
        <button
          type="button"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white/70 transition hover:bg-white/10 hover:text-white"
          onClick={onToggle}
        >
          <Menu size={20} />
        </button>
        <div className={`flex min-w-0 items-center gap-2 ${expanded ? 'flex-1 justify-end' : 'justify-center'}`}>
          <img
            src={BRAND_LOGO_SRC}
            alt=""
            className={`shrink-0 rounded-xl object-contain ${expanded ? 'h-9 w-9' : 'h-8 w-8'}`}
            width={expanded ? 36 : 32}
            height={expanded ? 36 : 32}
          />
          {expanded && (
            <span className="truncate text-base font-bold text-white">
              {t('appName')}
            </span>
          )}
        </div>
      </div>

      {/* nav */}
      <nav className="space-y-1 px-2 py-4">
        {allNavItems.map((item, idx) => {
          const Icon = item.icon;
          const label = t(item.labelKey);
          const isActive = activeView === item.id;
          const activeColor = ITEM_COLORS[idx % ITEM_COLORS.length];
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChangeView(item.id)}
              className={`group flex w-full items-center rounded-xl px-3 py-2.5 text-left transition ${
                isActive
                  ? `${activeColor} text-white shadow-lg`
                  : 'text-white/60 hover:bg-white/10 hover:text-white'
              }`}
              title={label}
            >
              <Icon size={20} className="shrink-0" />
              {expanded && <span className="ms-3 text-sm font-medium">{label}</span>}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

function TopNavbar({
  expanded,
  onToggleTheme,
  isDarkMode,
  onOpenNotifications,
  unreadCount,
  onLogout,
  userName,
  userAvatarUrl,
}) {
  const { t, dir, language, setLanguage, supportedLocales } = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      className={`fixed top-0 z-10 h-16 border-b border-brand-100 bg-white px-4 shadow-sm transition-all duration-300 dark:border-white/10 dark:bg-[#1A1A1A] md:px-6 ${
        dir === 'rtl'
          ? `${expanded ? 'right-[260px]' : 'right-16'} left-0`
          : `${expanded ? 'left-[260px]' : 'left-16'} right-0`
      }`}
    >
      <div className="flex h-full items-center justify-between gap-4">
        {/* Search */}
        <div className="flex min-w-0 flex-1 items-center gap-3 md:gap-4">
          <div className="relative w-full max-w-xl">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-brand-400 focus:ring-4 focus:ring-brand-100 dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Language */}
          <div className="relative inline-flex h-9 items-center gap-1 rounded-xl border border-slate-200 bg-white px-2 text-slate-600 transition hover:border-brand-300 hover:bg-brand-50 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
            <Globe size={14} className="shrink-0 text-brand-500" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              title={t('language')}
              className="cursor-pointer appearance-none bg-transparent py-1 text-xs font-medium outline-none dark:text-white/70"
            >
              {supportedLocales.map((locale) => (
                <option key={locale.code} value={locale.code}>{locale.nativeLabel}</option>
              ))}
            </select>
          </div>

          {/* Theme toggle */}
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-amber-500 transition hover:border-amber-300 hover:bg-amber-50 dark:border-white/10 dark:bg-white/5 dark:text-amber-400"
            onClick={onToggleTheme}
            title={t('toggleDarkMode')}
          >
            {isDarkMode ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          {/* Notifications */}
          <button
            type="button"
            onClick={onOpenNotifications}
            className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-pink-500 transition hover:border-pink-300 hover:bg-pink-50 dark:border-white/10 dark:bg-white/5 dark:text-pink-400"
            title={t('notifications')}
          >
            <Bell size={17} />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-pink-500 px-1 text-[10px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1.5 transition hover:border-brand-300 hover:bg-brand-50 dark:border-white/10 dark:bg-white/5"
            >
              <UserAvatar name={userName} imageUrl={userAvatarUrl} sizeClass="h-7 w-7" />
              <ChevronDown size={14} className="text-slate-400 dark:text-white/40" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-[#1A1A1A]">
                <div className="border-b border-slate-100 px-3 py-2.5 dark:border-white/10">
                  <p className="text-[11px] text-slate-400 dark:text-white/40">{t('signedInAs')}</p>
                  <p className="truncate text-xs font-semibold text-slate-700 dark:text-white/80">{userName}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onLogout(); }}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-xs font-medium text-pink-600 transition hover:bg-pink-50 dark:text-pink-400 dark:hover:bg-pink-500/10"
                >
                  <LogOut size={13} />
                  {t('logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function ViewPlaceholder({ title, description }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">{title}</h2>
      <p className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-300">{description}</p>
      <div className="mt-6 h-48 rounded-xl border border-dashed border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-950/30" />
    </section>
  );
}

function EmptyState({ icon, title, description, ctaLabel, onCta }) {
  return (
    <div className="rounded-2xl border border-dashed border-brand-300 bg-brand-50/60 p-6 text-center dark:border-brand-900 dark:bg-brand-900/10">
      <div className="text-3xl">{icon}</div>
      <h3 className="mt-2 text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">{description}</p>
      {ctaLabel && (
        <button
          type="button"
          onClick={onCta}
          className="mt-3 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-2xl border border-slate-200 bg-white/80 p-5 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="h-4 w-1/3 rounded bg-slate-200 dark:bg-slate-700" />
      <div className="mt-3 h-3 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
      <div className="mt-5 h-2 w-full rounded bg-slate-200 dark:bg-slate-700" />
      <div className="mt-4 h-8 w-full rounded bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

function StatCard({ title, value, icon: Icon }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-300">{title}</p>
        <div className="rounded-lg bg-brand-100 p-2 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
          <Icon size={18} />
        </div>
      </div>
      <p className="mt-4 text-2xl font-semibold text-slate-800 dark:text-white">{value}</p>
    </article>
  );
}

function MemberAvatarStack({ memberIds }) {
  const members = memberIds
    .map((id) => teamMembers.find((member) => member.id === id))
    .filter(Boolean);

  return (
    <div className="flex items-center">
      {members.map((member, index) => (
        <UserAvatar
          key={member.id}
          name={member.name}
          imageUrl={member.avatarUrl ?? member.avatar ?? null}
          sizeClass="-ml-2 first:ml-0 h-8 w-8 ring-2 ring-white dark:ring-slate-900"
          className=""
          title={member.name}
          style={{
            zIndex: members.length - index,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Edit Project Modal ─────────────────────────────────────── */
function EditProjectModal({ project, onClose, onUpdated }) {
  const { t } = useLocale();
  const [name, setName] = useState(project.name || '');
  const [description, setDescription] = useState(project.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!name.trim()) { setError(t('projectName') + ' is required.'); return; }
    setSaving(true); setError('');
    try {
      const updated = await apiUpdateProject(project.id, {
        name: name.trim(),
        description: description.trim() || '',
      });
      onUpdated(updated);
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to update project.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <span
              className="h-4 w-4 rounded-full flex-shrink-0"
              style={{ backgroundColor: project.color || '#1A5FB4' }}
            />
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
              {t('notesEdit')} — {project.projectNumber || project.name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* body */}
        <div className="px-6 py-5 space-y-4">
          <label className="block text-sm font-medium text-slate-600 dark:text-slate-300">
            {t('projectName')}
            <input
              className="mt-1.5 w-full rounded-xl border border-brand-200 dark:border-brand-900 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-800 dark:text-white outline-none ring-brand-300 focus:ring"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('projectName')}
            />
          </label>

          <label className="block text-sm font-medium text-slate-600 dark:text-slate-300">
            {t('description')}
            <textarea
              rows={4}
              className="mt-1.5 w-full rounded-xl border border-brand-200 dark:border-brand-900 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-800 dark:text-white outline-none ring-brand-300 focus:ring resize-none"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('description')}
            />
          </label>

          {error && (
            <p className="rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300">
              {error}
            </p>
          )}
        </div>

        {/* footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-brand-200 dark:border-brand-900 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-xl bg-brand-500 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-brand hover:opacity-95 disabled:opacity-60 transition-opacity"
          >
            {saving ? '…' : t('saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewProjectModal({ onClose, onCreate }) {
  const { t } = useLocale();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#1A5FB4');
  const [selectedMembers, setSelectedMembers] = useState(['u1', 'u2']);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [sampleDropOpen, setSampleDropOpen] = useState(false);

  const toggleMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    setSubmissionError('');
    try {
      await onCreate({
        id: `p${Date.now()}`,
        name: name.trim(),
        description:
          description.trim() || t('projectDefaultDescription'),
        status: 'Active',
        progress: 8,
        taskCount: 0,
        members: selectedMembers.length > 0 ? selectedMembers : ['u1'],
        color,
        startDate,
        endDate,
      });
      onClose();
    } catch (err) {
      setSubmissionError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/50 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl rounded-2xl border border-brand-200/70 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-slate-800 dark:text-white">{t('createNewProject')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {t('close')}
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="col-span-2 text-sm text-slate-600 dark:text-slate-300">
            {t('projectName')}
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              className="mt-1.5 w-full rounded-xl border border-brand-200 bg-white px-3 py-2 outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
            />
          </label>

          <label className="col-span-2 text-sm text-slate-600 dark:text-slate-300">
            {t('description')}
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              className="mt-1.5 w-full rounded-xl border border-brand-200 bg-white px-3 py-2 outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
            />
          </label>

          <label className="text-sm text-slate-600 dark:text-slate-300">
            {t('colorPicker')}
            <input
              type="color"
              value={color}
              onChange={(event) => setColor(event.target.value)}
              className="mt-1.5 h-10 w-full cursor-pointer rounded-lg border border-brand-200 bg-white p-1 dark:border-brand-900 dark:bg-slate-950"
            />
          </label>

          <div className="text-sm text-slate-600 dark:text-slate-300">
            {t('membersMultiSelect')}
            <div className="mt-1.5 max-h-28 space-y-1 overflow-y-auto rounded-xl border border-brand-200 p-2 dark:border-brand-900">
              {teamMembers.map((member) => (
                <label
                  key={member.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                >
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => toggleMember(member.id)}
                  />
                  <span>{member.name}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="text-sm text-slate-600 dark:text-slate-300">
            {t('startDate')}
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-brand-200 bg-white px-3 py-2 outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
            />
          </label>

          <label className="text-sm text-slate-600 dark:text-slate-300">
            {t('endDate')}
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-brand-200 bg-white px-3 py-2 outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
            />
          </label>
        </div>

        {submissionError && (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
            {submissionError}
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          {/* Fill sample data dropdown */}
          <div className="relative me-auto">
            <button
              type="button"
              onClick={() => setSampleDropOpen((v) => !v)}
              className="flex items-center gap-1.5 rounded-xl border border-dashed border-brand-300 px-4 py-2 text-sm font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/60"
            >
              {t('fillSampleData')}
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={sampleDropOpen ? 'rotate-180 transition-transform' : 'transition-transform'}><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {sampleDropOpen && (
              <div className="absolute bottom-full mb-2 start-0 z-20 w-72 overflow-hidden rounded-xl border border-brand-200 bg-white shadow-xl dark:border-brand-900 dark:bg-slate-900">
                {sampleProjectPresets(teamMembers.map((m) => m.id)).map((s, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setName(s.name);
                      setDescription(s.description);
                      setColor(s.color);
                      setStartDate(s.startDate);
                      setEndDate(s.endDate);
                      setSelectedMembers(s.selectedMembers);
                      setSubmissionError('');
                      setSampleDropOpen(false);
                    }}
                    className="w-full border-b border-brand-100 px-4 py-3 text-start last:border-0 hover:bg-brand-50 dark:border-brand-800 dark:hover:bg-brand-900/40"
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                      <span className="text-sm font-semibold text-slate-800 dark:text-white">{s.label}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">{s.description}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-brand-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-brand-900 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand transition hover:opacity-95"
          >
            {t('createProject')}
          </button>
        </div>
      </form>
    </div>
  );
}

function CategoryLabelManagerModal({
  onClose,
  categories,
  setCategories,
  labels,
  setLabels,
}) {
  const { t } = useLocale();
  const [activeTab, setActiveTab] = useState('categories');
  const [searchQuery, setSearchQuery] = useState('');
  const [newCategory, setNewCategory] = useState({ name: '', color: '#1A5FB4', icon: '🧩' });
  const [newLabel, setNewLabel] = useState({ name: '', color: '#D49A24' });

  const filteredCategories = categories.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredLabels = labels.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-4xl rounded-2xl border border-brand-200 bg-white p-4 shadow-2xl dark:border-brand-900 dark:bg-slate-900">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
            {t('categoriesLabels')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-brand-50 dark:text-slate-300 dark:hover:bg-brand-900/20"
          >
            {t('close')}
          </button>
        </div>

        <div className="mb-3 flex items-center gap-2">
          {[
            { id: 'categories', label: t('categories') },
            { id: 'labels', label: t('labels') },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                activeTab === tab.id
                  ? 'bg-brand-500 text-white'
                  : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={activeTab === 'categories' ? t('searchCategories') : t('searchLabels')}
            className="ml-auto rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
          />
        </div>

        {activeTab === 'categories' ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredCategories.map((category) => (
                <div
                  key={category.id}
                  className="rounded-xl border border-brand-200 p-3 dark:border-brand-900"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{category.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                          {category.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-300">
                          {category.taskCount} {t('taskCountLabel')}
                        </p>
                      </div>
                    </div>
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const name = window.prompt(t('renameCategory'), category.name);
                        if (!name || !name.trim()) return;
                        setCategories((prev) =>
                          prev.map((item) =>
                            item.id === category.id ? { ...item, name: name.trim() } : item
                          )
                        );
                      }}
                      className="rounded-md border border-brand-200 px-2 py-1 text-[11px] dark:border-brand-900"
                    >
                      {t('edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCategories((prev) => prev.filter((item) => item.id !== category.id))
                      }
                      className="rounded-md border border-brand-200 px-2 py-1 text-[11px] dark:border-brand-900"
                    >
                      {t('delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-xl border border-brand-200 p-3 dark:border-brand-900">
              <p className="mb-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                {t('newCategory')}
              </p>
              <div className="grid gap-2 md:grid-cols-3">
                <input
                  value={newCategory.name}
                  onChange={(event) =>
                    setNewCategory((prev) => ({ ...prev, name: event.target.value }))
                  }
                  placeholder={t('categoryName')}
                  className="rounded-lg border border-brand-200 px-2.5 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-950"
                />
                <input
                  type="color"
                  value={newCategory.color}
                  onChange={(event) =>
                    setNewCategory((prev) => ({ ...prev, color: event.target.value }))
                  }
                  className="h-8 rounded-lg border border-brand-200 p-1 dark:border-brand-900 dark:bg-slate-950"
                />
                <input
                  value={newCategory.icon}
                  onChange={(event) =>
                    setNewCategory((prev) => ({ ...prev, icon: event.target.value || '🧩' }))
                  }
                  placeholder={t('iconOrEmoji')}
                  className="rounded-lg border border-brand-200 px-2.5 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-950"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setNewCategory({ ...SAMPLE_CATEGORY_DRAFT })}
                  className="rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
                >
                  {t('fillSampleData')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!newCategory.name.trim()) return;
                    setCategories((prev) => [
                      ...prev,
                      {
                        id: `cat-${Date.now()}`,
                        name: newCategory.name.trim(),
                        color: newCategory.color,
                        icon: newCategory.icon || '🧩',
                        taskCount: 0,
                      },
                    ]);
                    setNewCategory({ name: '', color: '#1A5FB4', icon: '🧩' });
                  }}
                  className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  {t('createCategory')}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {filteredLabels.map((label) => (
                <div
                  key={label.id}
                  className="inline-flex items-center gap-2 rounded-full border border-brand-200 px-2 py-1 dark:border-brand-900"
                >
                  <span
                    className="rounded-full px-2 py-0.5 text-[11px] text-white"
                    style={{ backgroundColor: label.color }}
                  >
                    {label.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setLabels((prev) => prev.filter((item) => item.id !== label.id))}
                    className="text-[11px] text-slate-500 dark:text-slate-300"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-xl border border-brand-200 p-3 dark:border-brand-900">
              <p className="mb-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                {t('newLabel')}
              </p>
              <div className="grid gap-2 md:grid-cols-2">
                <input
                  value={newLabel.name}
                  onChange={(event) =>
                    setNewLabel((prev) => ({ ...prev, name: event.target.value }))
                  }
                  placeholder={t('labelName')}
                  className="rounded-lg border border-brand-200 px-2.5 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-950"
                />
                <input
                  type="color"
                  value={newLabel.color}
                  onChange={(event) =>
                    setNewLabel((prev) => ({ ...prev, color: event.target.value }))
                  }
                  className="h-8 rounded-lg border border-brand-200 p-1 dark:border-brand-900 dark:bg-slate-950"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setNewLabel(sampleLabelDraft())}
                  className="rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
                >
                  {t('fillSampleData')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!newLabel.name.trim()) return;
                    setLabels((prev) => [
                      ...prev,
                      {
                        id: `lbl-${Date.now()}`,
                        name: newLabel.name.trim(),
                        color: newLabel.color,
                      },
                    ]);
                    setNewLabel({ name: '', color: '#D49A24' });
                  }}
                  className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  {t('createLabel')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TaskDetailDrawerLocal({
  task,
  project,
  columns,
  categories,
  labels,
  onOpenCategoryManager,
  showManageCategoriesShortcut = true,
  onClose,
  onUpdateTask,
}) {
  const { t } = useLocale();
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');
  const [draftSubtask, setDraftSubtask] = useState('');
  const [subtasks, setSubtasks] = useState(
    Array.from({ length: task.subtaskTotal }).map((_, i) => ({
      id: `${task.id}-sub-${i + 1}`,
      title: `Subtask ${i + 1} for ${task.title}`,
      done: i < task.subtaskDone,
      assigneeId: task.assigneeId,
      status: i < task.subtaskDone ? 'Done' : 'Open',
    }))
  );

  const assignee = teamMembers.find((member) => member.id === task.assigneeId);
  const [description, setDescription] = useState(
    `Task context for "${task.title}". Add implementation notes, blockers, and delivery steps.`
  );
  const selectedLabels = task.labels || [];
  const [attachments] = useState([
    { id: 'a1', name: 'wireframe.png', kind: 'image' },
    { id: 'a2', name: 'api-spec.mp4', kind: 'video' },
    { id: 'a3', name: 'standup-note.mp3', kind: 'audio' },
  ]);
  const [comments, setComments] = useState([
    {
      id: 'c1',
      userId: 'u1',
      userName: 'Ava Johnson',
      timestamp: '2h ago',
      content: 'Please validate this with @Noah Smith before moving to review.',
      media: { type: 'image', label: 'ui-preview.png' },
      likes: 3,
      replies: [
        {
          id: 'c1-r1',
          userId: 'u2',
          userName: 'Noah Smith',
          timestamp: '1h ago',
          content: 'Looks good, I will confirm API side by EOD.',
          likes: 1,
        },
      ],
    },
    {
      id: 'c2',
      userId: 'u5',
      userName: 'Emma Davis',
      timestamp: '45m ago',
      content: 'Voice note attached with QA concerns for mobile.',
      media: { type: 'audio', label: 'qa-note.wav' },
      likes: 2,
      replies: [],
    },
  ]);
  const [commentInput, setCommentInput] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [attachedPreview, setAttachedPreview] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [loadingComments, setLoadingComments] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoadingComments(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const mentionMatches =
    mentionQuery.length > 0
      ? teamMembers.filter((member) =>
          member.name.toLowerCase().includes(mentionQuery.toLowerCase())
        )
      : [];

  const decorateMentions = (text) =>
    text.split(/(@[A-Za-z]+\s?[A-Za-z]*)/g).map((part, index) =>
      part.startsWith('@') ? (
        <span key={`${part}-${index}`} className="font-medium text-brand-600 dark:text-brand-300">
          {part}
        </span>
      ) : (
        <Fragment key={`${part}-${index}`}>{part}</Fragment>
      )
    );

  const mediaIcon = (type) => {
    if (type === 'image') return <Image size={12} />;
    if (type === 'video') return <Video size={12} />;
    return <Mic size={12} />;
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/45" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 w-full overflow-hidden rounded-none border border-brand-200 bg-white shadow-2xl dark:border-brand-900 dark:bg-slate-950 lg:w-[80%] lg:rounded-l-2xl">
        <div className="flex h-full flex-col">
          <header className="flex items-center justify-between border-b border-brand-200 px-4 py-3 dark:border-brand-900">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <input
                value={task.title}
                onChange={(event) => onUpdateTask(task.id, { title: event.target.value })}
                className="w-full rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-sm font-semibold outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-900"
              />
              <select
                value={task.columnId}
                onChange={(event) => onUpdateTask(task.id, { columnId: event.target.value })}
                className="rounded-lg border border-brand-200 bg-white px-2 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-900"
              >
                {columns.map((column) => (
                  <option key={column.id} value={column.id}>
                    {translatedColumnName(t, column.name)}
                  </option>
                ))}
              </select>
              <select
                value={task.priority}
                onChange={(event) => onUpdateTask(task.id, { priority: event.target.value })}
                className="rounded-lg border border-brand-200 bg-white px-2 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-900"
              >
                {Object.keys(priorityStyles).map((priority) => (
                  <option key={priority} value={priority}>
                    {translatedPriority(t, priority)}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="ml-2 rounded-md p-1.5 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
            >
              <X size={16} />
            </button>
          </header>

          <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-5">
            <div className="min-h-0 overflow-y-auto border-r border-brand-200 p-4 dark:border-brand-900 lg:col-span-3">
              <section>
                <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">{t('description')}</p>
                <TaskDescriptionEditor
                  value={description}
                  placeholder={t('taskDescriptionPlaceholder')}
                  onChange={setDescription}
                  t={t}
                />
              </section>

              <section className="mt-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('subtasks')}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setDraftSubtask(sampleSubtaskTitle(task.title))}
                      className="rounded-md border border-dashed border-brand-300 px-2.5 py-1 text-[11px] font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
                    >
                      {t('fillSampleData')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const title = draftSubtask.trim();
                        if (!title) return;
                        setSubtasks((prev) => [
                          ...prev,
                          {
                            id: `${task.id}-sub-${Date.now()}`,
                            title,
                            done: false,
                            assigneeId: task.assigneeId,
                            status: 'Open',
                          },
                        ]);
                        setDraftSubtask('');
                      }}
                      className="rounded-md bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white"
                    >
                      {t('addSubtask')}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {subtasks.map((subtask) => (
                    <div key={subtask.id} className="flex items-center justify-between rounded-lg border border-brand-200 px-2.5 py-2 dark:border-brand-900">
                      <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
                        <input
                          type="checkbox"
                          checked={subtask.done}
                          onChange={() =>
                            setSubtasks((prev) =>
                              prev.map((item) =>
                                item.id === subtask.id
                                  ? { ...item, done: !item.done, status: !item.done ? 'Done' : 'Open' }
                                  : item
                              )
                            )
                          }
                        />
                        {subtask.title}
                      </label>
                      <span className="text-xs text-slate-500 dark:text-slate-300">{subtask.status === 'Done' ? t('columnDone') : t('statusOpen')}</span>
                    </div>
                  ))}
                  <input
                    value={draftSubtask}
                    onChange={(event) => setDraftSubtask(event.target.value)}
                    placeholder={`+ ${t('addSubtask')}`}
                    className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-900"
                  />
                </div>
              </section>

              <section className="mt-4 rounded-xl border border-brand-200 p-3 dark:border-brand-900">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('activityComments')}</p>

                <div className="mt-3 space-y-3">
                  {loadingComments ? (
                    <>
                      <SkeletonCard />
                      <SkeletonCard />
                    </>
                  ) : comments.map((comment) => (
                    <div key={comment.id} className="rounded-lg border border-brand-100 p-2.5 dark:border-slate-800">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-brand-500 text-[10px] font-semibold text-white flex items-center justify-center">
                            {comment.userName
                              .split(' ')
                              .map((word) => word[0])
                              .join('')
                              .slice(0, 2)}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{comment.userName}</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-300">{comment.timestamp}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="rounded-md p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
                        >
                          <CircleEllipsis size={13} />
                        </button>
                      </div>

                      {editingCommentId === comment.id ? (
                        <textarea
                          rows={2}
                          value={comment.content}
                          onChange={(event) =>
                            setComments((prev) =>
                              prev.map((item) =>
                                item.id === comment.id ? { ...item, content: event.target.value } : item
                              )
                            )
                          }
                          className="mt-2 w-full rounded-md border border-brand-200 px-2 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-900"
                        />
                      ) : (
                        <p className="mt-2 text-xs text-slate-700 dark:text-slate-200">
                          {decorateMentions(comment.content)}
                        </p>
                      )}

                      {comment.media && (
                        <div className="mt-2 rounded-md border border-brand-200 bg-brand-50/60 p-2 text-xs dark:border-brand-900 dark:bg-brand-900/10">
                          <div className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                            {mediaIcon(comment.media.type)}
                            {comment.media.label}
                          </div>
                          {comment.media.type === 'audio' && (
                            <div className="mt-1.5 h-1.5 w-full rounded-full bg-brand-200 dark:bg-brand-900" />
                          )}
                        </div>
                      )}

                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                        <button
                          type="button"
                          onClick={() =>
                            setComments((prev) =>
                              prev.map((item) =>
                                item.id === comment.id ? { ...item, likes: item.likes + 1 } : item
                              )
                            )
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-slate-600 dark:border-brand-900 dark:text-slate-300"
                        >
                          <ThumbsUp size={11} />
                          {comment.likes}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setReplyDrafts((prev) => ({
                              ...prev,
                              [comment.id]: prev[comment.id] ? '' : `@${comment.userName.split(' ')[0]} `,
                            }))
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-slate-600 dark:border-brand-900 dark:text-slate-300"
                        >
                          <Reply size={11} />
                          {t('reply')}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setEditingCommentId((prev) => (prev === comment.id ? null : comment.id))
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-slate-600 dark:border-brand-900 dark:text-slate-300"
                        >
                          <Pencil size={11} />
                          {t('edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setComments((prev) => prev.filter((item) => item.id !== comment.id))}
                          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-slate-600 dark:border-brand-900 dark:text-slate-300"
                        >
                          <Trash2 size={11} />
                          {t('delete')}
                        </button>
                      </div>

                      {comment.replies.length > 0 && (
                        <div className="mt-2 space-y-1.5 border-l-2 border-brand-200 pl-3 dark:border-brand-900">
                          {comment.replies.map((reply) => (
                            <div key={reply.id} className="rounded-md border border-brand-100 p-2 text-xs dark:border-slate-800">
                              <p className="font-medium text-slate-700 dark:text-slate-200">
                                {reply.userName} <span className="font-normal text-slate-500">{reply.timestamp}</span>
                              </p>
                              <p className="mt-1 text-slate-600 dark:text-slate-300">{decorateMentions(reply.content)}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {replyDrafts[comment.id] !== undefined && (
                        <div className="mt-2 flex gap-2">
                          <input
                            value={replyDrafts[comment.id]}
                            onChange={(event) =>
                              setReplyDrafts((prev) => ({ ...prev, [comment.id]: event.target.value }))
                            }
                            className="flex-1 rounded-md border border-brand-200 px-2 py-1 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-900"
                            placeholder={t('writeReplyFull')}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const text = (replyDrafts[comment.id] || '').trim();
                              if (!text) return;
                              setComments((prev) =>
                                prev.map((item) =>
                                  item.id === comment.id
                                    ? {
                                        ...item,
                                        replies: [
                                          ...item.replies,
                                          {
                                            id: `${comment.id}-r-${Date.now()}`,
                                            userId: 'u3',
                                            userName: 'Mia Patel',
                                            timestamp: 'now',
                                            content: text,
                                            likes: 0,
                                          },
                                        ],
                                      }
                                    : item
                                )
                              );
                              setReplyDrafts((prev) => ({ ...prev, [comment.id]: '' }));
                            }}
                            className="rounded-md bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white"
                          >
                            {t('send')}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-3 rounded-lg border border-brand-200 p-2.5 dark:border-brand-900">
                  <textarea
                    rows={3}
                    value={commentInput}
                    onChange={(event) => {
                      const value = event.target.value;
                      setCommentInput(value);
                      const atIndex = value.lastIndexOf('@');
                      if (atIndex >= 0) {
                        const query = value.slice(atIndex + 1).split(/\s/)[0];
                        setMentionQuery(query);
                      } else {
                        setMentionQuery('');
                      }
                    }}
                    placeholder={t('writeCommentFull')}
                    className="w-full rounded-md border border-brand-200 px-2.5 py-1.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-900"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    {[
                      ['Bold', t('editorBold')],
                      ['Italic', t('editorItalic')],
                    ].map(([tool, label]) => (
                      <button key={tool} type="button" className="rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300">
                        {label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker((prev) => !prev)}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <Smile size={11} />
                      {t('emoji')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAttachedPreview({ type: 'image', label: 'new-image.png' })}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <Image size={11} />
                      {t('attachImage')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAttachedPreview({ type: 'video', label: 'demo.mp4' })}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <Video size={11} />
                      {t('attachVideo')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAttachedPreview({ type: 'audio', label: 'note.wav' })}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <Mic size={11} />
                      {t('attachAudio')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCommentInput((prev) => `${prev} @`)}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <AtSign size={11} />
                      {t('mention')}
                    </button>
                  </div>

                  {showEmojiPicker && (
                    <div className="mt-2 grid w-fit grid-cols-6 gap-1 rounded-md border border-brand-200 bg-white p-1.5 dark:border-brand-900 dark:bg-slate-900">
                      {['😀', '🔥', '👍', '🎯', '✅', '🚀', '💡', '🎉', '👀', '🙏', '😄', '🤝'].map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setCommentInput((prev) => `${prev}${emoji}`)}
                          className="rounded p-1 text-sm hover:bg-brand-50 dark:hover:bg-brand-900/20"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}

                  {mentionMatches.length > 0 && (
                    <div className="mt-2 w-56 rounded-md border border-brand-200 bg-white p-1 dark:border-brand-900 dark:bg-slate-900">
                      {mentionMatches.slice(0, 5).map((member) => (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => {
                            const atIndex = commentInput.lastIndexOf('@');
                            if (atIndex >= 0) {
                              const base = commentInput.slice(0, atIndex);
                              setCommentInput(`${base}@${member.name} `);
                            }
                            setMentionQuery('');
                          }}
                          className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-brand-50 dark:hover:bg-brand-900/20"
                        >
                          {member.name}
                        </button>
                      ))}
                    </div>
                  )}

                  {attachedPreview && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:bg-brand-900/10 dark:text-slate-300">
                      {mediaIcon(attachedPreview.type)}
                      {t('preview')}: {attachedPreview.label}
                      <button
                        type="button"
                        onClick={() => setAttachedPreview(null)}
                        className="rounded p-0.5 hover:bg-brand-100 dark:hover:bg-brand-900/30"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  )}

                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        const text = commentInput.trim();
                        if (!text) return;
                        setComments((prev) => [
                          ...prev,
                          {
                            id: `c-${Date.now()}`,
                            userId: 'u3',
                            userName: 'Mia Patel',
                            timestamp: 'now',
                            content: text,
                            media: attachedPreview,
                            likes: 0,
                            replies: [],
                          },
                        ]);
                        setCommentInput('');
                        setAttachedPreview(null);
                        setShowEmojiPicker(false);
                        setMentionQuery('');
                      }}
                      className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      {t('submit')}
                    </button>
                  </div>
                </div>
              </section>
            </div>

            <div className="min-h-0 overflow-y-auto p-4 lg:col-span-2">
              <div className="space-y-3 text-xs">
                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('assignee')}</span>
                  <select
                    value={task.assigneeId}
                    onChange={(event) => onUpdateTask(task.id, { assigneeId: event.target.value })}
                    className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-900"
                  >
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="rounded-lg border border-brand-200 px-2.5 py-1.5 dark:border-brand-900">
                  <p className="text-slate-500 dark:text-slate-300">{t('reporter')}</p>
                  <p className="mt-1 text-slate-700 dark:text-slate-200">{assignee?.name || 'Ava Johnson'}</p>
                </div>

                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('category')}</span>
                  <select
                    value={task.category}
                    onChange={(event) => onUpdateTask(task.id, { category: event.target.value })}
                    className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-900"
                  >
                    {!categories.some((category) => category.name === task.category) && (
                      <option value={task.category}>{task.category}</option>
                    )}
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('startDate')}</span>
                  <input type="date" className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-900" />
                </label>

                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('dueDate')}</span>
                  <input type="date" className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-900" />
                </label>

                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('project')}</span>
                  <input value={project.name} readOnly className="w-full rounded-lg border border-brand-200 bg-slate-50 px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-900" />
                </label>

                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('labelsTags')}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {labels.map((label) => {
                      const active = selectedLabels.includes(label.name);
                      return (
                        <button
                          key={label.id}
                          type="button"
                          onClick={() =>
                            onUpdateTask(task.id, {
                              labels: active
                                ? selectedLabels.filter((item) => item !== label.name)
                                : [...selectedLabels, label.name],
                            })
                          }
                          className={`rounded-full px-2 py-1 text-[11px] ${
                            active
                              ? 'text-white'
                              : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
                          }`}
                          style={active ? { backgroundColor: label.color } : undefined}
                        >
                          {label.name}
                        </button>
                      );
                    })}
                  </div>
                </label>

                {showManageCategoriesShortcut && (
                  <button
                    type="button"
                    onClick={onOpenCategoryManager}
                    className="w-full rounded-lg border border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-800 dark:text-brand-300 dark:hover:bg-brand-900/20"
                  >
                    {t('manageCategoriesLabels')}
                  </button>
                )}

                <div>
                  <p className="mb-1 text-slate-500 dark:text-slate-300">{t('attachments')}</p>
                  <div className="space-y-1.5">
                    {attachments.map((file) => (
                      <div key={file.id} className="flex items-center gap-2 rounded-lg border border-brand-200 px-2 py-1.5 dark:border-brand-900">
                        <Paperclip size={12} />
                        <span className="truncate">{file.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setMeetingModalOpen(true)}
                  className="w-full rounded-lg border border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-800 dark:text-brand-300 dark:hover:bg-brand-900/20"
                >
                  {t('shareMeetingLink')}
                </button>
              </div>
            </div>
          </div>

          <footer className="border-t border-brand-200 p-3 dark:border-brand-900">
            <button
              type="button"
              className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-brand"
            >
              {t('saveChanges')}
            </button>
          </footer>
        </div>
      </aside>

      {meetingModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-white">{t('shareMeetingLink')}</h4>
            <input
              value={meetingLink}
              onChange={(event) => setMeetingLink(event.target.value)}
              placeholder="https://meet.google.com/..."
              className="mt-3 w-full rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
            />
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setMeetingLink(sampleMeetingLink())}
                className="mr-auto rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
              >
                {t('fillSampleData')}
              </button>
              <button
                type="button"
                onClick={() => setMeetingModalOpen(false)}
                className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={() => setMeetingModalOpen(false)}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t('share')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  TASK DETAIL PAGE (full-screen route)                           */
/* ═══════════════════════════════════════════════════════════════ */
function TaskDetailPage() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { projectId, taskId } = useParams();
  const [viewerUserId, setViewerUserId] = useState(null);
  const [project, setProject] = useState(null);
  const [columns, setColumns] = useState([]);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiGetMe().then((me) => { if (me.userId) setViewerUserId(me.userId); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!projectId || !taskId) return;
    let cancelled = false;
    setLoading(true);
    setErr('');
    (async () => {
      try {
        const [board, detail] = await Promise.all([
          apiGetProjectBoard(Number(projectId)),
          apiGetWorkItemDetail(Number(projectId), Number(taskId)),
        ]);
        if (cancelled) return;
        setColumns(board.columns || []);
        setProject({ id: String(projectId), name: board.projectName || 'Project', readOnly: board.readOnly });
        // Build a minimal task stub so the drawer has something to show immediately
        setTask({
          id: String(taskId),
          taskNumber: detail?.taskNumber || '',
          title: detail?.title || '',
          columnId: String(detail?.columnId || ''),
          priority: detail?.priority || 'Medium',
          assigneeUserId: detail?.assigneeUserId || '',
          description: detail?.description || '',
          dueDateLocal: detail?.dueDateLocal || '',
          meetingLink: detail?.meetingLink || '',
          attachments: detail?.attachments || [],
          subtasks: detail?.subtasks || [],
          commentsTree: detail?.commentsTree || [],
        });
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Failed to load task.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, taskId]);

  const goBack = () => navigate(-1);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-slate-400 dark:text-slate-500">{t('loadingTask')}</div>
      </div>
    );
  }
  if (err || !task) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-slate-50 dark:bg-slate-950">
        <p className="text-red-500">{err || 'Task not found.'}</p>
        <button onClick={goBack} className="rounded-xl bg-brand-500 px-4 py-2 text-sm text-white hover:opacity-90">{t('close')}</button>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-slate-50 dark:bg-slate-950">
      {/* top bar */}
      <div className="flex items-center gap-3 border-b border-brand-200 dark:border-brand-900 bg-white dark:bg-slate-950 px-4 py-3">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          {t('openProject')}
        </button>
        <span className="text-slate-300 dark:text-slate-700">|</span>
        <span className="text-sm text-slate-500 dark:text-slate-400 truncate">{project?.name}</span>
      </div>

      {/* drawer rendered inside a full-height container, no overlay, no slide-in */}
      <div className="flex-1 overflow-hidden">
        <TaskDetailDrawerApi
          task={task}
          project={project || { id: projectId, name: '' }}
          columns={columns}
          categories={[]}
          t={t}
          apiProjectId={Number(projectId)}
          onBoardRefresh={() => {}}
          viewerUserId={viewerUserId}
          SkeletonCard={null}
          taskFieldsReadOnly={project?.readOnly || false}
          onClose={goBack}
          onUpdateTask={() => {}}
          pageMode
        />
      </div>
    </div>
  );
}

function TaskDetailDrawer(props) {
  const {
    isApiProject,
    apiProjectId,
    onBoardRefresh,
    viewerUserId,
    SkeletonCard: Skeleton,
    taskFieldsReadOnly,
    showManageCategoriesShortcut = true,
    t,
    ...rest
  } = props;
  if (isApiProject && apiProjectId != null) {
    return (
      <TaskDetailDrawerApi
        {...rest}
        t={t}
        apiProjectId={apiProjectId}
        onBoardRefresh={onBoardRefresh}
        viewerUserId={viewerUserId}
        SkeletonCard={Skeleton}
        taskFieldsReadOnly={taskFieldsReadOnly}
      />
    );
  }
  return <TaskDetailDrawerLocal {...rest} showManageCategoriesShortcut={showManageCategoriesShortcut} />;
}

function KanbanBoard({ project, onBackToProjects }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const isApiProject = /^\d+$/.test(String(project.id));
  const [viewerUserId, setViewerUserId] = useState(null);

  useEffect(() => {
    if (!isApiProject) return undefined;
    let cancelled = false;
    apiGetMe()
      .then((me) => {
        if (!cancelled && me.userId) setViewerUserId(me.userId);
      })
      .catch(() => {
        /* ignore — assignee dropdown falls back */
      });
    return () => {
      cancelled = true;
    };
  }, [isApiProject]);

  const [columns, setColumns] = useState(() => (isApiProject ? [] : boardColumnSeed));
  const [boardReadOnly, setBoardReadOnly] = useState(() =>
    isApiProject ? Boolean(project.readOnly) : false
  );
  const [tasks, setTasks] = useState(() =>
    isApiProject
      ? []
      : boardTaskSeed.map((task, index) => ({
          ...task,
          timelineStart: (index % 6) + 1,
          timelineDuration: (index % 4) + 2,
          dependencies: index > 0 ? [boardTaskSeed[index - 1].id] : [],
        }))
  );
  const [boardLoading, setBoardLoading] = useState(isApiProject);
  const [boardError, setBoardError] = useState('');
  const [boardProjectNumber, setBoardProjectNumber] = useState(() => project.projectNumber || '');

  useEffect(() => {
    setBoardProjectNumber(project.projectNumber || '');
  }, [project.id, project.projectNumber]);

  useEffect(() => {
    if (!isApiProject) return undefined;
    setBoardReadOnly(Boolean(project.readOnly));
    return undefined;
  }, [isApiProject, project.id, project.readOnly]);

  const structuralReadOnly = isApiProject && boardReadOnly;
  const workspaceAddsAllowed = useWorkspaceAdds();
  const allowStructuralAdds = workspaceAddsAllowed && !structuralReadOnly;

  const refreshApiBoard = async () => {
    const raw = await apiGetProjectBoard(project.id);
    const mapped = mapApiBoardToKanban(raw);
    setColumns(mapped.columns);
    setTasks(mapped.tasks);
    setBoardReadOnly(mapped.readOnly);
    if (mapped.projectNumber) setBoardProjectNumber(mapped.projectNumber);
  };

  useEffect(() => {
    if (!isApiProject) {
      setBoardLoading(false);
      setBoardError('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setBoardLoading(true);
      setBoardError('');
      try {
        const raw = await apiGetProjectBoard(project.id);
        if (cancelled) return;
        const mapped = mapApiBoardToKanban(raw);
        setColumns(mapped.columns);
        setTasks(mapped.tasks);
        setBoardReadOnly(mapped.readOnly);
        if (mapped.projectNumber) setBoardProjectNumber(mapped.projectNumber);
      } catch (err) {
        if (!cancelled) setBoardError(err instanceof Error ? err.message : t('couldNotLoadBoard'));
      } finally {
        if (!cancelled) setBoardLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [project.id, isApiProject]);

  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [quickAddByColumn, setQuickAddByColumn] = useState({});
  const [columnMenuId, setColumnMenuId] = useState(null);
  const [activeView, setActiveView] = useState('board');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [expandedTaskIds, setExpandedTaskIds] = useState([]);
  const [groupBy, setGroupBy] = useState('status');
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [newListTaskTitle, setNewListTaskTitle] = useState('');
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [categories, setCategories] = useState(defaultCategories);
  const [labels, setLabels] = useState(defaultLabels);
  const [timelineZoom, setTimelineZoom] = useState('Day');
  const [timelineGroupBy, setTimelineGroupBy] = useState('status');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [savedFilterViews, setSavedFilterViews] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState({
    assignees: [],
    priorities: [],
    statuses: [],
    categories: [],
    labels: [],
    dueDateRange: '',
    createdDateRange: '',
  });

  const filteredTasks = tasks.filter((task) => {
    const matchesAssignee = assigneeFilter === 'all' || task.assigneeId === assigneeFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    const term = searchTerm.trim().toLowerCase();
    const tn = String(task.taskNumber || '').toLowerCase();
    const matchesSearch =
      !term ||
      task.title.toLowerCase().includes(term) ||
      task.category.toLowerCase().includes(term) ||
      tn.includes(term);
    const matchesAdvancedAssignee =
      advancedFilters.assignees.length === 0 || advancedFilters.assignees.includes(task.assigneeId);
    const matchesAdvancedPriority =
      advancedFilters.priorities.length === 0 || advancedFilters.priorities.includes(task.priority);
    const statusLabel = columns.find((column) => column.id === task.columnId)?.name || 'Unknown';
    const matchesAdvancedStatus =
      advancedFilters.statuses.length === 0 || advancedFilters.statuses.includes(statusLabel);
    const matchesAdvancedCategory =
      advancedFilters.categories.length === 0 || advancedFilters.categories.includes(task.category);
    const matchesAdvancedLabels =
      advancedFilters.labels.length === 0 ||
      advancedFilters.labels.some((label) => (task.labels || []).includes(label));

    return (
      matchesAssignee &&
      matchesPriority &&
      matchesSearch &&
      matchesAdvancedAssignee &&
      matchesAdvancedPriority &&
      matchesAdvancedStatus &&
      matchesAdvancedCategory &&
      matchesAdvancedLabels
    );
  });

  const tasksForColumn = (columnId) => filteredTasks.filter((task) => task.columnId === columnId);
  const statusNameForTask = (task) =>
    columns.find((column) => column.id === task.columnId)?.name || 'Unknown';
  const sortableTasks = [...filteredTasks].sort((a, b) => {
    const assigneeA = teamMembers.find((member) => member.id === a.assigneeId)?.name || '';
    const assigneeB = teamMembers.find((member) => member.id === b.assigneeId)?.name || '';

    const priorityOrder = { Critical: 1, High: 2, Medium: 3, Low: 4 };
    const dueA = new Date(`2026 ${a.dueDate}`).getTime();
    const dueB = new Date(`2026 ${b.dueDate}`).getTime();

    const values = {
      taskNo: [a.taskNumber || '', b.taskNumber || ''],
      title: [a.title, b.title],
      status: [statusNameForTask(a), statusNameForTask(b)],
      priority: [priorityOrder[a.priority] || 999, priorityOrder[b.priority] || 999],
      assignee: [assigneeA, assigneeB],
      category: [a.category, b.category],
      dueDate: [dueA, dueB],
      subtasks: [a.subtaskDone / a.subtaskTotal, b.subtaskDone / b.subtaskTotal],
    };

    const [left, right] = values[sortBy] || values.dueDate;
    const compareResult =
      typeof left === 'string' ? left.localeCompare(right) : Number(left) - Number(right);
    return sortDirection === 'asc' ? compareResult : -compareResult;
  });

  const moveTask = async (taskId, direction) => {
    if (structuralReadOnly) return;
    if (isApiProject) {
      const task = tasks.find((row) => row.id === taskId);
      if (!task) return;
      const currentIndex = columns.findIndex((col) => col.id === task.columnId);
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= columns.length) return;
      const nextColId = columns[nextIndex].id;
      try {
        await apiMoveProjectTask(project.id, Number(taskId), { columnId: Number(nextColId) });
        setTasks((prev) =>
          prev.map((row) => (row.id === taskId ? { ...row, columnId: nextColId } : row))
        );
      } catch (err) {
        window.alert(err instanceof Error ? err.message : t('moveFailed'));
        try {
          await refreshApiBoard();
        } catch {
          /* ignore */
        }
      }
      return;
    }
    setTasks((prev) =>
      prev.map((row) => {
        if (row.id !== taskId) return row;
        const currentIndex = columns.findIndex((col) => col.id === row.columnId);
        const nextIndex = currentIndex + direction;
        if (nextIndex < 0 || nextIndex >= columns.length) return row;
        return { ...row, columnId: columns[nextIndex].id };
      })
    );
  };

  const addTask = async (columnId) => {
    if (!allowStructuralAdds) return;
    const title = (quickAddByColumn[columnId] || '').trim();
    if (!title) return;
    if (isApiProject) {
      try {
        await apiCreateProjectTask(project.id, { columnId: Number(columnId), title });
        await refreshApiBoard();
      } catch (err) {
        window.alert(err instanceof Error ? err.message : t('couldNotCreateTask'));
      }
      setQuickAddByColumn((prev) => ({ ...prev, [columnId]: '' }));
      return;
    }
    setTasks((prev) => [
      ...prev,
      {
        id: `t${Date.now()}`,
        title,
        priority: 'Medium',
        assigneeId: 'u1',
        dueDate: 'May 30',
        subtaskDone: 0,
        subtaskTotal: 1,
        category: 'General',
        hasAttachment: false,
        labels: [],
        timelineStart: 1,
        timelineDuration: 3,
        dependencies: [],
        columnId,
      },
    ]);
    setQuickAddByColumn((prev) => ({ ...prev, [columnId]: '' }));
  };

  const updateTaskStatus = async (taskId, nextColumnId) => {
    if (structuralReadOnly) return;
    if (isApiProject) {
      try {
        await apiMoveProjectTask(project.id, Number(taskId), { columnId: Number(nextColumnId) });
      } catch (err) {
        window.alert(err instanceof Error ? err.message : t('moveFailed'));
        try {
          await refreshApiBoard();
        } catch {
          /* ignore */
        }
        return;
      }
    }
    setTasks((prev) =>
      prev.map((task) => (task.id === taskId ? { ...task, columnId: nextColumnId } : task))
    );
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const toggleExpandedRow = (taskId) => {
    setExpandedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const setSort = (columnKey) => {
    setSortBy((prevSortBy) => {
      if (prevSortBy === columnKey) {
        setSortDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDirection('asc');
      }
      return columnKey;
    });
  };

  const addListTask = () => {
    if (!allowStructuralAdds) return;
    const title = newListTaskTitle.trim();
    if (!title) return;
    const defaultColumnId = columns[0]?.id ?? 'todo';
    if (isApiProject) {
      (async () => {
        try {
          await apiCreateProjectTask(project.id, {
            columnId: Number(defaultColumnId),
            title,
          });
          await refreshApiBoard();
        } catch (err) {
          window.alert(err instanceof Error ? err.message : t('couldNotCreateTask'));
        }
      })();
      setNewListTaskTitle('');
      return;
    }
    setTasks((prev) => [
      ...prev,
      {
        id: `t${Date.now()}`,
        title,
        priority: 'Medium',
        assigneeId: 'u1',
        dueDate: 'May 30',
        subtaskDone: 0,
        subtaskTotal: 1,
        category: 'General',
        hasAttachment: false,
        labels: [],
        timelineStart: 1,
        timelineDuration: 3,
        dependencies: [],
        columnId: defaultColumnId,
      },
    ]);
    setNewListTaskTitle('');
  };

  const renameColumn = (columnId) => {
    const value = window.prompt(t('renameColumnPrompt'));
    if (!value || !value.trim()) return;
    setColumns((prev) =>
      prev.map((column) => (column.id === columnId ? { ...column, name: value.trim() } : column))
    );
    setColumnMenuId(null);
  };

  const deleteColumn = (columnId) => {
    if (columns.length <= 1) return;
    setColumns((prev) => prev.filter((column) => column.id !== columnId));
    setTasks((prev) => prev.filter((task) => task.columnId !== columnId));
    setColumnMenuId(null);
  };

  const addColumn = () => {
    if (!allowStructuralAdds) return;
    const value = window.prompt(t('newColumnNamePrompt'));
    if (!value || !value.trim()) return;
    setColumns((prev) => [
      ...prev,
      {
        id: `col${Date.now()}`,
        name: value.trim(),
        tone: 'bg-brand-500',
      },
    ]);
    setColumnMenuId(null);
  };

  const groupedTaskMap = sortableTasks.reduce((acc, task) => {
    let key = t('ungrouped');
    if (groupBy === 'status') key = statusNameForTask(task);
    if (groupBy === 'priority') key = task.priority;
    if (groupBy === 'assignee') {
      key = teamMembers.find((member) => member.id === task.assigneeId)?.name || t('unassigned');
    }
    if (groupBy === 'category') key = task.category;
    if (!acc[key]) acc[key] = [];
    acc[key].push(task);
    return acc;
  }, {});
  const timelineGroups = tasks.reduce((acc, task) => {
    let key = statusNameForTask(task);
    if (timelineGroupBy === 'assignee') {
      key = teamMembers.find((member) => member.id === task.assigneeId)?.name || t('unassigned');
    } else if (timelineGroupBy === 'category') {
      key = task.category;
    }
    if (!acc[key]) acc[key] = [];
    acc[key].push(task);
    return acc;
  }, {});
  const activeTask = tasks.find((task) => task.id === activeTaskId) || null;

  const updateTask = (taskId, updates) => {
    setTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, ...updates } : task)));
  };
  const timelineColumnsCount =
    timelineZoom === 'Day' ? 14 : timelineZoom === 'Week' ? 10 : 6;
  const timelineLabels = Array.from({ length: timelineColumnsCount }, (_, index) => {
    if (timelineZoom === 'Day') return `D${index + 1}`;
    if (timelineZoom === 'Week') return `W${index + 1}`;
    return `M${index + 1}`;
  });
  const todayMarkerIndex = timelineZoom === 'Day' ? 6 : timelineZoom === 'Week' ? 3 : 1;
  const activeFilterChips = [
    ...advancedFilters.assignees.map((id) => ({
      kind: 'assignee',
      value: id,
      label: `${t('assignee')}:${teamMembers.find((m) => m.id === id)?.name || id}`,
    })),
    ...advancedFilters.priorities.map((p) => ({
      kind: 'priority',
      value: p,
      label: `${t('priority')}:${translatedPriority(t, p)}`,
    })),
    ...advancedFilters.statuses.map((s) => ({ kind: 'status', value: s, label: `${t('status')}:${s}` })),
    ...advancedFilters.categories.map((c) => ({ kind: 'category', value: c, label: `${t('category')}:${c}` })),
    ...advancedFilters.labels.map((l) => ({ kind: 'label', value: l, label: `${t('label')}:${l}` })),
    ...(advancedFilters.dueDateRange
      ? [{ kind: 'due', value: advancedFilters.dueDateRange, label: `${t('dueDateRange')}:${advancedFilters.dueDateRange}` }]
      : []),
    ...(advancedFilters.createdDateRange
      ? [{ kind: 'created', value: advancedFilters.createdDateRange, label: `${t('createdDateRange')}:${advancedFilters.createdDateRange}` }]
      : []),
  ];

  if (boardLoading) {
    return (
      <section className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
          {t('loadingProjectBoard')}
        </div>
      </section>
    );
  }

  if (boardError) {
    return (
      <section className="space-y-4">
        <div className="rounded-2xl border border-red-200/60 bg-white p-6 dark:border-red-900/50 dark:bg-slate-900">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">{boardError}</p>
          <button
            type="button"
            onClick={onBackToProjects}
            className="mt-4 text-sm font-semibold text-brand-600 dark:text-brand-300"
          >
            {t('backToProjects')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={onBackToProjects}
              className="text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-300"
            >
              {t('projectsBreadcrumb')} /{' '}
              {(() => {
                const pno = boardProjectNumber || project.projectNumber || '';
                return pno ? (
                  <>
                    <span className="font-mono">{pno}</span>
                    <span className="mx-1 text-slate-400">·</span>
                  </>
                ) : null;
              })()}
              {project.name}
            </button>
            <h2 className="mt-1 text-xl font-semibold text-slate-800 dark:text-white">{t('projectBoard')}</h2>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-brand-200 p-1 dark:border-brand-900">
            {[
              { id: 'board', icon: KanbanSquare },
              { id: 'list', icon: Table2 },
              { id: 'channel', icon: MessageSquare },
              { id: 'timeline', icon: CalendarDays },
            ].map((view) => (
              <button
                key={view.id}
                type="button"
                onClick={() => setActiveView(view.id)}
                className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
                  activeView === view.id
                    ? 'bg-brand-500 text-white'
                    : 'text-slate-600 hover:bg-brand-50 dark:text-slate-300 dark:hover:bg-brand-900/20'
                }`}
              >
                <view.icon size={14} />
                {t(projectViewTranslationKeys[view.id])}
              </button>
            ))}
          </div>
          {allowStructuralAdds && (
            <button
              type="button"
              onClick={() => setShowCategoryManager(true)}
              className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-brand-50 dark:border-brand-900 dark:text-slate-300 dark:hover:bg-brand-900/20"
            >
              {t('categoriesAndLabels')}
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <select
            value={assigneeFilter}
            onChange={(event) => setAssigneeFilter(event.target.value)}
            className="rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
          >
            <option value="all">{t('allAssignees')}</option>
            {teamMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>

          <select
            value={priorityFilter}
            onChange={(event) => setPriorityFilter(event.target.value)}
            className="rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
          >
            <option value="all">{t('allPriorities')}</option>
            {Object.keys(priorityStyles).map((priority) => (
              <option key={priority} value={priority}>
                {translatedPriority(t, priority)}
              </option>
            ))}
          </select>

          <div className="relative min-w-56 flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('searchBoardTasks')}
              className="w-full rounded-lg border border-brand-200 bg-white py-2 pl-8 pr-3 text-sm outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowAdvancedFilters((prev) => !prev)}
            className="rounded-lg border border-brand-200 px-3 py-2 text-sm text-slate-600 hover:bg-brand-50 dark:border-brand-900 dark:text-slate-300 dark:hover:bg-brand-900/20"
          >
            {t('advancedFilters')}
          </button>
        </div>
        {showAdvancedFilters && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-brand-50/60 p-3 text-xs dark:border-slate-800 dark:bg-brand-900/10">
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('assignee')}</span>
                <select
                  onChange={(event) => {
                    const value = event.target.value;
                    if (!value) return;
                    setAdvancedFilters((prev) => ({
                      ...prev,
                      assignees: prev.assignees.includes(value) ? prev.assignees : [...prev.assignees, value],
                    }));
                  }}
                  className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                >
                  <option value="">{t('selectAssignee')}</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('priority')}</span>
                <select
                  onChange={(event) => {
                    const value = event.target.value;
                    if (!value) return;
                    setAdvancedFilters((prev) => ({
                      ...prev,
                      priorities: prev.priorities.includes(value) ? prev.priorities : [...prev.priorities, value],
                    }));
                  }}
                  className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                >
                  <option value="">{t('selectPriority')}</option>
                  {Object.keys(priorityStyles).map((priority) => (
                    <option key={priority} value={priority}>
                      {translatedPriority(t, priority)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('status')}</span>
                <select
                  onChange={(event) => {
                    const value = event.target.value;
                    if (!value) return;
                    setAdvancedFilters((prev) => ({
                      ...prev,
                      statuses: prev.statuses.includes(value) ? prev.statuses : [...prev.statuses, value],
                    }));
                  }}
                  className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                >
                  <option value="">{t('selectStatus')}</option>
                  {columns.map((column) => (
                    <option key={column.id} value={column.name}>
                      {translatedColumnName(t, column.name)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('category')}</span>
                <select
                  onChange={(event) => {
                    const value = event.target.value;
                    if (!value) return;
                    setAdvancedFilters((prev) => ({
                      ...prev,
                      categories: prev.categories.includes(value) ? prev.categories : [...prev.categories, value],
                    }));
                  }}
                  className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                >
                  <option value="">{t('selectCategory')}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('label')}</span>
                <select
                  onChange={(event) => {
                    const value = event.target.value;
                    if (!value) return;
                    setAdvancedFilters((prev) => ({
                      ...prev,
                      labels: prev.labels.includes(value) ? prev.labels : [...prev.labels, value],
                    }));
                  }}
                  className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                >
                  <option value="">{t('selectLabel')}</option>
                  {labels.map((label) => (
                    <option key={label.id} value={label.name}>
                      {label.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('dueDateRange')}</span>
                <input
                  value={advancedFilters.dueDateRange}
                  onChange={(event) =>
                    setAdvancedFilters((prev) => ({ ...prev, dueDateRange: event.target.value }))
                  }
                  placeholder={t('dueDateRangePlaceholder')}
                  className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                />
              </label>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {activeFilterChips.map((chip) => (
                <button
                  key={`${chip.kind}-${chip.value}`}
                  type="button"
                  onClick={() => {
                    setAdvancedFilters((prev) => {
                      if (chip.kind === 'assignee')
                        return { ...prev, assignees: prev.assignees.filter((id) => id !== chip.value) };
                      if (chip.kind === 'priority')
                        return { ...prev, priorities: prev.priorities.filter((item) => item !== chip.value) };
                      if (chip.kind === 'status')
                        return { ...prev, statuses: prev.statuses.filter((item) => item !== chip.value) };
                      if (chip.kind === 'category')
                        return { ...prev, categories: prev.categories.filter((item) => item !== chip.value) };
                      if (chip.kind === 'label')
                        return { ...prev, labels: prev.labels.filter((item) => item !== chip.value) };
                      if (chip.kind === 'due') return { ...prev, dueDateRange: '' };
                      if (chip.kind === 'created') return { ...prev, createdDateRange: '' };
                      return prev;
                    });
                  }}
                  className="rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-600 ring-1 ring-brand-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-brand-900"
                >
                  {chip.label} ×
                </button>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  setAdvancedFilters({
                    assignees: [],
                    priorities: [],
                    statuses: [],
                    categories: [],
                    labels: [],
                    dueDateRange: '',
                    createdDateRange: '',
                  })
                }
                className="rounded-md border border-brand-200 px-2.5 py-1 text-[11px] dark:border-brand-900"
              >
                {t('clearAllFilters')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const viewName = window.prompt(t('saveFilterViewPrompt'));
                  if (!viewName || !viewName.trim()) return;
                  setSavedFilterViews((prev) => [
                    ...prev,
                    { id: `filter-${Date.now()}`, name: viewName.trim(), config: advancedFilters },
                  ]);
                }}
                className="rounded-md bg-brand-500 px-2.5 py-1 text-[11px] font-semibold text-white"
              >
                {t('saveFilterAsView')}
              </button>
              {savedFilterViews.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  onClick={() => setAdvancedFilters(view.config)}
                  className="rounded-full border border-brand-200 px-2.5 py-1 text-[11px] dark:border-brand-900"
                >
                  {view.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {activeView === 'timeline' ? (
        <section className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500 dark:text-slate-300">{t('zoom')}</span>
              {['Day', 'Week', 'Month'].map((zoom) => (
                <button
                  key={zoom}
                  type="button"
                  onClick={() => setTimelineZoom(zoom)}
                  className={`rounded-md px-2 py-1 ${
                    timelineZoom === zoom
                      ? 'bg-brand-500 text-white'
                      : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
                  }`}
                >
                  {t(zoom === 'Day' ? 'day' : zoom === 'Week' ? 'week' : 'month')}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500 dark:text-slate-300">{t('groupBy')}</span>
              <select
                value={timelineGroupBy}
                onChange={(event) => setTimelineGroupBy(event.target.value)}
                className="rounded-md border border-brand-200 bg-white px-2 py-1 dark:border-brand-900 dark:bg-slate-950"
              >
                <option value="status">{t('status')}</option>
                <option value="assignee">{t('assignee')}</option>
                <option value="category">{t('category')}</option>
              </select>
              {allowStructuralAdds && (
                <button
                  type="button"
                  onClick={() =>
                    setTasks((prev) => [
                      ...prev,
                      {
                        id: `t-${Date.now()}`,
                        title: t('newTimelineTask'),
                        priority: 'Medium',
                        assigneeId: 'u1',
                        dueDate: 'May 30',
                        subtaskDone: 0,
                        subtaskTotal: 1,
                        category: 'General',
                        hasAttachment: false,
                        labels: [],
                        columnId: 'todo',
                        timelineStart: 2,
                        timelineDuration: 2,
                        dependencies: [],
                      },
                    ])
                  }
                  className="rounded-md bg-brand-500 px-2.5 py-1 text-white"
                >
                  + {t('addTask')}
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-0 overflow-x-auto rounded-xl border border-slate-200 lg:grid-cols-12 dark:border-slate-800">
            <div className="border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 lg:col-span-4">
              <div className="grid grid-cols-3 border-b border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-300">
                <span>{t('task')}</span>
                <span>{t('assignee')}</span>
                <span>{t('status')}</span>
              </div>
              {Object.entries(timelineGroups).map(([groupName, groupTasks]) => (
                <div key={groupName}>
                  <p className="border-b border-brand-100/80 bg-brand-50 px-3 py-1 text-[10px] font-semibold uppercase text-slate-500 dark:border-brand-900/30 dark:bg-brand-900/10 dark:text-slate-300">
                    {groupName}
                  </p>
                  {groupTasks.map((task) => {
                    const assigneeName =
                      teamMembers.find((member) => member.id === task.assigneeId)?.name || t('unassigned');
                    return (
                      <div
                        key={`${groupName}-${task.id}`}
                        className="grid grid-cols-3 border-b border-brand-100/80 px-3 py-2 text-xs dark:border-brand-900/30"
                      >
                        <span className="truncate text-slate-700 dark:text-slate-200">
                          {task.taskNumber ? (
                            <span className="mr-1 inline font-mono text-[10px] font-semibold text-brand-700 dark:text-brand-400">
                              {task.taskNumber}
                            </span>
                          ) : null}
                          {task.title}
                        </span>
                        <span className="truncate text-slate-500 dark:text-slate-300">{assigneeName}</span>
                        <span className="truncate text-slate-500 dark:text-slate-300">{statusNameForTask(task)}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="relative min-w-[700px] bg-white dark:bg-slate-900 lg:col-span-8">
              <div
                className="grid border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-300"
                style={{ gridTemplateColumns: `repeat(${timelineColumnsCount}, minmax(50px, 1fr))` }}
              >
                {timelineLabels.map((label) => (
                  <div key={label} className="border-r border-brand-100/80 px-2 py-2 text-center dark:border-brand-900/30">
                    {label}
                  </div>
                ))}
              </div>
              <div
                className="pointer-events-none absolute top-0 h-full w-[2px] bg-pink-500/80"
                style={{ left: `calc(${((todayMarkerIndex + 0.5) / timelineColumnsCount) * 100}% - 1px)` }}
              />
              <div className="space-y-0">
                {Object.values(timelineGroups).flat().map((task) => {
                  const barStart = Math.max(1, Math.min(task.timelineStart || 1, timelineColumnsCount));
                  const barEnd = Math.max(
                    barStart + 1,
                    Math.min(barStart + (task.timelineDuration || 2), timelineColumnsCount + 1)
                  );
                  const dependencyCount = task.dependencies?.length || 0;
                  return (
                    <div
                      key={`timeline-${task.id}`}
                      className="grid items-center border-b border-brand-100/80 px-2 py-2 dark:border-brand-900/30"
                      style={{ gridTemplateColumns: `repeat(${timelineColumnsCount}, minmax(50px, 1fr))` }}
                    >
                      <div
                        className={`relative flex items-center justify-between gap-1 rounded-md px-2 py-1 text-[11px] text-white ${
                          task.priority === 'Critical'
                            ? 'bg-pink-600'
                            : task.priority === 'High'
                              ? 'bg-pink-500'
                              : task.priority === 'Medium'
                                ? 'bg-amber-500'
                                : 'bg-green-500'
                        }`}
                        style={{ gridColumn: `${barStart} / ${barEnd}` }}
                      >
                        <span className="truncate">
                          {task.taskNumber ? `${task.taskNumber} · ` : ''}
                          {task.title}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              updateTask(task.id, {
                                timelineStart: Math.max(1, (task.timelineStart || 1) - 1),
                              })
                            }
                            className="rounded bg-white/20 px-1"
                          >
                            -
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              updateTask(task.id, {
                                timelineStart: Math.min(
                                  timelineColumnsCount - (task.timelineDuration || 2) + 1,
                                  (task.timelineStart || 1) + 1
                                ),
                              })
                            }
                            className="rounded bg-white/20 px-1"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      {dependencyCount > 0 && (
                        <div
                          className="mt-1 text-[10px] text-slate-400"
                          style={{ gridColumn: `${barStart} / ${Math.min(barEnd + 2, timelineColumnsCount + 1)}` }}
                        >
                          ↳ {dependencyCount} {t(dependencyCount === 1 ? 'dependencySingular' : 'dependencyPlural')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ) : activeView === 'channel' ? (
        <ProjectChannelView project={project} />
      ) : activeView === 'list' ? (
        <section className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          {selectedTaskIds.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 dark:border-brand-900 dark:bg-brand-900/20">
              <span className="text-sm font-medium text-brand-700 dark:text-brand-300">
                {selectedTaskIds.length} {t('selected')}
              </span>
              <div className="flex flex-wrap gap-2">
                {[
                  ['assignTo', t('assignTo')],
                  ['changeStatus', t('changeStatus')],
                  ['delete', t('delete')],
                  ['moveToProject', t('moveToProject')],
                ].map(([key, action]) => (
                  <button
                    key={key}
                    type="button"
                    className="rounded-lg border border-brand-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:bg-brand-50 dark:border-brand-900 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-brand-900/20"
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500 dark:text-slate-300">{t('groupBy')}</span>
              <select
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value)}
                className="rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 text-sm outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
              >
                <option value="status">{t('status')}</option>
                <option value="priority">{t('priority')}</option>
                <option value="assignee">{t('assignee')}</option>
                <option value="category">{t('category')}</option>
              </select>
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-300">
              {t('sort')}: {sortBy} ({sortDirection})
            </span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-brand-50 dark:bg-brand-900/20">
                <tr className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={sortableTasks.length > 0 && selectedTaskIds.length === sortableTasks.length}
                      onChange={(event) =>
                        setSelectedTaskIds(event.target.checked ? sortableTasks.map((task) => task.id) : [])
                      }
                    />
                  </th>
                  {[
                    ['taskNo', t('taskNumber')],
                    ['title', t('taskTitle')],
                    ['status', t('status')],
                    ['priority', t('priority')],
                    ['assignee', t('assignee')],
                    ['category', t('category')],
                    ['dueDate', t('dueDate')],
                    ['subtasks', t('subtasks')],
                  ].map(([key, label]) => (
                    <th key={key} className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setSort(key)}
                        className="inline-flex items-center gap-1 hover:text-brand-600 dark:hover:text-brand-300"
                      >
                        {label}
                      </button>
                    </th>
                  ))}
                  <th className="px-3 py-2">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedTaskMap).map(([groupName, groupTasks]) => (
                  groupTasks.map((task, index) => {
                    const assignee = teamMembers.find((member) => member.id === task.assigneeId);
                    const expanded = expandedTaskIds.includes(task.id);
                    return (
                      <Fragment key={task.id}>
                        {index === 0 && (
                          <tr className="border-t border-slate-200 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-900">
                            <td colSpan={10} className="px-3 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-300">
                              {groupBy.toUpperCase()}: {groupName}
                            </td>
                          </tr>
                        )}
                        <tr
                          className="group border-t border-brand-200/50 bg-white transition hover:bg-brand-50/80 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-brand-900/20"
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedTaskIds.includes(task.id)}
                              onChange={() => toggleTaskSelection(task.id)}
                            />
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-brand-700 dark:text-brand-400">
                            {task.taskNumber || '—'}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => toggleExpandedRow(task.id)}
                              className="font-medium text-slate-700 hover:text-brand-600 dark:text-slate-200 dark:hover:text-brand-300"
                            >
                              {task.title}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={task.columnId}
                              onChange={(event) => updateTaskStatus(task.id, event.target.value)}
                              disabled={structuralReadOnly}
                              className="rounded-md border border-brand-200 bg-white px-2 py-1 text-xs dark:border-brand-900 dark:bg-slate-950"
                            >
                              {columns.map((column) => (
                                <option key={column.id} value={column.id}>
                                  {translatedColumnName(t, column.name)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`rounded-full px-2 py-0.5 text-xs ${priorityStyles[task.priority]}`}>
                              {translatedPriority(t, task.priority)}
                            </span>
                          </td>
                          <td className="px-3 py-2">{assignee?.name || t('unassigned')}</td>
                          <td className="px-3 py-2">{task.category}</td>
                          <td className="px-3 py-2">{task.dueDate}</td>
                          <td className="px-3 py-2">
                            {task.subtaskDone}/{task.subtaskTotal}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => isApiProject ? navigate(`/projects/${project.id}/tasks/${task.id}`) : setActiveTaskId(task.id)}
                              className="rounded-md p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
                            >
                              <CircleEllipsis size={14} />
                            </button>
                            <div className="inline-flex opacity-0 transition group-hover:opacity-100">
                              <button
                                type="button"
                                className="rounded-md p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
                                title={t('edit')}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                className="rounded-md p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
                                title={t('comment')}
                              >
                                <MessageCircle size={14} />
                              </button>
                              <button
                                type="button"
                                className="rounded-md p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
                                title={t('assign')}
                              >
                                <UserPlus2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="border-t border-brand-100 bg-brand-50/40 dark:border-brand-900/40 dark:bg-brand-900/10">
                            <td />
                            <td colSpan={9} className="px-3 py-2">
                              <div className="space-y-1.5">
                                {Array.from({ length: task.subtaskTotal }).map((_, subtaskIndex) => {
                                  const done = subtaskIndex < task.subtaskDone;
                                  return (
                                    <div
                                      key={`${task.id}-${subtaskIndex}`}
                                      className="ml-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
                                    >
                                      <input type="checkbox" checked={done} readOnly />
                                      <span>
                                        {t('subtaskFor')} {subtaskIndex + 1} {t('for')} {task.title}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                ))}

                <tr className="border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/40">
                  <td className="px-3 py-2" />
                  <td colSpan={7} className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        value={newListTaskTitle}
                        onChange={(event) => setNewListTaskTitle(event.target.value)}
                        placeholder={`+ ${t('addTask')}`}
                        disabled={!allowStructuralAdds}
                        className="min-w-0 flex-1 rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-sm outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
                      />
                      <button
                        type="button"
                        onClick={() => setNewListTaskTitle(pickSampleTaskTitle(columns.length))}
                        disabled={!allowStructuralAdds}
                        className="shrink-0 rounded-lg border border-dashed border-brand-300 px-2.5 py-1.5 text-[11px] font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
                      >
                        {t('fillSampleData')}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={addListTask}
                      disabled={!allowStructuralAdds}
                      className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                    >
                      {t('addTask')}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <div className="grid gap-4 xl:grid-cols-5">
          {columns.map((column, columnIdx) => {
            const columnTasks = tasksForColumn(column.id);
            return (
              <article
                key={column.id}
                className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className={`flex items-center gap-2 rounded-lg ${column.tone} px-2 py-1 text-white`}>
                    <span className="text-xs font-semibold">{translatedColumnName(t, column.name)}</span>
                    <span className="rounded-md bg-white/20 px-1.5 text-[11px]">{columnTasks.length}</span>
                  </div>
                  {!structuralReadOnly && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setColumnMenuId((prev) => (prev === column.id ? null : column.id))}
                        className="rounded-md p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
                      >
                        <CircleEllipsis size={16} />
                      </button>
                      {columnMenuId === column.id && (
                        <div className="absolute right-0 z-10 mt-1 w-36 rounded-lg border border-brand-200 bg-white p-1 text-xs shadow-lg dark:border-brand-900 dark:bg-slate-900">
                          <button
                            type="button"
                            onClick={() => renameColumn(column.id)}
                            className="w-full rounded-md px-2 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-900/20"
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteColumn(column.id)}
                            className="w-full rounded-md px-2 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-900/20"
                          >
                            Delete Column
                          </button>
                          {allowStructuralAdds && (
                            <button
                              type="button"
                              onClick={addColumn}
                              className="w-full rounded-md px-2 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-900/20"
                            >
                              Add Column
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  {columnTasks.length === 0 ? (
                    <EmptyState
                      icon="📝"
                      title={t('noTasksInColumn')}
                      description={
                        allowStructuralAdds
                          ? t('addTaskWorkflowStage')
                          : t('noTasksInStageYet')
                      }
                      ctaLabel={allowStructuralAdds ? `+ ${t('addTask')}` : undefined}
                      onCta={allowStructuralAdds ? () => addTask(column.id) : undefined}
                    />
                  ) : columnTasks.map((task) => {
                    const assignee = teamMembers.find((member) => member.id === task.assigneeId);
                    return (
                      <div
                        key={task.id}
                        className="rounded-xl border border-brand-100 bg-white p-3 shadow-sm dark:border-brand-900/40 dark:bg-slate-950/60"
                      >
                        {task.taskNumber ? (
                          <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                            {task.taskNumber}
                          </p>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => isApiProject ? navigate(`/projects/${project.id}/tasks/${task.id}`) : setActiveTaskId(task.id)}
                          className="text-left text-sm font-medium text-slate-800 hover:text-brand-600 dark:text-white dark:hover:text-brand-300"
                        >
                          {task.title}
                        </button>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                          <span className={`rounded-full px-2 py-0.5 ${priorityStyles[task.priority]}`}>{task.priority}</span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            <Tag size={12} />
                            {task.category}
                          </span>
                          {task.hasAttachment && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                              <Image size={12} />
                              {t('media')}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-300">
                          <span className="inline-flex items-center gap-1">
                            <UserRound size={12} />
                            {assignee ? assignee.name : t('unassigned')}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Timer size={12} />
                            {task.subtaskDone}/{task.subtaskTotal}
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500 dark:text-slate-300">
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays size={12} />
                            {task.dueDate}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <GripVertical size={12} />
                            {t('simulateDrag')}
                          </span>
                        </div>
                        {!structuralReadOnly && (
                          <div className="mt-2 grid grid-cols-2 gap-1">
                            <button
                              type="button"
                              onClick={() => moveTask(task.id, -1)}
                              className="rounded-md border border-brand-200 px-2 py-1 text-xs text-slate-600 hover:bg-brand-50 dark:border-brand-900 dark:text-slate-300 dark:hover:bg-brand-900/20"
                            >
                              {t('moveLeft')}
                            </button>
                            <button
                              type="button"
                              onClick={() => moveTask(task.id, 1)}
                              className="rounded-md border border-brand-200 px-2 py-1 text-xs text-slate-600 hover:bg-brand-50 dark:border-brand-900 dark:text-slate-300 dark:hover:bg-brand-900/20"
                            >
                              {t('moveRight')}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {allowStructuralAdds && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <input
                        value={quickAddByColumn[column.id] || ''}
                        onChange={(event) =>
                          setQuickAddByColumn((prev) => ({
                            ...prev,
                            [column.id]: event.target.value,
                          }))
                        }
                        placeholder={t('quickAddTask')}
                        className="min-w-0 flex-1 rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setQuickAddByColumn((prev) => ({
                            ...prev,
                            [column.id]: pickSampleTaskTitle(columnIdx),
                          }))
                        }
                        className="shrink-0 rounded-lg border border-dashed border-brand-300 px-2 py-1.5 text-[10px] font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
                      >
                        {t('fillSampleData')}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => addTask(column.id)}
                      className="inline-flex w-full items-center justify-center gap-1 rounded-lg bg-brand-500 px-2.5 py-1.5 text-xs font-semibold text-white"
                    >
                      <Plus size={12} />
                      Add Task
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
      {activeTask && (
        <TaskDetailDrawer
          task={activeTask}
          project={project}
          columns={columns}
          categories={categories}
          labels={labels}
          t={t}
          isApiProject={isApiProject}
          apiProjectId={isApiProject ? Number(project.id) : null}
          onBoardRefresh={isApiProject ? refreshApiBoard : undefined}
          viewerUserId={viewerUserId}
          SkeletonCard={SkeletonCard}
          taskFieldsReadOnly={structuralReadOnly}
          showManageCategoriesShortcut={allowStructuralAdds}
          onOpenCategoryManager={() => setShowCategoryManager(true)}
          onClose={() => setActiveTaskId(null)}
          onUpdateTask={updateTask}
        />
      )}
      {allowStructuralAdds && showCategoryManager && (
        <CategoryLabelManagerModal
          onClose={() => setShowCategoryManager(false)}
          categories={categories}
          setCategories={setCategories}
          labels={labels}
          setLabels={setLabels}
        />
      )}
    </section>
  );
}

function ProjectsDashboard() {
  const { t } = useLocale();
  const [projects, setProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectsLoadError, setProjectsLoadError] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const canCreateProjects = useWorkspaceAdds();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingProjects(true);
      setProjectsLoadError('');
      try {
        const rows = await apiListMyProjects();
        if (cancelled) return;
        setProjects(rows.map(mapApiProjectSummaryToCard));
      } catch (err) {
        if (!cancelled) {
          setProjects([]);
          setProjectsLoadError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedProject = projects.find((project) => project.id === selectedProjectId) || null;

  const filteredProjects = projects.filter((project) =>
    activeFilter === 'All' ? true : project.status === activeFilter
  );

  const stats = [
    { title: t('dashboardStatTotalProjects'), value: projects.length, icon: FolderOpen },
    {
      title: t('statActiveTasks'),
      value: projects.reduce((sum, project) => sum + (project.status === 'Active' ? project.taskCount : 0), 0),
      icon: Activity,
    },
    {
      title: t('statCompleted'),
      value: projects.filter((project) => project.status === 'Completed').length,
      icon: CheckCircle2,
    },
    { title: t('dashboardStatTeamMembers'), value: teamMembers.length, icon: Users },
  ];

  if (selectedProject) {
    return (
      <KanbanBoard
        project={selectedProject}
        onBackToProjects={() => setSelectedProjectId(null)}
      />
    );
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-800 dark:text-white">{t('projectsDashboardTitle')}</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
            {t('projectsDashboardSubtitle')}
          </p>
        </div>
        {canCreateProjects && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand transition hover:opacity-95"
          >
            <Plus size={16} />
            {t('newProject')}
          </button>
        )}
      </div>

      {projectsLoadError && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/25 dark:text-red-200">
          {projectsLoadError}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.title} title={stat.title} value={stat.value} icon={stat.icon} />
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {['All', 'Active', 'Completed', 'On Hold'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveFilter(tab)}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
              activeFilter === tab
                ? 'bg-brand-500 text-white shadow-md shadow-brand'
                : 'border border-brand-200 bg-white text-slate-600 hover:bg-brand-50 dark:border-brand-900 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-brand-900/20'
            }`}
          >
            {translatedStatus(t, tab)}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {loadingProjects ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : filteredProjects.length === 0 ? (
          <div className="lg:col-span-2 xl:col-span-3">
            <EmptyState
              icon="📁"
              title={t('noProjectsYet')}
              description={
                canCreateProjects
                  ? t('noProjectsEmptyCreate')
                  : t('noProjectsEmptyInvited')
              }
              ctaLabel={canCreateProjects ? t('createProjectCta') : undefined}
              onCta={canCreateProjects ? () => setShowModal(true) : undefined}
            />
          </div>
        ) : (
          filteredProjects.map((project) => (
          <article
            key={project.id}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-400/20 dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                {project.projectNumber ? (
                  <p className="mb-0.5 font-mono text-xs font-semibold text-brand-600 dark:text-brand-400">
                    {project.projectNumber}
                  </p>
                ) : null}
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">{project.name}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">{project.description}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${statusStyles[project.status]}`}>
                {translatedStatus(t, project.status)}
              </span>
            </div>

            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between text-xs text-slate-500 dark:text-slate-300">
                <span>{t('progress')}</span>
                <span>{project.progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${project.progress}%`,
                    background: project.color,
                  }}
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <MemberAvatarStack memberIds={project.members} />
              <span className="text-sm text-slate-500 dark:text-slate-300">
                {project.taskCount} {t(project.taskCount === 1 ? 'taskSingular' : 'taskPlural')}
              </span>
            </div>

            <div className="mt-4 flex gap-2">
              {!project.readOnly && (
                <button
                  type="button"
                  onClick={() => setEditingProject(project)}
                  className="flex items-center gap-1.5 rounded-xl border border-brand-300 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-300 dark:hover:bg-brand-900/40 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  {t('notesEdit')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedProjectId(project.id)}
                className="flex-1 rounded-xl bg-brand-500 px-3 py-2 text-sm font-semibold text-white shadow-md shadow-brand transition hover:opacity-95"
              >
                {t('openProject')}
              </button>
            </div>
          </article>
          ))
        )}
      </div>

      {showModal && canCreateProjects && (
        <NewProjectModal
          onClose={() => setShowModal(false)}
          onCreate={async (draft) => {
            const created = await apiCreateProject({
              name: draft.name,
              description: draft.description?.trim()
                ? draft.description
                : t('projectDefaultDescription'),
            });
            const id = String(created?.id ?? created?.Id ?? '');
            if (!id) throw new Error(t('newProjectServerIdError'));
            setProjects((prev) => [
              {
                ...draft,
                id,
                projectNumber: String(created?.projectNumber ?? created?.ProjectNumber ?? ''),
                name: created?.name ?? created?.Name ?? draft.name,
                description: (created?.description ?? created?.Description) || draft.description || '',
                taskCount: 0,
                progress: 0,
              },
              ...prev,
            ]);
          }}
        />
      )}

      {editingProject && (
        <EditProjectModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onUpdated={(updated) => {
            setProjects((prev) =>
              prev.map((p) =>
                p.id === String(updated?.id ?? updated?.Id ?? editingProject.id)
                  ? {
                      ...p,
                      name: updated?.name ?? updated?.Name ?? p.name,
                      description: (updated?.description ?? updated?.Description) ?? p.description,
                    }
                  : p
              )
            );
          }}
        />
      )}
    </section>
  );
}

function TeamMembersPage() {
  const { t } = useLocale();
  const [members, setMembers] = useState(teamMemberDirectorySeed);
  const [activeFilter, setActiveFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [menuMemberId, setMenuMemberId] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'Developer',
    projects: ['Core Platform Revamp'],
  });
  const [loadingMembers, setLoadingMembers] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoadingMembers(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const roleOptions = ['Admin', 'Manager', 'Developer', 'Designer', 'Viewer'];
  const inviteRoleOptions = ['Manager', 'Developer', 'Designer', 'Viewer'];
  const projectOptions = seedProjects.map((project) => project.name);

  const filteredMembers = members.filter((member) => {
    const matchesOnline = activeFilter === 'Online' ? member.online : true;
    const matchesRole = roleFilter === 'All' ? true : member.role === roleFilter;
    const term = search.trim().toLowerCase();
    const matchesSearch =
      !term ||
      member.name.toLowerCase().includes(term) ||
      member.email.toLowerCase().includes(term) ||
      member.role.toLowerCase().includes(term);
    return matchesOnline && matchesRole && matchesSearch;
  });

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-800 dark:text-white">{t('teamMembersTitle')}</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
            Manage people, roles, and assignments across all active projects.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand transition hover:opacity-95"
        >
          <Plus size={16} />
          {t('inviteMember')}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
        {['All', 'Online'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveFilter(tab)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              activeFilter === tab
                ? 'bg-brand-500 text-white'
                : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
            }`}
          >
            {tab}
          </button>
        ))}
        <select
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value)}
          className="rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-950"
        >
          <option value="All">By Role</option>
          {roleOptions.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
        <div className="relative ml-auto min-w-56">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search members..."
            className="w-full rounded-lg border border-brand-200 bg-white py-1.5 pl-8 pr-2.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {loadingMembers ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : filteredMembers.length === 0 ? (
          <div className="sm:col-span-2 xl:col-span-3">
            <EmptyState
              icon="👥"
              title="No members found"
              description="Adjust filters or invite new people to this workspace."
              ctaLabel="+ Invite Member"
              onCta={() => setShowInviteModal(true)}
            />
          </div>
        ) : (
          filteredMembers.map((member) => (
          <article
            key={member.id}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-start justify-between gap-2">
              <button
                type="button"
                onClick={() => setSelectedMember(member)}
                className="flex items-center gap-2 text-left"
              >
                <div className="relative">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-sm font-semibold text-white">
                    {member.name
                      .split(' ')
                      .map((part) => part[0])
                      .join('')
                      .slice(0, 2)}
                  </div>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white dark:ring-slate-900 ${
                      member.online ? 'bg-green-500' : 'bg-slate-400'
                    }`}
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">{member.name}</p>
                  <span className="mt-0.5 inline-block rounded-full bg-brand-100 px-2 py-0.5 text-[11px] text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                    {member.role}
                  </span>
                </div>
              </button>

              <div className="relative">
                <button
                  type="button"
                  onClick={() =>
                    setMenuMemberId((prev) => (prev === member.id ? null : member.id))
                  }
                  className="rounded-md p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
                >
                  <CircleEllipsis size={14} />
                </button>
                {menuMemberId === member.id && (
                  <div className="absolute right-0 z-10 mt-1 w-40 rounded-lg border border-brand-200 bg-white p-1 text-xs shadow-lg dark:border-brand-900 dark:bg-slate-900">
                    <button
                      type="button"
                      onClick={() => {
                        const nextRole =
                          roleOptions[(roleOptions.indexOf(member.role) + 1) % roleOptions.length];
                        setMembers((prev) =>
                          prev.map((item) =>
                            item.id === member.id ? { ...item, role: nextRole } : item
                          )
                        );
                        setMenuMemberId(null);
                      }}
                      className="block w-full rounded px-2 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-900/20"
                    >
                      Change Role
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMembers((prev) =>
                          prev.map((item) =>
                            item.id === member.id
                              ? { ...item, activeProjects: Math.max(0, item.activeProjects - 1) }
                              : item
                          )
                        );
                        setMenuMemberId(null);
                      }}
                      className="block w-full rounded px-2 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-900/20"
                    >
                      Remove from Project
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMember(member);
                        setMenuMemberId(null);
                      }}
                      className="block w-full rounded px-2 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-900/20"
                    >
                      View Profile
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 space-y-1 text-xs text-slate-500 dark:text-slate-300">
              <p className="inline-flex items-center gap-1">
                <Mail size={12} />
                {member.email}
              </p>
              <p>Active Projects: {member.activeProjects}</p>
              <p>Tasks: {member.taskCount}</p>
            </div>
          </article>
          ))
        )}
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Invite Member</h3>
            <div className="mt-3 space-y-3 text-xs">
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">Email</span>
                <input
                  value={inviteForm.email}
                  onChange={(event) =>
                    setInviteForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                  className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">Role</span>
                <select
                  value={inviteForm.role}
                  onChange={(event) =>
                    setInviteForm((prev) => ({ ...prev, role: event.target.value }))
                  }
                  className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                >
                  {inviteRoleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>

              <div>
                <span className="mb-1 block text-slate-500 dark:text-slate-300">
                  Project Access
                </span>
                <div className="max-h-28 space-y-1 overflow-y-auto rounded-lg border border-brand-200 p-2 dark:border-brand-900">
                  {projectOptions.map((projectName) => (
                    <label key={projectName} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={inviteForm.projects.includes(projectName)}
                        onChange={() =>
                          setInviteForm((prev) => ({
                            ...prev,
                            projects: prev.projects.includes(projectName)
                              ? prev.projects.filter((item) => item !== projectName)
                              : [...prev.projects, projectName],
                          }))
                        }
                      />
                      <span>{projectName}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!inviteForm.email.trim()) return;
                  const localName = inviteForm.email.split('@')[0] || 'New Member';
                  const formattedName = localName
                    .split(/[._-]/)
                    .map((part) =>
                      part.length > 0 ? `${part[0].toUpperCase()}${part.slice(1)}` : ''
                    )
                    .join(' ')
                    .trim();
                  setMembers((prev) => [
                    {
                      id: `u-${Date.now()}`,
                      name: formattedName || 'New Member',
                      role: inviteForm.role,
                      email: inviteForm.email.trim(),
                      online: false,
                      activeProjects: inviteForm.projects.length,
                      taskCount: 0,
                      joinedDate: new Date().toISOString().slice(0, 10),
                      assignedTasks: [],
                      activity: [0, 0, 1, 0, 1],
                    },
                    ...prev,
                  ]);
                  setInviteForm({
                    email: '',
                    role: 'Developer',
                    projects: ['Core Platform Revamp'],
                  });
                  setShowInviteModal(false);
                }}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
              >
                Send Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedMember && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-base font-semibold text-white">
                  {selectedMember.name
                    .split(' ')
                    .map((part) => part[0])
                    .join('')
                    .slice(0, 2)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    {selectedMember.name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-300">
                    {selectedMember.role} • {selectedMember.email}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-300">
                    Joined: {selectedMember.joinedDate}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMember(null)}
                className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-brand-50 dark:text-slate-300 dark:hover:bg-brand-900/20"
              >
                Close
              </button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-brand-200 p-3 dark:border-brand-900">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
                  Assigned Tasks
                </p>
                <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                  {selectedMember.assignedTasks.length === 0 ? (
                    <li>No tasks assigned yet.</li>
                  ) : (
                    selectedMember.assignedTasks.map((taskName) => (
                      <li key={taskName}>• {taskName}</li>
                    ))
                  )}
                </ul>
              </div>

              <div className="rounded-lg border border-brand-200 p-3 dark:border-brand-900">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
                  Activity (last 5 weeks)
                </p>
                <div className="mt-3 flex h-24 items-end gap-2">
                  {selectedMember.activity.map((value, index) => (
                    <div key={`${selectedMember.id}-bar-${index}`} className="flex-1">
                      <div
                        className="w-full rounded-t bg-brand-500"
                        style={{ height: `${Math.max(10, value * 12)}px` }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ProjectChannelView({ project }) {
  const { t } = useLocale();
  const [channels, setChannels] = useState([
    { id: 'ch1', name: 'general', description: 'Team updates and standups', unread: 3, members: 8 },
    { id: 'ch2', name: 'design', description: 'Design reviews and assets', unread: 1, members: 5 },
    { id: 'ch3', name: 'dev', description: 'Implementation and blockers', unread: 4, members: 7 },
    { id: 'ch4', name: 'random', description: 'Informal discussions', unread: 0, members: 6 },
  ]);
  const [activeChannelId, setActiveChannelId] = useState('ch1');
  const [showComposer, setShowComposer] = useState(false);
  const [composer, setComposer] = useState({
    title: '',
    body: '',
    media: null,
    mention: '',
    channelId: 'ch1',
  });
  const [postsByChannel, setPostsByChannel] = useState({
    ch1: [
      {
        id: 'p1',
        author: 'Ava Johnson',
        timestamp: '10:24 AM',
        content: 'Shipping board updates today. @Noah Smith please review before noon.',
        media: { type: 'image', label: 'board-overview.png' },
        reactions: { '👍': 4, '🔥': 2 },
        comments: [
          { id: 'c1', author: 'Noah Smith', content: 'Reviewing now.', timestamp: '10:40 AM' },
        ],
        showComments: false,
      },
      {
        id: 'p2',
        author: 'Mia Patel',
        timestamp: '11:15 AM',
        content: 'Quick walkthrough recording attached for onboarding.',
        media: { type: 'video', label: 'walkthrough.mp4' },
        reactions: { '🎯': 3 },
        comments: [],
        showComments: false,
      },
    ],
    ch2: [],
    ch3: [],
    ch4: [],
  });

  const activeChannel = channels.find((channel) => channel.id === activeChannelId) || channels[0];
  const activePosts = postsByChannel[activeChannelId] || [];

  const setPostForChannel = (channelId, updater) =>
    setPostsByChannel((prev) => ({
      ...prev,
      [channelId]: updater(prev[channelId] || []),
    }));

  const addChannel = () => {
    const value = window.prompt(t('channelNamePrompt'));
    if (!value || !value.trim()) return;
    const id = `ch-${Date.now()}`;
    const cleanName = value.trim().toLowerCase().replace(/\s+/g, '-');
    setChannels((prev) => [
      ...prev,
      { id, name: cleanName, description: t('newProjectChannel'), unread: 0, members: 1 },
    ]);
    setPostsByChannel((prev) => ({ ...prev, [id]: [] }));
  };

  const mediaPreview = (media) => {
    if (!media) return null;
    if (media.type === 'image') {
      return (
        <div className="rounded-lg border border-brand-200 bg-brand-50 p-2 text-xs dark:border-brand-900 dark:bg-brand-900/10">
          <div className="inline-flex items-center gap-1">
            <Image size={12} />
            {media.label}
          </div>
        </div>
      );
    }
    if (media.type === 'video') {
      return (
        <div className="rounded-lg border border-brand-200 bg-brand-50 p-2 text-xs dark:border-brand-900 dark:bg-brand-900/10">
          <div className="inline-flex items-center gap-1">
            <PlayCircle size={12} />
            {media.label}
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-brand-200 bg-brand-50 p-2 text-xs dark:border-brand-900 dark:bg-brand-900/10">
        <div className="inline-flex items-center gap-1">
          <Mic size={12} />
          {media.label}
        </div>
      </div>
    );
  };

  return (
    <section className="grid gap-4 lg:grid-cols-12">
      <aside className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900 lg:col-span-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('channels')}</h3>
          <button
            type="button"
            onClick={addChannel}
            className="rounded-md border border-brand-200 px-2 py-1 text-[11px] dark:border-brand-900"
          >
            {t('add')}
          </button>
        </div>
        <div className="space-y-1.5">
          {channels.map((channel) => (
            <button
              key={channel.id}
              type="button"
              onClick={() => setActiveChannelId(channel.id)}
              className={`flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-xs ${
                channel.id === activeChannelId
                  ? 'bg-brand-500 text-white'
                  : 'hover:bg-brand-50 dark:text-slate-300 dark:hover:bg-brand-900/20'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Hash size={12} />
                {channel.name}
              </span>
              {channel.unread > 0 && (
                <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px]">
                  {channel.unread}
                </span>
              )}
            </button>
          ))}
        </div>
      </aside>

      <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 lg:col-span-9">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-200 p-3 dark:border-brand-900">
          <div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-white">
              #{activeChannel.name}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-300">
              {activeChannel.description} • {activeChannel.members} {t('members')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              placeholder={t('searchInChannel')}
              className="rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
            />
            <button
              type="button"
              className="rounded-md border border-brand-200 px-2 py-1 text-xs dark:border-brand-900"
            >
              <Settings size={13} />
            </button>
          </div>
        </header>

        <div className="space-y-3 p-3">
          {activePosts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-brand-300 p-6 text-center text-sm text-slate-500 dark:border-brand-900 dark:text-slate-300">
              {t('noPostsInChannel')}
            </div>
          ) : (
            activePosts.map((post) => (
              <article
                key={post.id}
                className="rounded-xl border border-brand-200 p-3 dark:border-brand-900"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-white">
                      {post.author}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-300">{post.timestamp}</p>
                  </div>
                  <button
                    type="button"
                    className="rounded-md p-1 text-slate-500 hover:bg-brand-50 dark:text-slate-300 dark:hover:bg-brand-900/20"
                  >
                    <CircleEllipsis size={14} />
                  </button>
                </div>

                <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{post.content}</p>
                <div className="mt-2">{mediaPreview(post.media)}</div>

                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  {Object.entries(post.reactions).map(([emoji, count]) => (
                    <button
                      key={`${post.id}-${emoji}`}
                      type="button"
                      onClick={() =>
                        setPostForChannel(activeChannelId, (prev) =>
                          prev.map((item) =>
                            item.id === post.id
                              ? {
                                  ...item,
                                  reactions: {
                                    ...item.reactions,
                                    [emoji]: item.reactions[emoji] + 1,
                                  },
                                }
                              : item
                          )
                        )
                      }
                      className="rounded-full border border-brand-200 px-2 py-0.5 dark:border-brand-900"
                    >
                      {emoji} {count}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setPostForChannel(activeChannelId, (prev) =>
                        prev.map((item) =>
                          item.id === post.id
                            ? {
                                ...item,
                                reactions: {
                                  ...item.reactions,
                                  '✨': (item.reactions['✨'] || 0) + 1,
                                },
                              }
                            : item
                        )
                      )
                    }
                    className="rounded-full border border-brand-200 px-2 py-0.5 dark:border-brand-900"
                  >
                    {t('reaction')}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setPostForChannel(activeChannelId, (prev) =>
                        prev.map((item) =>
                          item.id === post.id
                            ? { ...item, showComments: !item.showComments }
                            : item
                        )
                      )
                    }
                    className="rounded-full border border-brand-200 px-2 py-0.5 dark:border-brand-900"
                  >
                    {t('comments')} ({post.comments.length})
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-full border border-brand-200 px-2 py-0.5 dark:border-brand-900"
                  >
                    <Share2 size={11} />
                    {t('share')}
                  </button>
                </div>

                {post.showComments && (
                  <div className="mt-3 space-y-2 rounded-lg border border-brand-200 p-2 dark:border-brand-900">
                    {post.comments.map((comment) => (
                      <div
                        key={comment.id}
                        className="rounded-md border border-brand-100 px-2 py-1.5 text-xs dark:border-slate-800"
                      >
                        <p className="font-medium text-slate-700 dark:text-slate-200">
                          {comment.author}{' '}
                          <span className="font-normal text-slate-500">{comment.timestamp}</span>
                        </p>
                        <p className="mt-1 text-slate-600 dark:text-slate-300">{comment.content}</p>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        placeholder={t('replyToThread')}
                        className="flex-1 rounded-md border border-brand-200 px-2 py-1.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
                      />
                      <button
                        type="button"
                        className="rounded-md bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white"
                      >
                            {t('send')}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            ))
          )}
        </div>

        <div className="border-t border-brand-200 p-3 dark:border-brand-900">
          <button
            type="button"
            onClick={() => {
              setComposer((prev) => ({ ...prev, channelId: activeChannelId }));
              setShowComposer(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-3 py-2 text-xs font-semibold text-white"
          >
            <Plus size={13} />
            {t('newPost')}
          </button>
        </div>
      </div>

      {showComposer && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-brand-200 bg-white p-4 shadow-2xl dark:border-brand-900 dark:bg-slate-900">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">{t('postComposer')}</h3>
            <div className="mt-3 space-y-3 text-xs">
              <input
                value={composer.title}
                onChange={(event) =>
                  setComposer((prev) => ({ ...prev, title: event.target.value }))
                }
                placeholder={t('postTitleOptional')}
                className="w-full rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
              />
              <textarea
                rows={4}
                value={composer.body}
                onChange={(event) =>
                  setComposer((prev) => ({ ...prev, body: event.target.value }))
                }
                placeholder={t('writeYourPost')}
                className="w-full rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
              />
              <div className="flex flex-wrap gap-1.5">
                {[
                  ['Bold', t('editorBold')],
                  ['Italic', t('editorItalic')],
                ].map(([tool, label]) => (
                  <button
                    key={tool}
                    type="button"
                    className="rounded-md border border-brand-200 px-2 py-1 dark:border-brand-900"
                  >
                    {label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setComposer((prev) => ({ ...prev, media: { type: 'image', label: 'post-image.png' } }))
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 dark:border-brand-900"
                >
                  <Image size={11} />
                  {t('image')}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setComposer((prev) => ({ ...prev, media: { type: 'video', label: 'intro.mp4' } }))
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 dark:border-brand-900"
                >
                  <Video size={11} />
                  {t('video')}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setComposer((prev) => ({ ...prev, media: { type: 'audio', label: 'voice.wav' } }))
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 dark:border-brand-900"
                >
                  <Mic size={11} />
                  {t('audio')}
                </button>
                <button
                  type="button"
                  onClick={() => setComposer((prev) => ({ ...prev, mention: '@Ava Johnson' }))}
                  className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 dark:border-brand-900"
                >
                  <AtSign size={11} />
                  {t('mention')}
                </button>
              </div>

              {composer.media && <div>{mediaPreview(composer.media)}</div>}

              <select
                value={composer.channelId}
                onChange={(event) =>
                  setComposer((prev) => ({ ...prev, channelId: event.target.value }))
                }
                className="w-full rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
              >
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    #{channel.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowComposer(false)}
                className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!composer.body.trim()) return;
                  const text = composer.mention
                    ? `${composer.body.trim()} ${composer.mention}`
                    : composer.body.trim();
                  setPostsByChannel((prev) => ({
                    ...prev,
                    [composer.channelId]: [
                      {
                        id: `p-${Date.now()}`,
                        author: 'Mia Patel',
                        timestamp: 'now',
                        content: text,
                        media: composer.media,
                        reactions: { '👍': 0 },
                        comments: [],
                        showComments: false,
                      },
                      ...(prev[composer.channelId] || []),
                    ],
                  }));
                  setComposer({
                    title: '',
                    body: '',
                    media: null,
                    mention: '',
                    channelId: composer.channelId,
                  });
                  setActiveChannelId(composer.channelId);
                  setShowComposer(false);
                }}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
              >
                <Send size={12} />
                {t('post')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function GlobalSearchModal({
  open,
  query,
  setQuery,
  recentSearches,
  setRecentSearches,
  onClose,
  onNavigate,
}) {
  const { t } = useLocale();
  if (!open) return null;

  const searchableData = {
    Tasks: [
      { id: 'task-1', title: 'Refactor auth middleware', subtitle: 'Core Platform Revamp', timestamp: t('today') },
      { id: 'task-2', title: 'Design sprint planning template', subtitle: 'Design System 2.0', timestamp: t('yesterday') },
    ],
    Projects: seedProjects.map((project) => ({
      id: project.id,
      title: project.name,
      subtitle: project.description,
      timestamp: `${project.progress}%`,
    })),
    Members: teamMembers.map((member) => ({
      id: member.id,
      title: member.name,
      subtitle: t('teamMember'),
      timestamp: t('statusActive'),
    })),
    'Channel Posts': [
      { id: 'post-1', title: 'Shipping board updates today', subtitle: '#general', timestamp: '10:24 AM' },
    ],
    Comments: [
      { id: 'comment-1', title: '@Noah Smith please review', subtitle: t('taskComment'), timestamp: '2h ago' },
    ],
  };

  const sectionLabel = (section) => {
    if (section === 'Tasks') return t('tasks');
    if (section === 'Projects') return t('navProjects');
    if (section === 'Members') return t('members');
    if (section === 'Channel Posts') return t('channelPosts');
    if (section === 'Comments') return t('comments');
    return section;
  };

  const normalized = query.trim().toLowerCase();
  const filtered = Object.fromEntries(
    Object.entries(searchableData).map(([section, entries]) => [
      section,
      entries.filter(
        (item) =>
          !normalized ||
          item.title.toLowerCase().includes(normalized) ||
          item.subtitle.toLowerCase().includes(normalized)
      ),
    ])
  );

  const selectResult = (title, targetView) => {
    setRecentSearches((prev) => [title, ...prev.filter((entry) => entry !== title)].slice(0, 6));
    onNavigate(targetView);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-900/50 p-4 pt-20">
      <div className="w-full max-w-3xl rounded-2xl border border-brand-200 bg-white p-4 shadow-2xl dark:border-brand-900 dark:bg-slate-900">
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('globalSearchPlaceholder')}
          className="w-full rounded-xl border border-brand-200 bg-white px-3 py-2 text-sm outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
        />

        {query.trim() === '' && recentSearches.length > 0 ? (
          <div className="mt-3">
            <p className="mb-2 text-xs font-semibold uppercase text-slate-400">{t('recentSearches')}</p>
            <div className="flex flex-wrap gap-1.5">
              {recentSearches.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setQuery(item)}
                  className="rounded-full border border-brand-200 px-2.5 py-1 text-xs dark:border-brand-900"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-3 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {Object.entries(filtered).map(([section, entries]) => (
              <div key={section}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{sectionLabel(section)}</p>
                {entries.length === 0 ? (
                  <p className="text-xs text-slate-400">{t('noMatches')}</p>
                ) : (
                  <div className="space-y-1.5">
                    {entries.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() =>
                          selectResult(
                            item.title,
                            section === 'Members'
                              ? 'team'
                              : section === 'Projects' || section === 'Tasks'
                                ? 'projects'
                                : section === 'Channel Posts' || section === 'Comments'
                                  ? 'projects'
                                  : 'dashboard'
                          )
                        }
                        className="flex w-full items-center justify-between rounded-lg border border-brand-200 px-2.5 py-2 text-left hover:bg-brand-50 dark:border-brand-900 dark:hover:bg-brand-900/20"
                      >
                        <div>
                          <p className="text-sm text-slate-700 dark:text-slate-200">{item.title}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-300">{item.subtitle}</p>
                        </div>
                        <span className="text-[11px] text-slate-400">{item.timestamp}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


void TeamMembersPage;

function OnboardingModal({ step, setStep, onComplete }) {
  const { t } = useLocale();
  const [projectName, setProjectName] = useState('');
  const [inviteEmails, setInviteEmails] = useState('teammate@company.com');
  const total = 4;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-xl rounded-2xl border border-brand-200 bg-white p-5 shadow-2xl dark:border-brand-900 dark:bg-slate-900">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">{t('onboardingTitle')}</h3>
          <span className="text-xs text-slate-500 dark:text-slate-300">
            {t('onboardingStep')} {step} / {total}
          </span>
        </div>
        <div className="mb-4 flex gap-1.5">
          {Array.from({ length: total }).map((_, idx) => (
            <div
              key={`onb-dot-${idx + 1}`}
              className={`h-1.5 flex-1 rounded-full ${
                idx + 1 <= step ? 'bg-brand-500' : 'bg-slate-200 dark:bg-slate-700'
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-2 text-sm">
            <p className="text-slate-700 dark:text-slate-200">{t('onboardingSystemTitle')}</p>
            <p className="text-xs text-slate-500 dark:text-slate-300">
              {t('onboardingSystemSubtitle')}
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2 text-sm">
            <p className="text-slate-700 dark:text-slate-200">{t('onboardingCreateFirstProject')}</p>
            <input
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              placeholder={t('projectName')}
              className="w-full rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm dark:border-brand-900 dark:bg-slate-950"
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2 text-sm">
            <p className="text-slate-700 dark:text-slate-200">{t('onboardingInviteTeamMembers')}</p>
            <textarea
              rows={3}
              value={inviteEmails}
              onChange={(event) => setInviteEmails(event.target.value)}
              className="w-full rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm dark:border-brand-900 dark:bg-slate-950"
            />
          </div>
        )}

        {step === 4 && (
          <div className="grid gap-2 md:grid-cols-3">
            {[t('dashboardOpen'), t('dashboardCreateTask'), t('dashboardInviteTeamCard')].map((card) => (
              <div key={card} className="rounded-xl border border-brand-200 p-3 text-xs dark:border-brand-900">
                <p className="font-medium text-slate-700 dark:text-slate-200">{card}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep((prev) => prev - 1)}
              className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900"
            >
              {t('back')}
            </button>
          )}
          {step < total ? (
            <button
              type="button"
              onClick={() => setStep((prev) => prev + 1)}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
            >
              {t('next')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onComplete}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
            >
              {t('onboardingDone')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const { language, setLanguage, supportedLocales, t } = useLocale();
  return (
    <LoginPage
      onLogin={onLogin}
      language={language}
      setLanguage={setLanguage}
      supportedLocales={supportedLocales}
      t={t}
    />
  );
}

function MainContent({
  expanded,
  notifications,
  onRefreshNotifications,
  onNavigate,
  onToast,
  onWorkspaceAppearance,
  onLoginAs,
  isSuperAdmin,
}) {
  const { dir } = useLocale();
  const { t } = useLocale();
  const profileFallback = {
    title: t('pageProfileTitle'),
    description: t('profileFallbackDescription'),
  };

  return (
    <main
      className={`pt-20 transition-all duration-300 ${
        dir === 'rtl'
          ? expanded
            ? 'mr-[260px]'
            : 'mr-16'
          : expanded
            ? 'ml-[260px]'
            : 'ml-16'
      } min-h-screen px-4 pb-6 md:px-6`}
    >
      <Routes>
        <Route
          path="/"
          element={isSuperAdmin
            ? <Navigate to="/super-admin" replace />
            : <DashboardPageView t={t} onNavigate={onNavigate} onToast={onToast} />}
        />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/projects" element={<ProjectsDashboard />} />
        <Route path="/ai-tasker" element={<AiTaskerPageView t={t} onToast={onToast} />} />
        <Route path="/ai-assistance" element={<AiAssistancePageView t={t} onToast={onToast} />} />
        <Route path="/ai-video" element={<AiVideoPageView t={t} onToast={onToast} />} />
        <Route path="/notes" element={<NotesPageView t={t} onToast={onToast} />} />
        <Route path="/super-admin" element={<SuperAdminPageView t={t} onToast={onToast} onLoginAs={onLoginAs} />} />
        <Route path="/contact-messages" element={<ContactMessagesPageView t={t} onToast={onToast} />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPageView t={t} />} />
        <Route path="/projects/:projectId/tasks/:taskId" element={<TaskDetailPage />} />
        <Route
          path="/team"
          element={
            <TeamMembersPageView t={t} SkeletonCard={SkeletonCard} EmptyState={EmptyState} />
          }
        />
        <Route
          path="/messages"
          element={<MessagesPageView t={t} EmptyState={EmptyState} onToast={onToast} />}
        />
        <Route
          path="/notifications"
          element={
            <NotificationsCenterView
              notifications={notifications}
              onRefreshNotifications={onRefreshNotifications}
              onNavigate={onNavigate}
              dir={dir}
              t={t}
              onToast={onToast}
            />
          }
        />
        <Route
          path="/settings"
          element={
            <SettingsPageView
              t={t}
              onToast={onToast}
              onWorkspaceAppearance={onWorkspaceAppearance}
            />
          }
        />
        <Route
          path="/profile"
          element={<ViewPlaceholder title={profileFallback.title} description={profileFallback.description} />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </main>
  );
}

function AppLayout({ onLogout, userName }) {
  const { isDarkMode, toggleTheme, setDarkMode } = useContext(ThemeContext) ?? {};
  const { dir, t } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [workspaceAddsAllowed, setWorkspaceAddsAllowed] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState(['Core Platform Revamp', 'Ava Johnson']);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [toasts, setToasts] = useState([]);
  const [userAvatarUrl, setUserAvatarUrl] = useState(null);
  const unreadCount = notifications.filter((item) => item.unread).length;

  const refreshNotifications = useCallback(async () => {
    try {
      const rows = await apiListNotifications();
      setNotifications(rows.map(mapNotificationDto));
    } catch {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    void refreshNotifications();
  }, [refreshNotifications]);

  useEffect(() => {
    let cancelled = false;
    apiGetMe()
      .then((me) => {
        if (!cancelled) {
          setWorkspaceAddsAllowed(!!me.canCreateWorkspaceResources);
          setIsSuperAdmin(Array.isArray(me.roles) && me.roles.includes('SuperAdmin'));
        }
      })
      .catch(() => {
        if (!cancelled) setWorkspaceAddsAllowed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyWorkspaceAppearance = useCallback(
    (appearance) => {
      if (!appearance) return;
      if (setDarkMode) {
        if (appearance.mode === 'Dark') setDarkMode(true);
        else if (appearance.mode === 'Light') setDarkMode(false);
        else
          setDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
      if (typeof appearance.compactSidebar === 'boolean') {
        setSidebarExpanded(!appearance.compactSidebar);
      }
    },
    [setDarkMode],
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const raw = await apiGetWorkspaceSettings();
        if (cancelled) return;
        const mapped = mapWorkspaceSettingsResponse(raw);
        applyWorkspaceAppearance(mapped.appearance);
        if (!cancelled) setUserAvatarUrl(mapped.profile?.avatarUrl ?? null);
      } catch {
        /* no settings yet or offline */
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [applyWorkspaceAppearance]);

  const pushToast = (type, message) => {
    const toast = { id: `toast-${Date.now()}-${Math.random()}`, type, message };
    setToasts((prev) => [...prev, toast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((item) => item.id !== toast.id));
    }, 3000);
  };

  useEffect(() => {
    const handleAvatarUpdated = (event) => {
      const url = event.detail?.avatarUrl ?? null;
      setUserAvatarUrl(url);
    };
    window.addEventListener('coretask:avatar-updated', handleAvatarUpdated);
    return () => window.removeEventListener('coretask:avatar-updated', handleAvatarUpdated);
  }, []);

  useEffect(() => {
    const handleKeydown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
      }
      if (event.key === 'Escape') {
        setIsSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);

  useEffect(() => {
    const seen = window.localStorage.getItem('coretask-onboarding-done');
    if (!seen) setShowOnboarding(true);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setSidebarExpanded(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const routeToView = (pathname) => {
    if ((pathname === '/' || pathname === '') && isSuperAdmin) return 'super-admin';
    if (pathname === '/' || pathname === '') return 'dashboard';
    const segment = pathname.split('/').filter(Boolean)[0];
    if (['dashboard', 'projects', 'ai-tasker', 'ai-assistance', 'ai-video', 'notes', 'team', 'messages', 'notifications', 'settings', 'profile', 'super-admin', 'contact-messages', 'privacy-policy'].includes(segment)) {
      return segment;
    }
    return 'dashboard';
  };

  const viewToRoute = {
    dashboard: '/',
    projects: '/projects',
    'ai-tasker': '/ai-tasker',
    'ai-assistance': '/ai-assistance',
    'ai-video': '/ai-video',
    notes: '/notes',
    team: '/team',
    messages: '/messages',
    notifications: '/notifications',
    settings: '/settings',
    profile: '/profile',
    'super-admin':      '/super-admin',
    'contact-messages': '/contact-messages',
    'privacy-policy':   '/privacy-policy',
  };

  const activeView = routeToView(location.pathname);

  return (
    <WorkspaceAddsContext.Provider value={workspaceAddsAllowed}>
      <div className="min-h-screen bg-[#f4f6f8] text-slate-900 transition-colors duration-300 dark:bg-[#111111] dark:text-white">
      <Sidebar
        activeView={activeView}
        onChangeView={(nextView) => navigate(viewToRoute[nextView] || '/')}
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded((prev) => !prev)}
        isSuperAdmin={isSuperAdmin}
      />
      <TopNavbar
        expanded={sidebarExpanded}
        onToggleTheme={toggleTheme}
        isDarkMode={isDarkMode}
        onOpenNotifications={() =>
          setIsNotificationsOpen((prev) => {
            const next = !prev;
            if (!prev && next) void refreshNotifications();
            return next;
          })}
        unreadCount={unreadCount}
        onLogout={onLogout}
        userName={userName}
        userAvatarUrl={userAvatarUrl}
        onAvatarChange={setUserAvatarUrl}
      />
      <MainContent
        expanded={sidebarExpanded}
        notifications={notifications}
        onRefreshNotifications={refreshNotifications}
        onNavigate={(targetView) => {
          navigate(viewToRoute[targetView] || '/');
          setIsNotificationsOpen(false);
        }}
        onToast={(message, variant = 'error') => pushToast(variant, message)}
        onWorkspaceAppearance={applyWorkspaceAppearance}
        isSuperAdmin={isSuperAdmin}
        onLoginAs={(session) => {
          setAuthSession({ token: session.token, email: session.email });
          pushToast('success', t('superAdminImpersonateToast'));
          navigate('/', { replace: true });
          window.location.reload();
        }}
      />
      {isNotificationsOpen && (
        <NotificationsCenterView
          compact
          notifications={notifications}
          onRefreshNotifications={refreshNotifications}
          onNavigate={(targetView) => {
            navigate(viewToRoute[targetView] || '/');
          }}
          onClose={() => setIsNotificationsOpen(false)}
          dir={dir}
          t={t}
          onToast={(message, variant = 'error') => pushToast(variant, message)}
        />
      )}
      <GlobalSearchModal
        open={isSearchOpen}
        query={searchQuery}
        setQuery={setSearchQuery}
        recentSearches={recentSearches}
        setRecentSearches={setRecentSearches}
        onClose={() => setIsSearchOpen(false)}
        onNavigate={(targetView) => {
          navigate(viewToRoute[targetView] || '/');
        }}
      />
      {showOnboarding && (
        <OnboardingModal
          step={onboardingStep}
          setStep={setOnboardingStep}
          onComplete={() => {
            window.localStorage.setItem('coretask-onboarding-done', '1');
            setShowOnboarding(false);
            pushToast('success', 'Onboarding completed successfully.');
            pushToast('info', 'Tip: Press Ctrl+K to open global search.');
            pushToast('warning', 'Remember to review due dates each morning.');
            pushToast('error', 'Sample error toast: failed sync can appear here.');
          }}
        />
      )}
      <div className={`fixed bottom-4 z-[120] space-y-2 ${dir === 'rtl' ? 'left-4' : 'right-4'}`}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`min-w-[220px] rounded-xl border px-4 py-2.5 text-xs font-medium shadow-xl ${
              toast.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700'
                : toast.type === 'error'
                  ? 'border-pink-200 bg-pink-50 text-pink-700'
                  : toast.type === 'warning'
                    ? 'border-amber-200 bg-amber-50 text-amber-700'
                    : 'border-brand-200 bg-brand-50 text-brand-700'
            }`}
          >
            {toast.message}
          </div>
        ))}
      </div>
      </div>
    </WorkspaceAddsContext.Provider>
  );
}

// ── Splash Screen ─────────────────────────────────────────────────────────────
function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState('enter'); // 'enter' → 'hold' → 'exit'

  useEffect(() => {
    // enter: 600 ms scale-in
    const t1 = setTimeout(() => setPhase('hold'), 600);
    // hold until 4 500 ms, then start fade-out
    const t2 = setTimeout(() => setPhase('exit'), 4500);
    // exit: 500 ms fade-out → unmount at 5 000 ms
    const t3 = setTimeout(() => onDone(), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);

  return (
    <div
      className={`fixed inset-0 z-[999] overflow-hidden transition-opacity duration-500 ${
        phase === 'exit' ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* splash image */}
      <img
        src={`${process.env.PUBLIC_URL || ''}/images/splash.png`}
        alt="CoreTask"
        draggable={false}
        className={`h-full w-full object-cover transition-all duration-600 ${
          phase === 'enter' ? 'scale-105 opacity-0' : 'scale-100 opacity-100'
        }`}
      />

      {/* loading dots — bottom center */}
      <div
        className={`absolute bottom-10 flex gap-2 transition-opacity duration-500 delay-300 ${
          phase === 'enter' ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-2 w-2 rounded-full bg-white/70"
            style={{ animation: `splashDot 1.2s ${i * 0.2}s infinite ease-in-out` }}
          />
        ))}
      </div>

      <style>{`
        @keyframes splashDot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40%            { transform: scale(1.2); opacity: 1;   }
        }
      `}</style>
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const [splashDone, setSplashDone] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [language, setLanguage] = useState(() => {
    try {
      return normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
    } catch {
      return 'en';
    }
  });

  useEffect(() => {
    const session = loadAuthSession();
    if (session) {
      setUserEmail(session.email);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      setIsAuthenticated(false);
      setUserEmail('');
      navigate('/login', { replace: true });
    };
    window.addEventListener('coretask:session-expired', onSessionExpired);
    return () => window.removeEventListener('coretask:session-expired', onSessionExpired);
  }, [navigate]);
  const themeValue = useMemo(
    () => ({
      isDarkMode,
      setDarkMode: setIsDarkMode,
      toggleTheme: () => setIsDarkMode((prev) => !prev),
    }),
    [isDarkMode]
  );
  const localeValue = useMemo(() => {
    const locale = getLocaleMetadata(language);
    const t = (key) => translations[language]?.[key] || translations.en?.[key] || key;
    return { language, setLanguage, dir: locale.dir, locale, supportedLocales, t };
  }, [language]);

  useEffect(() => {
    const locale = getLocaleMetadata(language);
    document.documentElement.dir = locale.dir;
    document.documentElement.lang = language;
    if (language === 'ar') {
      document.documentElement.style.fontFamily = "'Cairo', sans-serif";
    } else {
      document.documentElement.style.fontFamily = '';
    }
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, locale.code);
    } catch {
      /* ignore */
    }
  }, [language]);

  return (
    <ThemeContext.Provider value={themeValue}>
      <LocaleContext.Provider value={localeValue}>
        <div className={isDarkMode ? 'dark' : ''}>
          {!splashDone && <SplashScreen onDone={() => setSplashDone(true)} />}
          <Routes>
            <Route
              path="/accept-invite"
              element={
                <AcceptInvitePage
                  language={language}
                  setLanguage={setLanguage}
                  supportedLocales={supportedLocales}
                  t={(key) => translations[language]?.[key] || translations.en?.[key] || key}
                />
              }
            />
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/" replace />
                ) : (
                  <LoginScreen
                    onLogin={({ email }) => {
                      setUserEmail(email);
                      setIsAuthenticated(true);
                    }}
                  />
                )
              }
            />
            <Route
              path="/*"
              element={
                isAuthenticated ? (
                  <AppLayout
                    userName={userEmail || 'User'}
                    onLogout={() => {
                      clearAuthSession();
                      setIsAuthenticated(false);
                      setUserEmail('');
                      navigate('/login', { replace: true });
                    }}
                  />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </div>
      </LocaleContext.Provider>
    </ThemeContext.Provider>
  );
}

export default App;
